import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { getSupabase, isSupabaseConfigured } from '@/lib/supabase';
import { clearPendingInvite } from '@/utils/pendingInvite';
import { syncSessionToServer } from '@/utils/syncSession';
import { signOutOnServer } from '@/data/authApi';
import * as trackersApi from '@/data/trackersApi';
import * as goalsApi from '@/data/goalsApi';
import * as notesApi from '@/data/notesApi';
import * as programAssignmentsApi from '@/data/programAssignmentsApi';
import * as clientsApi from '@/data/clientsApi';
import * as workoutsApi from '@/data/workoutsApi';
import { deleteTemplateViaApi } from '@/data/nutritionTemplates';
import { deleteAssignment as deleteNutritionAssignment } from '@/data/nutritionAssignments';
import { withStatus } from '@/lib/apiClient';
import { isMockMode } from '@utils/env';
import { purgeServerDataMirror } from '@/lib/localDataMirror';
import { useExerciseLibraryStore } from '@/stores/exerciseLibraryStore';
import { selectExercises } from '@/stores/exerciseLibraryStore.selectors';
import { listClients as listClientsData } from '@/data/clients';
import { buildClientDashboardMocks } from '@/mocks/clientDashboard';
import { mockProgramTemplates } from '@/mocks/templates';
import { assignProgramTemplateToClient } from '@/state/actions/assignTemplate';
import { toast } from 'sonner';
import type {
  FoodItem,
  FoodInMeal,
  Meal,
  DayPlan,
  NutritionTemplate,
  ClientNutritionPlan,
  NutritionTotals,
} from '@/types/nutrition';
import { snapshotToEditorFormat } from '@/utils/nutrition/snapshotConversion';
import { normalizeDayType } from '@/utils/dayType';
import { toLocalISODate, parseLocalISODate, todayISO } from '@/utils/date';
import { appStore } from '@/data/storage/app/adapter';
import type { ClientEvent } from '@/types/events';
import { addClientEventBase } from '@/utils/addClientEvent';
import { createMessageActivity } from '@/data/activityEvents';
import { messagesStore } from '@/data/storage/messages/adapter';
import type { ChatMessage, MessageThread } from '@/types/messages';
import type { DayType } from '@/types/nutrition';
import type { ClientGoal } from '@/types/goals';
import { atLocalMidnight } from '@/utils/goalSchedule';
import type { ClientTracker, TrackerEntry, TrackerSchedule, TrackerUnit } from '@/types/trackers';
import { trackersStore } from '@/data/storage/trackers/adapter';
import { computeNextDue as computeNextTrackerDue } from '@/utils/trackerSchedule';
import { goalsStore } from '@/data/storage/goals/adapter';
// Import debug utilities for console testing
import '@/utils/debugGoalsTrackers';
import { isIntakeBackendReady } from '@/data/storage/intake/adapter';
import { intakeStore, markMerged as intakeMarkMerged } from '@/data/storage/intake/adapter';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { identifyPostHog, resetPostHog, capturePostHog } from '@/lib/posthog';
import { fetchExercisesByIds } from '@/data/storage/exercises/supabase';
import { clearProgramCache } from '@/utils/clientProgramCompat';
import { DEFAULT_FREE_CLIENT_LIMIT } from '@/hooks/useSubscription';
import {
  failedAssignmentsEntry,
  isAssignmentsCacheFresh,
  type AssignmentsCacheEntry,
} from '@/lib/clientPrograms/assignmentsCachePolicy';
// NEW: ensure direct adapter import already present
// Optional util import if available
// import { addClientEvent as addClientEventUtil } from '@/utils/addClientEvent';
import type {
  IntakePacket,
  IntakeAnswer,
  IntakeGoalDraft as IntakeGoal,
  IntakeTrackerDraft as IntakeTracker,
} from '@/types/intake';

// Re-export nutrition types for backward compatibility
// ----- Coach default form_flags helpers -----
type FormFlags = {
  include_full_name: boolean;
  include_age: boolean;
  include_motivation: boolean;
  include_experience: boolean;
  include_injuries: boolean;
  include_trackers: boolean;
  include_height: boolean;
  include_weight: boolean;
};

/** Read CSRF token from cookie (or in-memory fallback) for mutating API requests */
function getCsrfToken(): string {
  if (typeof document === 'undefined') return '';
  const match = document.cookie.match(/__csrf=([^;]+)/);
  return match?.[1] || (window as any).__csrfToken || '';
}

/**
 * Wrapper around fetch that auto-injects CSRF token for mutation requests.
 * Only GET reads still use this (API-3); every write goes through a src/data module on apiClient.
 */
function csrfFetch(url: string, init?: RequestInit): Promise<Response> {
  const method = (init?.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const headers = new Headers(init?.headers);
    if (!headers.has('X-CSRF-Token')) {
      headers.set('X-CSRF-Token', getCsrfToken());
    }
    return fetch(url, { ...init, headers });
  }
  return fetch(url, init);
}

/** Legacy error copy: these writes surfaced only the error body's `error` field (else a fallback). */
function bodyError(res: { body?: unknown }): string | undefined {
  return (res.body as { error?: string } | undefined)?.error;
}

export const DEFAULT_FORM_FLAGS: FormFlags = {
  include_full_name: true,
  include_age: true,
  include_motivation: true,
  include_experience: true,
  include_injuries: true,
  include_trackers: true,
  include_height: false,
  include_weight: false,
};

export type ClientProgramAssignment = {
  id: string;
  programId: string;
  startDate: string; // ISO date
  timezone: string | null;
  status: string | null; // e.g. 'active'
  createdAt: string;
  updatedAt: string;
  // Client-specific overrides
  displayName?: string | null;
  description?: string | null;
  difficulty?: string | null;
  tags?: string[];
  totalWeeksOverride?: number | null;
  effectiveTotalWeeks?: number;
  program?: {
    id: string;
    name: string;
    level?: string | null;
    totalWeeks?: number | null;
    goal?: string | null;
    // Template description lives in programs.notes (the template editor saves
    // meta.description there); tags are the template's tags.
    notes?: string | null;
    tags?: string[];
  } | null;
};

async function getCoachKey(): Promise<string> {
  try {
    const sb = getSupabase();
    const resp: any = await (sb as any)?.auth?.getUser?.();
    const uid = resp?.data?.user?.id;
    return uid || 'coach_demo';
  } catch {
    return 'coach_demo';
  }
}

async function loadDefaultFormFlags(): Promise<FormFlags> {
  try {
    const key = `coachd:form_flags:defaults:${await getCoachKey()}`;
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULT_FORM_FLAGS;
    const obj = JSON.parse(raw || '{}');
    return { ...DEFAULT_FORM_FLAGS, ...(obj || {}) } as FormFlags;
  } catch {
    return DEFAULT_FORM_FLAGS;
  }
}

async function saveDefaultFormFlags(flags: Partial<FormFlags>) {
  try {
    const key = `coachd:form_flags:defaults:${await getCoachKey()}`;
    const merged = { ...(await loadDefaultFormFlags()), ...flags } as FormFlags;
    localStorage.setItem(key, JSON.stringify(merged));
  } catch {
    /* ignored */
  }
}

// ---- Default tracker template (coach-scoped) ----
type TrackerTpl = {
  title: string;
  unit?: string;
  cadence?: 'daily' | 'weekly';
  reminders?: boolean;
  showInAnalytics?: boolean;
  days?: string[];
};
async function loadDefaultTrackersTemplate(): Promise<TrackerTpl[]> {
  try {
    const key = `coachd:trackers_tpl:defaults:${await getCoachKey()}`;
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
async function saveDefaultTrackersTemplate(rows: TrackerTpl[]): Promise<void> {
  try {
    const key = `coachd:trackers_tpl:defaults:${await getCoachKey()}`;
    localStorage.setItem(key, JSON.stringify(rows || []));
  } catch {
    /* ignored */
  }
}

// ---- Default custom questions (coach-scoped) ----
type CustomQ = { key?: string; prompt: string; required?: boolean };
async function loadDefaultCustomQs(): Promise<CustomQ[]> {
  try {
    const key = `coachd:custom_qs:defaults:${await getCoachKey()}`;
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
async function saveDefaultCustomQs(rows: CustomQ[]): Promise<void> {
  try {
    const key = `coachd:custom_qs:defaults:${await getCoachKey()}`;
    localStorage.setItem(key, JSON.stringify(rows || []));
  } catch {
    /* ignored */
  }
}

// ---- Helper: Seed a packet from the current coach template ----
async function seedPacketFromTemplate(packetId: string): Promise<void> {
  try {
    const {
      upsertAnswer,
      upsertTrackerDraft,
      deleteTrackerDraft,
      deleteAnswer,
      listTrackerDrafts,
      listAnswersForPacket,
    } = await import('@/data/storage/intake/adapter');

    // Flags
    const flags = await loadDefaultFormFlags();
    for (const [k, v] of Object.entries(flags)) {
      await upsertAnswer(packetId, 'form_flags', k, !!v);
    }

    // Trackers: upsert each from template; remove any tracker in packet that's not in template
    const tmplTrackers = await loadDefaultTrackersTemplate();
    const existing = await listTrackerDrafts(packetId);

    // upsert all template trackers
    for (const t of tmplTrackers) {
      await upsertTrackerDraft(packetId, t);
    }
    // prune non-template trackers
    const tmplTitles = new Set(tmplTrackers.map((t) => String(t.title).trim().toLowerCase()));
    for (const et of existing) {
      const title = String(et.title || '')
        .trim()
        .toLowerCase();
      if (title && !tmplTitles.has(title)) {
        await deleteTrackerDraft(packetId, et.id || et.title);
      }
    }

    // Custom Questions: upsert questions; remove ones not in template
    const tmplQs = await loadDefaultCustomQs(); // [{ key, prompt, required }]
    const answers = await listAnswersForPacket(packetId);
    const cq = answers.filter((a: any) => a.section === 'custom_questions');
    const tmplKeys = new Set(tmplQs.map((q) => q.key).filter(Boolean));

    for (const q of tmplQs) {
      if (q.key) {
        await upsertAnswer(packetId, 'custom_questions', q.key, { label: q.prompt, required: !!q.required });
      }
    }
    for (const a of cq) {
      if (!tmplKeys.has((a as any).key)) {
        await deleteAnswer(packetId, 'custom_questions', (a as any).key);
      }
    }
  } catch (e) {
    try {
      console.warn('seedPacketFromTemplate failed:', e);
    } catch {
      /* ignored */
    }
  }
}
export type { FoodItem, FoodInMeal, Meal, DayPlan, NutritionTemplate, ClientNutritionPlan, NutritionTotals };

// Small helper to generate unique IDs
const uid = (p = 'id') =>
  globalThis.crypto?.randomUUID?.() ?? `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

// Types
export interface Exercise {
  id: string;
  name: string;
  // Legacy fields (reps mode)
  sets?: number;
  reps?: string;
  // Tracking mode discriminator
  trackBy?: 'reps' | 'time' | 'distance' | 'interval';
  // Time mode
  durationSec?: number; // total seconds
  // Distance mode
  distance?: number; // numeric distance
  distanceUnit?: 'mi' | 'km';
  targetPaceSec?: number; // seconds per unit (optional)
  // Interval mode
  rounds?: number;
  workSec?: number;
  restSec?: number;
  intervalType?: 'time' | 'distance'; // Interval track type (distance intervals vs time intervals)
  effort?: string; // Replaces weight - can be RPE scale (1-10) or intensity level
  rest?: string;
  notes?: string;
  intensity?: string; // Keep for backwards compatibility
}

export interface WorkoutDay {
  id: string;
  name: string;
  exercises: Exercise[];
  notes?: string;
  estimatedDuration?: number;
  dayOfWeek?: string;
  tags?: string[]; // Add tags for workout library categorization
  weeks?: number[]; // Weeks this workout applies to
  // Optional modality for Program Editor-created workouts
  modality?: 'strength' | 'cardio' | 'hiit' | 'mobility' | 'recovery' | 'other';
}

// New interface for standalone workouts in the library
export interface SavedWorkout {
  id: string;
  name: string;
  exercises: Exercise[];
  notes?: string;
  estimatedDuration?: number;
  tags?: string[];
  createdDate?: string;
  difficulty?: 'Beginner' | 'Intermediate' | 'Advanced';
  category?: string; // e.g., "Upper Body", "Cardio", "Full Body"
  // NEW
  modality?: 'strength' | 'cardio' | 'hiit' | 'mobility' | 'recovery' | 'other';
}

export interface WorkoutTemplate {
  id: string;
  title: string;
  description?: string;
  tags: string[];
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  createdDate: string;
  assignedClients: number;
  isPublic: boolean;
  weeks: {
    [key: string]: WorkoutDay[];
  };
}

export interface ClientProgram {
  id: string;
  clientId: string;
  templateId: string;
  programData: WorkoutTemplate;
  assignedDate: string;
  startDate?: string;
  notes?: string;
  allowCustomizations: boolean;
  isCustomized: boolean;
}

export interface ClientNote {
  id: string;
  date: string;
  note: string;
  tags: string[];
  pinned: boolean;
  type: 'observation' | 'reminder' | 'goal' | 'concern' | 'general' | 'workout' | 'nutrition' | 'ai-learning';
  coachName: string;
}

export interface Coach {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
  orgId?: string;
}

export interface Client {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
  assignedPrograms: string[];
  assignedNutritionPlans: string[];
  primaryCoachId?: string;
  joinDate?: string;
  lastLoginDate?: string;
  goals?: string[];
  notes?: string;
  coachNotes: ClientNote[];
  nutritionPreferences?: {
    allergies: string[];
    dietaryRestrictions: string[];
    preferences: string[];
  };

  // Optional fields used by EnhancedClientProfile
  initials?: string;
  status?: 'active' | 'attention' | 'inactive';
  lastSeen?: string;
  program?: string;
  motivationalQuote?: string;
  metrics?: {
    hrv: number;
    hrvBaseline: number;
    sleep: number;
    sleepTarget: number;
    readiness: number;
    workoutCompliance: number;
    weight: number;
    weightGoal: number;
  };
  alerts?: number;
  currentWeek?: number;
  totalWeeks?: number;
  nextWorkout?: string;
  journey?: Array<{
    date: string;
    type: 'milestone' | 'pr' | 'body_comp' | 'note' | 'program_change';
    title: string;
    description: string;
    value?: number;
    unit?: string;
  }>;
  moodEnergy?: Array<{
    date: string;
    mood: number; // 1-5 scale
    energy: number; // 1-5 scale
    notes?: string;
  }>;
  aiSuggestions?: Array<{
    id: string;
    type: 'workout' | 'nutrition' | 'recovery';
    title: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
    timestamp: string;
    reasoning: string;
    confidence: number;
    isLearning: boolean;
    basedOnCoachHistory?: string;
  }>;
  aiLearningHistory?: Array<{
    date: string;
    suggestion: string;
    coachAction: 'applied' | 'modified' | 'dismissed';
    outcome?: string;
    feedback?: 'positive' | 'negative';
  }>;
  aiAdaptationRate?: number;
}

interface GlobalState {
  // Auth-ish
  userType?: 'client' | 'coach';
  setUserType?: (userType: 'client' | 'coach' | null) => void;
  currentCoach?: Coach | null;
  authSession?: any;
  authUser?: any;
  signOut?: () => Promise<void> | void;
  authReady?: boolean;
  // Programs
  templates: WorkoutTemplate[];
  clientPrograms: ClientProgram[];

  // Workout Library
  savedWorkouts: SavedWorkout[];
  workouts: Record<
    string,
    { id: string; name: string; durationMin?: number; notes?: string; modality?: string; exercises: any[] }
  >;

  // Nutrition
  nutritionTemplates: NutritionTemplate[];
  clientNutritionPlans: ClientNutritionPlan[];
  foodItems: FoodItem[];

  // Coach data (pre-loaded like nutrition)
  coachPrograms: any[];
  coachWorkouts: any[];
  coachMeals: any[];

  // Clients
  clients: Client[];
  clientsLoadError?: string | null;
  clientsLoaded?: boolean;

  // Goals (client-side)
  clientGoalsMap: Record<string, ClientGoal[]>;

  // Client Dashboard domain
  programs?: Record<string, any>;
  clientProgramsV2?: Array<{ clientId: string; programId: string; status: 'active' | 'paused' | 'completed' }>;
  clientEvents?: Array<any>;
  programTemplates?: Array<any>;

  // Program Management
  saveTemplate: (template: WorkoutTemplate) => void;
  deleteTemplate: (templateId: string) => void;
  assignProgramToClients: (
    templateId: string,
    clientIds: string[],
    options: {
      startDate?: string;
      notes?: string;
      allowCustomizations: boolean;
    },
  ) => void;

  // Client Program Management
  updateClientProgram: (clientProgramId: string, updates: Partial<WorkoutTemplate>) => void;
  removeClientProgram: (clientProgramId: string) => Promise<void>;
  getClientPrograms: (clientId: string) => ClientProgram[];
  getClientProgram: (clientProgramId: string) => ClientProgram | undefined;
  // v2 compiled program update for client's active relation
  updateCompiledProgram: (
    clientId: string,
    patch: { name?: string; totalWeeks?: number; weeks?: Record<string, WorkoutDay[]> },
  ) => void;

  // Nutrition Management
  saveNutritionTemplate: (template: NutritionTemplate) => void;
  deleteNutritionTemplate: (templateId: string) => void;
  assignNutritionPlanToClients: (
    templateId: string,
    clientIds: string[],
    options: {
      startDate?: string;
      notes?: string;
      allowCustomizations: boolean;
    },
  ) => void;

  // Meals Management
  listCoachMeals: (opts?: { includePublic?: boolean; search?: string; type?: string }) => Promise<any[]>;
  getMealWithItems: (mealId: string) => Promise<any>;
  saveCoachMeal: (payload: any) => Promise<any>;
  deleteCoachMeal: (mealId: string) => Promise<void>;
  duplicateCoachMeal: (mealId: string) => Promise<any>;

  // Client Nutrition Management
  updateClientNutritionPlan: (planId: string, updates: Partial<NutritionTemplate>) => void;
  removeClientNutritionPlan: (planId: string) => void;
  getClientNutritionPlans: (clientId: string) => ClientNutritionPlan[];
  getClientNutritionPlan: (planId: string) => ClientNutritionPlan | undefined;
  loadNutritionAssignments: () => Promise<void>;

  // Coach data loading states
  coachDataLoading: boolean;
  coachDataReady: boolean;
  coachWorkoutsLoaded: boolean;
  lastSeenData: { [clientId: string]: string | null };

  // Optimistic mutation pending states
  pendingProgramDeletes: Set<string>;
  pendingProgramDuplicates: Set<string>;
  pendingWorkoutDeletes: Set<string>;
  pendingWorkoutDuplicates: Set<string>;

  // Food Item Management
  searchFoodItems: (query: string) => FoodItem[];
  addFoodItem: (foodItem: Omit<FoodItem, 'id'>) => string;

  // Client Management
  addClient: (
    client: Omit<Client, 'id' | 'assignedPrograms' | 'assignedNutritionPlans' | 'coachNotes'>,
  ) => Promise<string>;
  updateClient: (clientId: string, updates: Partial<Client>) => void;
  deleteClient: (clientId: string) => void;
  getClient: (clientId: string) => Client | undefined;

  // Client Notes Management
  addClientNote: (clientId: string, note: Omit<ClientNote, 'id'>) => void;
  updateClientNote: (clientId: string, noteId: string, updates: Partial<ClientNote>) => Promise<boolean>;
  deleteClientNote: (clientId: string, noteId: string) => void;
  getClientNotes: (clientId: string) => ClientNote[];
  loadClientNotes: (clientId: string, cursor?: string) => Promise<void>;
  loadMoreClientNotes: (clientId: string) => Promise<void>;
  clientHasMoreNotes: (clientId: string) => boolean;
  notesInFlight: Set<string>;

  // Goals actions
  listGoals: (clientId: string) => Promise<ClientGoal[]>;
  createClientGoal: (
    goal: Omit<ClientGoal, 'id' | 'createdAt' | 'updatedAt' | 'progress' | 'status' | 'history'>,
  ) => Promise<string>;
  updateGoal: (clientId: string, goalId: string, patch: Partial<ClientGoal>) => Promise<void>;
  deleteGoal: (clientId: string, goalId: string) => Promise<void>;
  addGoalProgress: (clientId: string, goalId: string, value: number, note?: string) => Promise<void>;
  clearGoalsCache: (clientId: string) => void;

  // Trackers
  listTrackers: (clientId: string) => Promise<ClientTracker[]>;
  createTracker: (args: {
    clientId: string;
    title: string;
    unit?: TrackerUnit;
    targetDirection?: 'down' | 'up' | 'stability';
    schedule: TrackerSchedule;
  }) => Promise<void> | void;
  updateTracker: (patch: Partial<ClientTracker> & { id: string; clientId: string }) => Promise<void> | void;
  deleteTracker: (clientId: string, trackerId: string) => Promise<void> | void;
  listTrackerEntries: (trackerId: string) => Promise<TrackerEntry[]>;
  addTrackerEntry: (args: {
    trackerId: string;
    clientId: string;
    value: number;
    note?: string;
    createdBy?: 'client' | 'coach';
  }) => Promise<void>;

  // Workout Library Management
  saveWorkout: (workout: SavedWorkout) => void;
  deleteWorkout: (workoutId: string) => void;
  getSavedWorkouts: () => SavedWorkout[];
  searchWorkouts: (query: string, tags?: string[]) => SavedWorkout[];
  createAndSaveWorkout: (partial: Partial<SavedWorkout> & Pick<SavedWorkout, 'name' | 'exercises'>) => SavedWorkout;

  // Program Template Assignment (dev-only)
  assignProgramTemplate?: (templateId: string, clientId: string, startDate: Date, programNameOverride?: string) => void;

  // Messaging (P1A)
  listThreadsForCoach?: (coachId: string) => Promise<any[]>;
  listThreadsForClient?: (clientId: string) => Promise<any[]>;
  sendMessage?: (args: {
    threadId?: string;
    clientId?: string;
    coachId?: string;
    body: string;
  }) => Promise<{ id: string; threadId: string } | void>;
  markThreadRead?: (args: { threadId: string; userType: 'coach' | 'client'; userId: string }) => Promise<void>;

  // Invites
  inviteClientByEmail?: (args: { email: string; clientName?: string }) => Promise<any>;
  /** false = API ok but 24h dedupe (no new email); true = email sent or clipboard fallback */
  sendInviteMagicLink?: (inviteId: string) => Promise<boolean>;

  // Intake v2
  createIntakePacket?: (inviteId: string, clientEmail: string) => Promise<IntakePacket>;
  getIntakeByInvite?: (inviteId: string) => Promise<IntakePacket | null>;
  saveIntakeAnswer?: (
    packetId: string,
    section: 'personal' | 'goals' | 'experience' | 'trackers' | 'form_flags',
    key: string,
    value: any,
  ) => Promise<void>;
  addIntakeGoal?: (
    packetId: string,
    goal: {
      title: string;
      unit?: string | null;
      targetValue?: number | null;
      priority?: 'low' | 'medium' | 'high';
      deadline?: string | null;
    },
  ) => Promise<IntakeGoal>;
  removeIntakeGoal?: (id: string) => Promise<void>;
  addIntakeTracker?: (
    packetId: string,
    tracker: {
      title: string;
      unit?: string | null;
      cadence?: string | null;
      reminders?: boolean;
      initialValue?: number | null;
      showInAnalytics?: boolean;
    },
  ) => Promise<IntakeTracker>;
  removeIntakeTracker?: (id: string) => Promise<void>;
  submitIntake?: (packetId: string) => Promise<void>;
  listIntakeGoals?: (packetId: string) => Promise<IntakeGoal[]>;
  listIntakeTrackers?: (packetId: string) => Promise<IntakeTracker[]>;
  listIntakeAnswers?: (packetId: string) => Promise<IntakeAnswer[]>;
  getIntakeForClient?: (clientId: string) => Promise<IntakePacket | null>;
  getIntakePacketById?: (packetId: string) => Promise<IntakePacket | null>;
  finalizeIntake?: (
    packetOrInviteId: string,
    packetSeed?: {
      id: string;
      inviteId?: string;
      clientEmail?: string;
      status?: string;
      goals?: any[];
      trackers?: any[];
      answers?: any[];
    },
  ) => Promise<{ clientId: string; migratedGoals: number; migratedTrackers: number; mergedAtISO: string }>;
  deleteIntakeAnswer?: (packetId: string, section: string, key: string) => Promise<boolean>;
  // Coach defaults for intake flags
  saveDefaultFormFlags?: (
    flags: Partial<{
      include_full_name: boolean;
      include_age: boolean;
      include_motivation: boolean;
      include_experience: boolean;
      include_injuries: boolean;
      include_trackers: boolean;
    }>,
  ) => Promise<void> | void;
  loadDefaultFormFlags?: () => Promise<{
    include_full_name: boolean;
    include_age: boolean;
    include_motivation: boolean;
    include_experience: boolean;
    include_injuries: boolean;
    include_trackers: boolean;
    include_height?: boolean;
    include_weight?: boolean;
  }>;
  loadDefaultTrackersTemplate?: () => Promise<
    Array<{
      title: string;
      unit?: string;
      cadence?: 'daily' | 'weekly';
      reminders?: boolean;
      showInAnalytics?: boolean;
      days?: string[];
    }>
  >;
  saveDefaultTrackersTemplate?: (
    rows: Array<{
      title: string;
      unit?: string;
      cadence?: 'daily' | 'weekly';
      reminders?: boolean;
      showInAnalytics?: boolean;
      days?: string[];
    }>,
  ) => Promise<void> | void;
  loadDefaultCustomQs?: () => Promise<Array<{ key?: string; prompt: string; required?: boolean }>>;
  saveDefaultCustomQs?: (rows: Array<{ key?: string; prompt: string; required?: boolean }>) => Promise<void> | void;
  seedPacketFromTemplate?: (packetId: string) => Promise<void>;

  // Exercises (Supabase-backed)

  // Workouts (Supabase-backed)
  listCoachWorkouts?: (search?: string) => Promise<any[]>;
  searchCoachWorkouts?: (query?: string, limit?: number) => Promise<any[]>;
  getWorkout?: (id: string) => Promise<any | null>;
  saveCoachWorkout?: (workout: any, items: any[]) => Promise<any | null>;
  deleteCoachWorkout?: (id: string, opts?: { hard?: boolean }) => Promise<boolean | null>;
  duplicateCoachWorkout?: (id: string) => Promise<any | null>;
  countCoachWorkouts?: () => Promise<number>;
  countWorkoutsCreatedThisWeek?: () => Promise<number>;
  countCoachPrograms?: () => Promise<number>;
  getLibraryCounts?: () => Promise<{
    programs_count: number;
    workouts_count: number;
    workouts_created_this_week: number;
  }>;

  // Programs (Supabase-backed)
  listCoachPrograms?: (search?: string) => Promise<any[]>;
  getProgram?: (id: string) => Promise<any | null>;
  saveCoachProgram?: (program: any, weeks?: any[], days?: any[]) => Promise<any | null>;
  duplicateCoachProgram?: (id: string) => Promise<any | null>;
  deleteCoachProgram?: (id: string, opts?: { hard?: boolean }) => Promise<boolean | null>;
  getExercisesByIds?: (ids: string[]) => Promise<any[]>;

  // Client Programs
  assignProgramToClient?: (
    programId: string,
    clientId: string,
    startDate: string,
    timezone?: string,
    replaceExisting?: boolean,
  ) => Promise<string>;
  listClientPrograms?: () => Promise<any[]>;
  getClientAssignments?: (clientId: string) => Promise<ClientProgramAssignment[]>;
  clearAssignmentsCache?: (clientId?: string) => void;
  getClientProgramDays?: (clientProgramId: string) => Promise<any[]>;
  saveClientDaySnapshot?: (
    dayId: string,
    items: any[],
    meta?: { name?: string | null; durationMin?: number | null; modality?: string | null; notes?: string | null },
  ) => Promise<boolean>;
  setClientDayWorkout?: (dayId: string, workoutId: string | null) => Promise<boolean>;
  clearClientDaySnapshot?: (dayId: string) => Promise<boolean>;
  createClientProgramDay?: (
    clientProgramId: string,
    args: { weekNumber: number; weekday: number; title?: string },
  ) => Promise<{ id: string } | null>;
  swapClientDayWorkouts?: (dayIdA: string, dayIdB: string) => Promise<boolean>;
  clearClientDayWorkout?: (dayId: string) => Promise<boolean>;
  getAssignmentStatusByDay?: (dayId: string) => Promise<string | null>;
  updateClientProgramMeta?: (
    assignmentId: string,
    patch: {
      displayName?: string | null;
      description?: string | null;
      difficulty?: string | null;
      tags?: string[] | null;
      totalWeeksOverride?: number | null;
    },
  ) => Promise<any>;
  archiveClientProgram?: (clientProgramId: string) => Promise<boolean>;

  // Client Management
  listClients?: () => Promise<Client[]>;
  createClientProfile?: (name: string, email: string, userId?: string, coachId?: string) => Promise<string>;
  archiveClient?: (clientId: string, archive: boolean) => Promise<boolean>;
}

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

export function useGlobalState() {
  const context = useContext(GlobalStateContext);
  if (context === undefined) {
    // Return a minimal safe fallback to avoid hard crashes when a component
    // renders outside of the provider (e.g., isolated screens, tests, stories).
    // Dev-only warning; keep UX functional where possible.
    try {
      const isDev = (import.meta as any).env?.DEV === true;
      if (isDev) {
        // eslint-disable-next-line no-console
        console.warn('[GlobalState] useGlobalState called outside provider. Using safe fallback.');
      }
    } catch {
      /* ignored */
    }
    const SAFE_DEFAULT: any = {
      // Core collections
      templates: [],
      clientPrograms: [],
      savedWorkouts: [],
      workouts: {},
      nutritionTemplates: [],
      clientNutritionPlans: [],
      foodItems: [],
      clients: [],
      clientGoalsMap: {},
      programs: {},
      clientProgramsV2: [],
      clientEvents: [],
      programTemplates: [],
      // Program management (no-ops)
      saveTemplate: () => {},
      deleteTemplate: () => {},
      assignProgramToClients: () => {},
      updateClientProgram: () => {},
      removeClientProgram: async () => {},
      getClientPrograms: () => [],
      getClientProgram: () => undefined,
      // Nutrition management (no-ops)
      saveNutritionTemplate: () => {},
      deleteNutritionTemplate: () => {},
      assignNutritionPlanToClients: () => {},
      updateClientNutritionPlan: () => {},
      removeClientNutritionPlan: () => {},
      getClientNutritionPlans: () => [],
      getClientNutritionPlan: () => undefined,
      loadNutritionAssignments: async () => {},
      // Meals management (no-ops)
      listCoachMeals: async () => [],
      getMealWithItems: async () => null,
      saveCoachMeal: async () => ({}),
      deleteCoachMeal: async () => {},
      duplicateCoachMeal: async () => ({}),
      // Food items (no-ops)
      searchFoodItems: () => [],
      addFoodItem: () => {},
      // Clients (no-ops)
      addClient: () => {},
      updateClient: () => {},
      deleteClient: () => {},
      getClient: () => undefined,
      addClientNote: () => {},
      updateClientNote: async () => false,
      deleteClientNote: () => {},
      getClientNotes: () => [],
      loadClientNotes: async () => {},
      loadMoreClientNotes: async () => {},
      clientHasMoreNotes: () => false,
      notesInFlight: new Set(),
      // Goals (no-ops)
      listGoals: () => [],
      createClientGoal: async () => 'goal_0',
      updateGoal: async () => {},
      deleteGoal: async () => {},
      addGoalProgress: async () => {},
      // Workout library (no-ops)
      saveWorkout: () => {},
      deleteWorkout: () => {},
      getSavedWorkouts: () => [],
      searchWorkouts: () => [],
      createAndSaveWorkout: (partial: any) => ({ id: '0', name: String(partial?.name ?? 'Untitled'), exercises: [] }),
      // Program template assignment (optional)
      assignProgramTemplate: undefined,
    };
    return SAFE_DEFAULT as GlobalState;
  }
  return context;
}

interface GlobalStateProviderProps {
  children: ReactNode;
}

// Helper function to migrate old clients to new format
const _migrateClient = (client: any): Client => {
  return {
    ...client,
    coachNotes: Array.isArray(client.coachNotes) ? client.coachNotes : [],
    assignedPrograms: Array.isArray(client.assignedPrograms) ? client.assignedPrograms : [],
    assignedNutritionPlans: Array.isArray(client.assignedNutritionPlans) ? client.assignedNutritionPlans : [],
  };
};

// Helper function to normalize/migrate saved workouts to current format
const migrateSavedWorkout = (w: any): SavedWorkout => {
  const exercises: Exercise[] = Array.isArray(w?.exercises)
    ? w.exercises.map((e: any) => {
        const ex: Exercise = {
          id: e?.id ?? `e_${Date.now()}_${Math.random()}`,
          name: String(e?.name ?? '').trim(),
          // legacy reps
          sets: e?.sets != null ? Number(e.sets) : undefined,
          reps: e?.reps != null ? String(e.reps).trim() : undefined,
          // new tracking fields (best-effort inference)
          trackBy: e?.trackBy,
          durationSec: e?.durationSec != null ? Number(e.durationSec) : undefined,
          distance: e?.distance != null ? Number(e.distance) : undefined,
          distanceUnit: e?.distanceUnit === 'km' ? 'km' : e?.distanceUnit === 'mi' ? 'mi' : undefined,
          intervalType: e?.intervalType === 'distance' ? 'distance' : e?.intervalType === 'time' ? 'time' : undefined,
          targetPaceSec: e?.targetPaceSec != null ? Number(e.targetPaceSec) : undefined,
          rounds: e?.rounds != null ? Number(e.rounds) : undefined,
          workSec: e?.workSec != null ? Number(e.workSec) : undefined,
          restSec: e?.restSec != null ? Number(e.restSec) : undefined,
          effort: e?.effort ? String(e.effort) : undefined,
          rest: e?.rest ? String(e.rest) : undefined,
          notes: e?.notes ? String(e.notes) : undefined,
          intensity: e?.intensity ? String(e.intensity) : undefined,
        };
        // infer trackBy if missing
        if (!ex.trackBy) {
          if (ex.durationSec) ex.trackBy = 'time';
          else if (ex.distance) ex.trackBy = 'distance';
          else if (ex.rounds || ex.workSec) ex.trackBy = 'interval';
          else ex.trackBy = 'reps';
        }
        return ex;
      })
    : [];
  const tags: string[] = Array.isArray(w?.tags) ? w.tags.filter(Boolean).map((t: any) => String(t).trim()) : [];
  const estimated =
    typeof w?.estimatedDuration === 'number'
      ? w.estimatedDuration
      : Math.max(
          15,
          exercises.reduce((mins, ex) => mins + Math.ceil(((Number(ex.sets) || 0) * 120) / 60), 0),
        );

  return {
    id: String(w?.id ?? `w_${Date.now()}_${Math.random()}`),
    name: String(w?.name ?? 'Untitled Workout'),
    exercises,
    notes: w?.notes ? String(w.notes) : undefined,
    estimatedDuration: estimated,
    tags,
    createdDate: w?.createdDate ?? new Date().toISOString(),
    difficulty: (w?.difficulty as SavedWorkout['difficulty']) ?? 'Beginner',
    category: w?.category ? String(w.category) : undefined,
    modality: w?.modality as any,
  };
};

// Helper function to migrate nutrition templates to include dayType field
export const migrateNutritionTemplate = (template: any): NutritionTemplate => {
  if (!template || !Array.isArray(template.days)) {
    return template;
  }

  return {
    ...template,
    days: template.days.map((day: any) => {
      // Backfill: set dayType = dayName if undefined, then normalize
      const rawDayType = day.dayType ?? day.dayName;
      return {
        ...day,
        dayType: normalizeDayType(rawDayType),
      };
    }),
  };
};

function normalizeTrackerBeforeSave(t: ClientTracker): ClientTracker {
  const schedule = (t.schedule ?? { cadence: 'weekly', daysOfWeek: [0], remindersEnabled: true }) as TrackerSchedule;
  const baseDay = t.lastLoggedAtISO ? String(t.lastLoggedAtISO).slice(0, 10) : todayISO();

  let nextDueOn: string | undefined;
  try {
    nextDueOn = computeNextTrackerDue(schedule, baseDay) || undefined;
  } catch (e) {
    console.warn('Failed to compute next due date for tracker:', e);
    nextDueOn = undefined;
  }

  return { ...t, schedule, nextDueOn } as ClientTracker;
}

export function GlobalStateProvider({ children }: GlobalStateProviderProps) {
  // Add a stable key to prevent HMR issues
  const [providerKey] = useState(() => `gsp-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [userType, _setUserType] = useState<'client' | 'coach'>(() => {
    try {
      const v = localStorage.getItem('coachd:userType');
      if (v === 'coach' || v === 'client') return v;
    } catch {
      /* ignored */
    }
    return 'client';
  });

  const setUserType = React.useCallback((t: 'client' | 'coach' | null) => {
    const next = (t ?? 'client') as 'client' | 'coach';
    _setUserType(next);
    try {
      localStorage.setItem('coachd:userType', next);
    } catch {
      /* ignored */
    }
  }, []);
  const [currentCoach, setCurrentCoach] = useState<Coach | null>(null);
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [clientPrograms, setClientPrograms] = useState<ClientProgram[]>([]);
  const [savedWorkouts, setSavedWorkouts] = useState<SavedWorkout[]>([]);
  const [nutritionTemplates, setNutritionTemplates] = useState<NutritionTemplate[]>([]);
  const [clientNutritionPlans, setClientNutritionPlans] = useState<ClientNutritionPlan[]>([]);
  const [foodItems, setFoodItems] = useState<FoodItem[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoadError, setClientsLoadError] = useState<string | null>(null);
  const [clientsLoaded, setClientsLoaded] = useState<boolean>(false);
  // Coach programs (pre-loaded like nutrition templates)
  const [coachPrograms, setCoachPrograms] = useState<any[]>([]);
  const [coachWorkouts, setCoachWorkouts] = useState<any[]>([]);
  const [coachMeals, setCoachMeals] = useState<any[]>([]);
  const clientsRef = React.useRef<Client[]>([]);
  React.useEffect(() => {
    clientsRef.current = clients;
  }, [clients]);
  const [clientGoalsMap, setClientGoalsMap] = useState<Record<string, ClientGoal[]>>({});
  const [clientTrackersMap, setClientTrackersMap] = useState<Record<string, ClientTracker[]>>({});
  // Client Dashboard domain state (loose typed here to avoid coupling)
  const [programs, setPrograms] = useState<Record<string, any>>({});
  const [clientProgramsV2, setClientProgramsV2] = useState<
    Array<{ clientId: string; programId: string; status: 'active' | 'paused' | 'completed' }>
  >([]);
  const [clientEvents, setClientEvents] = useState<Array<any>>([]);
  const [programTemplates, setProgramTemplates] = useState<Array<any>>([]);
  const coachId = currentCoach?.id ?? 'coach_demo';
  const useMocks = (import.meta as any).env?.VITE_USE_MOCKS === 'true';
  // Optional Supabase auth state (mock-safe)
  const [authSession, setAuthSession] = useState<any>(undefined);
  const [authUser, setAuthUser] = useState<any>(null);
  const [authReady, setAuthReady] = useState<boolean>(() => !isSupabaseConfigured());

  // Coach data loading states for optimized initial load
  const [coachDataLoading, setCoachDataLoading] = useState<boolean>(false);
  const [coachDataReady, setCoachDataReady] = useState<boolean>(false);
  // Exercises now live in src/stores/exerciseLibraryStore.ts (ARCH-7); workouts keep their own flag.
  const [coachWorkoutsLoaded, setCoachWorkoutsLoaded] = useState<boolean>(false);
  const [lastSeenData, setLastSeenData] = useState<{ [clientId: string]: string | null }>({});

  // Optimistic mutation pending states
  const [pendingProgramDeletes, setPendingProgramDeletes] = useState<Set<string>>(new Set());
  const [pendingProgramDuplicates, setPendingProgramDuplicates] = useState<Set<string>>(new Set());
  const [pendingWorkoutDeletes, setPendingWorkoutDeletes] = useState<Set<string>>(new Set());
  const [pendingWorkoutDuplicates, setPendingWorkoutDuplicates] = useState<Set<string>>(new Set());
  useEffect(() => {
    const configured = isSupabaseConfigured();
    const sb = getSupabase();
    // If not configured or client couldn't be created, unblock UI (mock mode)
    if (!configured || !sb) {
      setAuthSession(null);
      try {
        const stored = localStorage.getItem('coachd:userType') as any;
        const fallback = stored === 'coach' || stored === 'client' ? stored : 'coach';
        setUserType(fallback);
        localStorage.setItem('coachd:userType', fallback);
      } catch {
        /* ignored */
      }
      setAuthReady(true);
      return;
    }
    let cancelled = false;
    let authSubscription: { unsubscribe: () => void } | null = null;

    (async () => {
      try {
        // First, try to get session from Worker (httpOnly cookies)
        try {
          const workerResponse = await csrfFetch('/api/auth/session', {
            credentials: 'include',
          });
          if (workerResponse.ok) {
            const ct = (workerResponse.headers.get('content-type') || '').toLowerCase();
            // Vite-only dev often returns index.html (200) for /api/* — skip authoritative worker handling.
            if (ct.includes('application/json')) {
              const { session } = (await workerResponse.json()) as { session?: { user?: { id?: string } } | null };
              const user = session?.user;

              // Server cookies are the source of truth when the worker responds. If there is no cookie
              // session, do not fall through to sb.auth.getSession() — localStorage can still hold tokens
              // after sign-out or failed cookie sync, which incorrectly unlocks the shell.
              if (!user) {
                try {
                  await sb.auth.signOut({ scope: 'local' });
                } catch {
                  /* ignored */
                }
                if (!cancelled) {
                  setAuthSession(null);
                  setAuthUser(null);
                }
                return;
              }

              if (!cancelled) {
                // Sync session from Worker to Supabase client
                try {
                  // First, check if the client already has a session
                  const { data: localSession } = await sb.auth.getSession();

                  if (localSession?.session?.user?.id === user.id) {
                    setAuthSession(localSession.session);
                  } else {
                    // Fetch the full session from the Worker
                    const sessionResponse = await csrfFetch('/api/auth/session');
                    if (sessionResponse.ok) {
                      const { session: workerSession } = await sessionResponse.json();

                      if (workerSession?.access_token && workerSession?.refresh_token) {
                        const { data, error } = await sb.auth.setSession({
                          access_token: workerSession.access_token,
                          refresh_token: workerSession.refresh_token,
                        });

                        if (error) {
                          console.error('[auth] Failed to sync session:', error);
                          setAuthSession({ user }); // Minimal session object
                        } else {
                          setAuthSession(data.session);
                        }
                      } else if (workerSession) {
                        console.warn('[auth] Worker session missing tokens; use local getSession if present');
                        const { data: again } = await sb.auth.getSession();
                        setAuthSession(again?.session?.user?.id === user.id ? again.session : { user });
                      } else {
                        console.warn('[auth] ⚠️ No session returned from Worker');
                        setAuthSession({ user }); // Minimal session object
                      }
                    } else {
                      console.error('[auth] Failed to fetch session from Worker');
                      setAuthSession({ user }); // Minimal session object
                    }
                  }
                } catch (syncErr) {
                  console.error('[auth] Error syncing Supabase client session:', syncErr);
                  setAuthSession({ user }); // Minimal session object
                }

                setAuthUser(user);
                return;
              }
            }
          }
        } catch (err) {
          console.warn('[auth] Worker session fetch failed, falling back to localStorage:', err);
        }

        // Fallback: try localStorage session (old flow)
        const { data } = await sb.auth.getSession();
        if (cancelled) return;
        setAuthSession(data?.session ?? null);
        setAuthUser(data?.session?.user ?? null);
      } finally {
        if (!cancelled) setAuthReady(true);
        // Must live in `finally`: early `return` paths inside `try` skip any code after this block.
        // Subscribe only after bootstrap — otherwise INITIAL_SESSION replays localStorage before the
        // worker reconciliation above, causing a one-frame "logged in" flash on protected routes.
        if (!cancelled && sb) {
          const { data: listener } = sb.auth.onAuthStateChange((_e, session) => {
            if (_e === 'TOKEN_REFRESHED' && session?.access_token && session?.refresh_token) {
              syncSessionToServer({
                access_token: session.access_token,
                refresh_token: session.refresh_token,
              });
            }
            setAuthSession(session ?? null);
            setAuthUser(session?.user ?? null);
            setAuthReady(true);
          });
          authSubscription = listener.subscription;
        }
      }
    })();

    return () => {
      cancelled = true;
      authSubscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intake v2 backend health check (dev only)
  useEffect(() => {
    try {
      if ((import.meta as any).env?.DEV) {
        isIntakeBackendReady()
          .then((ok) => {
            try {
              console.debug('[Intake v2 health]', {
                ok,
                backend: (import.meta as any).env?.VITE_INTAKE_BACKEND || 'local',
              });
            } catch {
              /* ignored */
            }
          })
          .catch(() => {});
      }
    } catch {
      /* ignored */
    }
  }, []);

  // Resolve and persist role whenever session changes
  useEffect(() => {
    if (authSession === undefined) return; // not fetched yet
    try {
      const metaRole = (authSession?.user as any)?.user_metadata?.role;
      const stored = localStorage.getItem('coachd:userType') as 'coach' | 'client' | null;
      const resolved =
        metaRole === 'coach' || metaRole === 'client'
          ? metaRole
          : stored === 'coach' || stored === 'client'
            ? stored
            : 'coach';
      if (resolved && resolved !== userType) {
        setUserType(resolved);
        try {
          localStorage.setItem('coachd:userType', resolved);
        } catch {
          /* ignored */
        }
      }
    } catch {
      /* ignored */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authSession]);

  // Sentry: set user context when authenticated (hashed IDs only).
  // PostHog: identify with the real coach id so analytics attribute to a user.
  useEffect(() => {
    if (authUser?.id) {
      setSentryUser({
        id: authUser.id,
        coachId: (currentCoach?.id as string) ?? undefined,
      });
      identifyPostHog({ id: authUser.id, email: authUser.email ?? undefined });
    } else {
      clearSentryUser();
      resetPostHog();
    }
  }, [authUser?.id, authUser?.email, currentCoach?.id]);

  // Fetch CSRF token when authenticated so all mutation requests can include it
  useEffect(() => {
    if (!authSession) return;
    fetch('/api/csrf/token', { credentials: 'include' })
      .then((r) => r.json())
      .then((json: any) => {
        if (json.ok && json.token) {
          // The server set the __csrf cookie via Set-Cookie header.
          // Store token in memory as fallback in case cookie is not readable.
          (window as any).__csrfToken = json.token;
        }
      })
      .catch(() => {});
  }, [authSession]);

  // Bootstrap coach in both mock and non-mock modes
  useEffect(() => {
    // Wait for auth to be ready before setting coach
    if (!authReady) {
      return;
    }

    void (async () => {
      try {
        if (!currentCoach) {
          if (useMocks) {
            // Mock mode: use demo coach
            const devCoach: Coach = { id: 'coach_demo', name: 'Alex Chen' } as any;
            setCurrentCoach(devCoach);
          } else {
            // Non-mock mode: use authenticated user as coach
            const sb = getSupabase();
            if (sb && authUser) {
              let profileName =
                authUser.user_metadata?.full_name ||
                authUser.user_metadata?.name ||
                authUser.email?.split('@')[0] ||
                'Coach';
              try {
                const { data: profile } = await sb.from('profiles').select('full_name').eq('id', authUser.id).single();
                if (profile?.full_name) profileName = profile.full_name;
              } catch (profileErr) {
                console.warn('[coach-bootstrap] Failed to load profile name, using auth metadata fallback', profileErr);
              }
              const realCoach: Coach = {
                id: authUser.id,
                name: profileName,
                email: authUser.email,
              };
              setCurrentCoach(realCoach);
            } else {
              // No Supabase or no auth user available
            }
          }
        } else {
          /* no-op */
        }

        // Link clients to coach (mock mode only)
        if (useMocks) {
          setClients((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;
            const next = prev.map((c) => {
              if (String(c.id) === 'c1' && !(c as any).primaryCoachId) {
                return { ...c, primaryCoachId: currentCoach?.id ?? 'coach_demo' } as any;
              }
              return c;
            });
            return next;
          });
        }
      } catch (error) {
        console.error('Failed to set currentCoach:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMocks, currentCoach?.id, authUser, authReady]);

  useEffect(() => {
    const handleProfileUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ full_name?: string }>).detail;
      if (!detail?.full_name || !authUser?.id) return;
      setCurrentCoach((prev) => {
        if (!prev || String(prev.id) !== String(authUser.id)) return prev;
        return { ...prev, name: detail.full_name } as any;
      });
    };

    window.addEventListener('coachd:profile-updated', handleProfileUpdated as EventListener);
    return () => window.removeEventListener('coachd:profile-updated', handleProfileUpdated as EventListener);
  }, [authUser?.id]);
  const str = (x: any) => String(x);
  const addClientEvent = async (evt: Omit<ClientEvent, 'id' | 'createdAt' | 'coachId'>) =>
    addClientEventBase(currentCoach?.id ?? null, evt);
  // ---- Trackers ----
  const listTrackers = React.useCallback(
    async (clientId: string) => {
      // Use Worker endpoint for consistent authentication
      // Note: Cache intentionally bypassed to ensure fresh data with proper camelCase mapping
      try {
        const response = await fetch(`/api/coach/trackers?clientId=${encodeURIComponent(clientId)}`);

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to list trackers');
        }

        const result = await response.json();

        // Transform database format to ClientTracker format
        const trackers = (result.trackers || []).map((row: any) => ({
          id: row.id,
          clientId: row.client_id,
          ownerCoachId: row.owner_coach_id,
          title: row.title,
          description: row.description,
          unit: row.unit,
          targetDirection: row.target_direction,
          schedule: row.schedule,
          showInAnalytics: row.show_in_analytics,
          isArchived: row.is_archived,
          lastLoggedAtISO: row.last_logged_at,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
          nextDueOn: row.next_due_on,
        }));

        // Update cache with fresh data
        setClientTrackersMap((prev) => ({
          ...prev,
          [String(clientId)]: trackers,
        }));

        return trackers;
      } catch (error) {
        console.error('Failed to list trackers via Worker, falling back to store:', error);
        // Fallback to store if Worker fails
        return trackersStore.listTrackers(String(clientId));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientTrackersMap],
  );

  const createTracker = React.useCallback(
    async (args: {
      clientId: string;
      title: string;
      unit?: TrackerUnit;
      targetDirection?: 'down' | 'up' | 'stability';
      schedule: TrackerSchedule;
    }) => {
      if (userType !== 'coach') {
        // Continue anyway for development/testing
      }

      // Get real client ID
      let realClientId = String(args.clientId);

      // If clientId is a mock ID, try to find the real client UUID
      if (args.clientId.length < 10) {
        // Mock IDs are short
        const realClient = clients.find(
          (c) => String(c.id).includes(args.clientId) || c.name?.toLowerCase().includes('demo'),
        );
        if (realClient) {
          realClientId = realClient.id;
        }
      }

      try {
        // Use Worker endpoint instead of direct Supabase
        const response = await trackersApi.createTracker({
          clientId: realClientId,
          title: String(args.title || '').trim(),
          unit: args.unit ?? 'lbs',
          targetDirection: args.targetDirection ?? 'stability',
          schedule: args.schedule ?? { cadence: 'weekly', daysOfWeek: [0], remindersEnabled: true },
        });

        if (!response.ok) {
          throw new Error(bodyError(response) || 'Failed to create tracker');
        }

        const result = response as { tracker: Record<string, any> };
        // Update local cache immediately to avoid refresh delay
        const newTracker = {
          id: result.tracker.id,
          clientId: realClientId,
          ownerCoachId: result.tracker.owner_coach_id,
          title: String(args.title || '').trim(),
          unit: args.unit ?? 'lbs',
          targetDirection: args.targetDirection ?? 'stability',
          schedule: args.schedule ?? { cadence: 'weekly', daysOfWeek: [0], remindersEnabled: true },
          showInAnalytics: true,
          isArchived: false,
          lastLoggedAtISO: undefined,
          createdAt: result.tracker.created_at,
          updatedAt: result.tracker.updated_at,
        };

        // Update the cache immediately
        setClientTrackersMap((prev) => ({
          ...prev,
          [String(realClientId)]: [...(prev[String(realClientId)] || []), newTracker],
        }));
      } catch (error) {
        console.error('❌ Failed to create tracker:', error);
        throw error;
      }
    },
    [userType, clients, setClientTrackersMap],
  );

  const updateTracker = React.useCallback(
    async (patch: Partial<ClientTracker> & { id: string; clientId: string }) => {
      if (userType !== 'coach') return;

      // Use Worker endpoint for updates
      try {
        // trackersApi.updateTracker supplies the required If-Match (this call used to 428).
        const response = await trackersApi.updateTracker(patch.id, {
          title: patch.title,
          unit: patch.unit,
          targetDirection: patch.targetDirection,
          schedule: patch.schedule,
          showInAnalytics: patch.showInAnalytics,
        });

        if (!response.ok) {
          console.error('[updateTracker] Worker update failed:', response.body ?? response.error);
          throw new Error(
            response.status === 409
              ? 'This tracker was changed elsewhere. Reload and try again.'
              : bodyError(response) || 'Failed to update tracker',
          );
        }

        const serverUpdatedAt = response.tracker?.updatedAt as string | undefined;
        // Update local state to reflect the change immediately (keep the server's version stamp).
        setClientTrackersMap((prev) => ({
          ...prev,
          [String(patch.clientId)]: (prev[String(patch.clientId)] || []).map((t) =>
            t.id === patch.id ? { ...t, ...patch, ...(serverUpdatedAt ? { updatedAt: serverUpdatedAt } : {}) } : t,
          ),
        }));
      } catch (error) {
        console.error('[updateTracker] Update failed:', error);
        throw error;
      }
    },
    [userType, setClientTrackersMap],
  );

  // ----- Intake v2 actions -----
  const createIntakePacket = React.useCallback(
    async (inviteId: string, clientEmail: string): Promise<IntakePacket> => {
      // IMPORTANT: Get coach_id from the INVITE, not the current user!
      // The current user might be the client (on their onboarding page)
      let coach: string | undefined;
      try {
        const { invitesStore } = await import('@/data/storage/app/invites.adapter');
        const invite = await (invitesStore as any).get?.(inviteId);
        coach = invite?.coachId || invite?.coach_id;
      } catch (e) {
        console.warn('[createIntakePacket] Failed to get coach from invite:', e);
      }

      // Fallback to currentCoach only if invite lookup failed
      if (!coach) {
        coach = currentCoach?.id;
      }

      // Final fallback for development
      coach = coach || 'coach_demo';

      const pkt = await intakeStore.createPacket(String(inviteId), String(coach), String(clientEmail));
      try {
        await seedPacketFromTemplate(String(pkt.id));
      } catch (e) {
        try {
          console.warn('seed on create failed:', e);
        } catch {
          /* ignored */
        }
      }
      return pkt;
    },
    [currentCoach?.id],
  );

  const getIntakeByInvite = React.useCallback(async (inviteId: string): Promise<IntakePacket | null> => {
    return intakeStore.getPacketByInvite(String(inviteId));
  }, []);

  // Exercise library: moved to src/stores/exerciseLibraryStore.ts (ARCH-7 golden slice).

  // ----- Workouts (Supabase-backed) -----
  const listCoachWorkouts = React.useCallback(
    async (search?: string) => {
      try {
        let ownerId: string | undefined;
        try {
          const sbMod = await import('@/lib/supabase');
          const sb = (sbMod as any).getSupabase?.();
          const user = await sb?.auth.getUser();
          ownerId = user?.data?.user?.id || ownerId;
        } catch {
          /* ignored */
        }
        ownerId = ownerId || String(currentCoach?.id || 'coach_demo');
        const mod = await import('@/data/storage/workouts/adapter');
        return await (mod as any).listWorkoutsForCoach(ownerId, { search, includePublic: true, limit: 50 });
      } catch (e) {
        try {
          console.warn('[workouts] listCoachWorkouts failed', e);
        } catch {
          /* ignored */
        }
        return [];
      }
    },
    [currentCoach?.id],
  );

  const searchCoachWorkouts = React.useCallback(async (query?: string, limit = 200) => {
    try {
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (!sb) throw new Error('Supabase not configured');
      const {
        data: { user },
      } = await sb.auth.getUser();
      if (!user?.id) throw new Error('Not authenticated');

      let q = sb
        .from('workouts')
        .select(
          `
          id, name, estimated_duration_min, modality, updated_at,
          workout_exercises(count)
        `,
        )
        .eq('owner_coach_id', user.id)
        .is('deleted_at', null)
        .order('updated_at', { ascending: false })
        .limit(limit);

      if (query && query.trim()) {
        q = q.ilike('name', `%${query.trim()}%`);
      }

      const { data, error } = await q;
      if (error) throw error;

      return (data || []).map((w: any) => ({
        id: w.id,
        name: w.name,
        estimatedDurationMin: w.estimated_duration_min,
        modality: w.modality,
        exerciseCount: w.workout_exercises?.[0]?.count ?? 0,
        updatedAt: w.updated_at,
      }));
    } catch (e) {
      console.error('[workouts] searchCoachWorkouts failed', e);
      return [];
    }
  }, []);

  const getWorkout = React.useCallback(async (id: string) => {
    try {
      // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
      const res = await fetch(`/api/coach/workouts/${id}`, { credentials: 'include' });
      if (!res.ok) {
        console.error('[provider:getWorkout] Worker fetch failed:', res.status);
        return null;
      }

      const { workout: data } = await res.json();
      if (!data) return null;

      // Map workout_exercises to items array
      const items = (data?.workout_exercises ?? [])
        .sort((a: any, b: any) => (a?.order_index ?? 0) - (b?.order_index ?? 0))
        .map((we: any) => ({
          id: we.id,
          workoutId: we.workout_id,
          exerciseId: we.exercise_id,
          customName: we.custom_name ?? null,
          orderIndex: we.order_index,
          groupId: we.group_id ?? null,
          groupType: we.group_type ?? null,
          withinGroupOrder: we.within_group_order ?? null,
          notes: we.notes ?? null,
          showInAnalytics: !!we.show_in_analytics,
          trackBy: we.track_by ?? null,
          sets: we.sets ?? null,
          reps: we.reps ?? null,
          durationSec: we.duration_sec ?? null,
          distance: we.distance ?? null,
          distanceUnit: we.distance_unit ?? null,
          rounds: we.rounds ?? null,
          workSec: we.work_sec ?? null,
          restSec: we.rest_sec ?? null,
          intervalType: we.interval_type ?? null,
          tempo: we.tempo ?? null,
          restBetweenSetsSec: we.rest_between_sets_sec ?? null,
          effort: we.effort ?? null,
          intensity: we.intensity ?? null,
          targetPaceSec: we.target_pace_sec ?? null,
          loadKg: we.load_kg ?? null,
          loadPct1rm: we.load_pct_1rm ?? null,
          inclinePct: we.incline_pct ?? null,
          speedKph: we.speed_kph ?? null,
          cadenceRpm: we.cadence_rpm ?? null,
          unilateralSide: we.unilateral_side ?? null,
          executionNotes: we.execution_notes ?? null,
          alternatives: we.alternatives ?? null,
          targetMuscles: we.target_muscles ?? null,
          setPatterns: we.set_patterns ?? null,
          isOptional: we.is_optional ?? null,
          isAdvanced: we.is_advanced ?? null,
          exercise: we.exercises
            ? {
                id: we.exercises.id,
                slug: we.exercises.slug ?? null,
                name: we.exercises.name,
                modality: we.exercises.modality ?? null,
                primaryMuscles: we.exercises.primary_muscles ?? null,
                equipment: we.exercises.equipment ?? null,
                videoUrl: we.exercises.video_url ?? null,
              }
            : null,
        }));

      return {
        id: data.id,
        ownerCoachId: data.owner_coach_id,
        slug: data.slug ?? null,
        name: data.name,
        modality: data.modality ?? null,
        difficulty: data.difficulty ?? null,
        estimatedDurationMin: data.estimated_duration_min ?? null,
        notes: data.notes ?? null,
        tags: data.tags ?? null,
        createdAt: data.created_at,
        updatedAt: data.updated_at ?? null,
        itemsCount: items.length,
        items,
      };
    } catch (e) {
      try {
        console.warn('[workouts] getWorkout failed', e);
      } catch {
        /* ignored */
      }
      return null;
    }
  }, []);

  const saveCoachWorkout = React.useCallback(async (workout: any, items: any[]) => {
    try {
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (Array.isArray(items) && items.length === 0) {
        throw new Error('No exercises to save');
      }
      // Warn if any incoming rows contain a workout_id field; we will drop it defensively
      try {
        if ((items ?? []).some((x: any) => 'workout_id' in (x || {}) || 'workoutId' in (x || {}))) {
          console.warn('[provider] dropping workout_id from items payload');
        }
      } catch {
        /* ignored */
      }

      // Strict UUID coercion and validation
      function coerceExerciseId(it: any): string | null {
        const cand = it?.exercise_id ?? it?.exerciseId ?? it?.exercise?.id ?? null;
        return typeof cand === 'string' && UUID_RE.test(cand) ? cand : null;
      }

      function mapItemsToRpc(items: any[]): any[] {
        return (items || []).map((itRaw, idx) => {
          const exId = coerceExerciseId(itRaw);
          if (!exId) {
            // Surface a clear error (this is what you saw as 22P02 downstream)
            console.error('[provider] bad exercise_id in item', { itRaw });
            throw new Error(`Bad exercise_id in items (not a UUID). Item ${idx}: ${JSON.stringify(itRaw, null, 2)}`);
          }
          const it = itRaw || {};
          const toInt = (v: any) => (v === '' || v == null ? null : Number.parseInt(String(v), 10));
          const toNum = (v: any) => (v === '' || v == null ? null : Number(v));
          const emptyToNull = (v: any) => (v === '' || v == null ? null : String(v));

          return {
            exercise_id: exId,
            custom_name: emptyToNull(it.custom_name ?? it.customName),
            order_index: it.order_index ?? it.orderIndex ?? idx + 1,
            track_by: it.track_by ?? it.trackBy ?? 'reps',
            sets: toInt(it.sets),
            reps: emptyToNull(it.reps),
            duration_sec: toInt(it.duration_sec ?? it.durationSec),
            distance: toNum(it.distance),
            distance_unit: emptyToNull(it.distance_unit ?? it.distanceUnit),
            target_pace_sec: toInt(it.target_pace_sec ?? it.targetPaceSec),
            rounds: toInt(it.rounds),
            work_sec: toInt(it.work_sec ?? it.workSec),
            rest_sec: toInt(it.rest_sec ?? it.restSec),
            interval_type: emptyToNull(it.interval_type ?? it.intervalType),
            effort: emptyToNull(it.effort),
            tempo: emptyToNull(it.tempo),
            rest_between_sets_sec: toInt(it.rest_between_sets_sec ?? it.restBetweenSetsSec),
            intensity: emptyToNull(it.intensity),
            load_kg: toNum(it.load_kg ?? it.loadKg),
            load_pct_1rm: toNum(it.load_pct_1rm ?? it.loadPct1rm),
            incline_pct: toNum(it.incline_pct ?? it.inclinePct),
            speed_kph: toNum(it.speed_kph ?? it.speedKph),
            cadence_rpm: toNum(it.cadence_rpm ?? it.cadenceRpm),
            unilateral_side: emptyToNull(it.unilateral_side ?? it.unilateralSide),
            group_id: it.group_id || null,
            group_type: emptyToNull(it.group_type),
            within_group_order: toInt(it.within_group_order),
            notes: emptyToNull(it.notes),
            show_in_analytics: !!it.show_in_analytics,
          };
        });
      }

      const p_items = mapItemsToRpc(items || []);
      if (p_items.length === 0) {
        throw new Error('No exercises to save');
      }
      // Build a strict snake_case meta payload expected by RPC
      const p_workout = {
        name: workout?.name ?? '',
        modality: workout?.modality ?? 'strength',
        notes: workout?.notes ?? '',
        difficulty: workout?.difficulty ?? undefined,
        estimated_duration_min: workout?.estimatedDurationMin ?? workout?.estimated_duration_min ?? null,
        id: workout?.id ?? undefined,
        slug: workout?.slug ?? undefined,
      };

      // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
      const res = await workoutsApi.saveWorkout(p_workout, p_items);

      if (!res.ok) {
        throw new Error(bodyError(res) || 'Failed to save workout');
      }

      const { workout: savedWorkout } = (res.data ?? {}) as { workout?: any };

      // Map the response to the expected format
      const mappedItems = (savedWorkout?.workout_exercises ?? [])
        .sort((a: any, b: any) => (a?.order_index ?? 0) - (b?.order_index ?? 0))
        .map((we: any) => ({
          id: we.id,
          workoutId: we.workout_id,
          exerciseId: we.exercise_id,
          orderIndex: we.order_index,
          groupId: we.group_id ?? null,
          groupType: we.group_type ?? null,
          withinGroupOrder: we.within_group_order ?? null,
          notes: we.notes ?? null,
          showInAnalytics: !!we.show_in_analytics,
          trackBy: we.track_by ?? null,
          sets: we.sets ?? null,
          reps: we.reps ?? null,
          durationSec: we.duration_sec ?? null,
          distance: we.distance ?? null,
          distanceUnit: we.distance_unit ?? null,
          rounds: we.rounds ?? null,
          workSec: we.work_sec ?? null,
          restSec: we.rest_sec ?? null,
          intervalType: we.interval_type ?? null,
          tempo: we.tempo ?? null,
          restBetweenSetsSec: we.rest_between_sets_sec ?? null,
          effort: we.effort ?? null,
          intensity: we.intensity ?? null,
          targetPaceSec: we.target_pace_sec ?? null,
          loadKg: we.load_kg ?? null,
          loadPct1rm: we.load_pct_1rm ?? null,
          inclinePct: we.incline_pct ?? null,
          speedKph: we.speed_kph ?? null,
          cadenceRpm: we.cadence_rpm ?? null,
          unilateralSide: we.unilateral_side ?? null,
          executionNotes: we.execution_notes ?? null,
          alternatives: we.alternatives ?? null,
          targetMuscles: we.target_muscles ?? null,
          setPatterns: we.set_patterns ?? null,
          isOptional: we.is_optional ?? null,
          isAdvanced: we.is_advanced ?? null,
          exercise: we.exercises
            ? {
                id: we.exercises.id,
                slug: we.exercises.slug ?? null,
                name: we.exercises.name,
                modality: we.exercises.modality ?? null,
                primaryMuscles: we.exercises.primary_muscles ?? null,
                equipment: we.exercises.equipment ?? null,
                videoUrl: we.exercises.video_url ?? null,
              }
            : null,
        }));

      const result = {
        id: savedWorkout.id,
        ownerCoachId: savedWorkout.owner_coach_id,
        slug: savedWorkout.slug ?? null,
        name: savedWorkout.name,
        modality: savedWorkout.modality ?? null,
        difficulty: savedWorkout.difficulty ?? null,
        estimatedDurationMin: savedWorkout.estimated_duration_min ?? null,
        notes: savedWorkout.notes ?? null,
        tags: savedWorkout.tags ?? null,
        createdAt: savedWorkout.created_at,
        updatedAt: savedWorkout.updated_at ?? null,
        itemsCount: mappedItems.length,
        items: mappedItems,
      };

      // Dispatch event to refresh workouts list
      try {
        window.dispatchEvent(new CustomEvent('workouts:changed'));
      } catch {
        /* ignored */
      }

      return result;
    } catch (e) {
      try {
        console.error('[workouts] saveCoachWorkout failed', e);
      } catch {
        /* ignored */
      }
      throw e as any;
    }
  }, []);

  const deleteCoachWorkout = React.useCallback(
    async (id: string, opts?: { hard?: boolean }) => {
      const { optimisticMutation } = await import('../utils/optimisticMutation');

      // Store workout for rollback
      const workoutToDelete = coachWorkouts.find((w) => w.id === id);
      if (!workoutToDelete) {
        throw new Error('Workout not found');
      }

      return await optimisticMutation({
        mutationFn: async () => {
          // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
          const res = await workoutsApi.deleteWorkout(id, opts);

          if (!res.ok) {
            throw new Error(bodyError(res) || withStatus(res, (status) => `Failed to delete workout (${status})`));
          }

          return true;
        },
        onOptimistic: () => {
          // Add to pending
          setPendingWorkoutDeletes((prev) => new Set([...prev, id]));
          // Remove from list immediately
          setCoachWorkouts((prev) => prev.filter((w) => w.id !== id));
        },
        onRollback: () => {
          // Remove from pending
          setPendingWorkoutDeletes((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          // Restore workout to list
          setCoachWorkouts((prev) => {
            // Find insertion point (maintain sort order by updated_at desc)
            const insertIndex = prev.findIndex(
              (w) =>
                new Date(w.updated_at || w.updatedAt || 0) <
                new Date(workoutToDelete.updated_at || workoutToDelete.updatedAt || 0),
            );
            if (insertIndex === -1) {
              return [...prev, workoutToDelete];
            }
            return [...prev.slice(0, insertIndex), workoutToDelete, ...prev.slice(insertIndex)];
          });
        },
        onSuccess: () => {
          // Remove from pending
          setPendingWorkoutDeletes((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          // Trigger refresh event
          try {
            window.dispatchEvent(new CustomEvent('workouts:changed'));
          } catch {
            /* ignored */
          }
        },
        successMessage: 'Workout deleted',
      });
    },
    [coachWorkouts],
  );

  const duplicateCoachWorkout = React.useCallback(
    async (id: string) => {
      const {
        optimisticMutation,
        generateClientMutationId,
        createOptimisticItem,
        reconcileOptimisticItem,
        removeOptimisticItem,
      } = await import('../utils/optimisticMutation');

      // Find source workout in state
      const srcInState = coachWorkouts.find((w) => w.id === id);
      if (!srcInState) {
        throw new Error('Source workout not found');
      }

      // Generate client mutation ID
      const clientMutationId = generateClientMutationId();

      // Find next available copy number
      const baseName = srcInState.name;
      const existingCopies = coachWorkouts
        .map((w) => w.name)
        .filter(
          (name) =>
            name === baseName ||
            name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\(Copy \\d+\\)$`)),
        )
        .map((name) => {
          const match = name.match(/\(Copy (\d+)\)$/);
          return match ? parseInt(match[1], 10) : 0;
        });
      const nextNumber = existingCopies.length > 0 ? Math.max(...existingCopies) + 1 : 1;
      const uniqueName = `${baseName} (Copy ${nextNumber})`;

      // Create optimistic copy
      const optimisticCopy = createOptimisticItem(
        {
          ...srcInState,
          name: uniqueName,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        clientMutationId,
      );

      return await optimisticMutation({
        mutationFn: async () => {
          // Fetch full workout with items
          const src = await getWorkout(String(id));
          if (!src) throw new Error('Source workout not found');

          // Find next available copy number (server-side check)
          const baseName = src.name;
          const existingCopies = coachWorkouts
            .map((w) => w.name)
            .filter(
              (name) =>
                name === baseName ||
                name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\(Copy \\d+\\)$`)),
            )
            .map((name) => {
              const match = name.match(/\(Copy (\d+)\)$/);
              return match ? parseInt(match[1], 10) : 0;
            });
          const nextNumber = existingCopies.length > 0 ? Math.max(...existingCopies) + 1 : 1;
          const uniqueName = `${baseName} (Copy ${nextNumber})`;

          const clonedMeta: any = {
            name: uniqueName,
            modality: src.modality ?? null,
            difficulty: src.difficulty ?? null,
            notes: src.notes ?? null,
            tags: Array.isArray(src.tags) ? src.tags : [],
            client_mutation_id: clientMutationId, // For reconciliation
          };

          const clonedItems = (src.items || []).map((it: any, idx: number) => ({
            exerciseId: it.exerciseId || (it.exercise?.id ?? null),
            orderIndex: typeof it.orderIndex === 'number' ? it.orderIndex : idx + 1,
            trackBy: it.trackBy ?? 'reps',
            sets: it.sets ?? null,
            reps: it.reps ?? null,
            durationSec: it.durationSec ?? null,
            distance: it.distance ?? null,
            distanceUnit: it.distanceUnit ?? null,
            rounds: it.rounds ?? null,
            workSec: it.workSec ?? null,
            restSec: it.restSec ?? null,
            tempo: it.tempo ?? null,
            restBetweenSetsSec: it.restBetweenSetsSec ?? null,
            effort: it.effort ?? null,
            intensity: it.intensity ?? null,
            targetPaceSec: it.targetPaceSec ?? null,
            loadKg: it.loadKg ?? null,
            loadPct1rm: it.loadPct1rm ?? null,
            inclinePct: it.inclinePct ?? null,
            speedKph: it.speedKph ?? null,
            cadenceRpm: it.cadenceRpm ?? null,
            groupId: it.groupId ?? null,
            groupType: it.groupType ?? null,
            withinGroupOrder: it.withinGroupOrder ?? null,
            notes: it.notes ?? null,
          }));

          return await saveCoachWorkout(clonedMeta, clonedItems);
        },
        onOptimistic: () => {
          // Add to pending
          setPendingWorkoutDuplicates((prev) => new Set([...prev, id]));
          // Add optimistic copy to top of list
          setCoachWorkouts((prev) => [optimisticCopy, ...prev]);
        },
        onRollback: () => {
          // Remove from pending
          setPendingWorkoutDuplicates((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          // Remove optimistic copy
          setCoachWorkouts((prev) => removeOptimisticItem(prev, clientMutationId));
        },
        onSuccess: (realWorkout) => {
          // Remove from pending
          setPendingWorkoutDuplicates((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          // Replace optimistic copy with real data
          if (realWorkout) {
            setCoachWorkouts((prev) => reconcileOptimisticItem(prev, clientMutationId, realWorkout));
          }
          // Trigger refresh event
          try {
            window.dispatchEvent(new CustomEvent('workouts:changed'));
          } catch {
            /* ignored */
          }
        },
        successMessage: 'Workout duplicated',
      });
    },
    [coachWorkouts, getWorkout, saveCoachWorkout],
  );

  // Workout/Program counts (Supabase-backed)
  const countCoachWorkouts = React.useCallback(async (): Promise<number> => {
    const sb = (await import('@/lib/supabase')).getSupabase();
    if (!sb) return 0;
    const uid = (await sb.auth.getUser())?.data?.user?.id || currentCoach?.id;
    if (!uid) return 0;
    const { count, error } = await sb
      .from('workouts')
      .select('id', { count: 'exact', head: true })
      .eq('owner_coach_id', uid)
      .is('deleted_at', null);
    if (error) {
      try {
        console.warn('[workouts] countCoachWorkouts failed', error);
      } catch {
        /* ignored */
      }
      return 0;
    }
    return count || 0;
  }, [currentCoach?.id]);

  const countWorkoutsCreatedThisWeek = React.useCallback(async (): Promise<number> => {
    const sb = (await import('@/lib/supabase')).getSupabase();
    if (!sb) return 0;
    const uid = (await sb.auth.getUser())?.data?.user?.id || currentCoach?.id;
    if (!uid) return 0;
    const { count, error } = await sb
      .from('workouts')
      .select('id', { count: 'exact', head: true })
      .eq('owner_coach_id', uid)
      .is('deleted_at', null)
      .gte('created_at', new Date(new Date().setHours(0, 0, 0, 0) - new Date().getDay() * 86400000).toISOString());
    if (error) {
      try {
        console.warn('[workouts] countWorkoutsCreatedThisWeek failed', error);
      } catch {
        /* ignored */
      }
      return 0;
    }
    return count || 0;
  }, [currentCoach?.id]);

  const countCoachPrograms = React.useCallback(async (): Promise<number> => {
    const sb = (await import('@/lib/supabase')).getSupabase();
    if (!sb) return 0;
    const uid = (await sb.auth.getUser())?.data?.user?.id || currentCoach?.id;
    if (!uid) return 0;
    const { count, error } = await sb
      .from('programs')
      .select('id', { count: 'exact', head: true })
      .eq('owner_coach_id', uid)
      .is('deleted_at', null);
    if (error) {
      try {
        console.warn('[programs] countCoachPrograms failed', error);
      } catch {
        /* ignored */
      }
      return 0;
    }
    return count || 0;
  }, [currentCoach?.id]);

  // Combined counts via RPC
  const getLibraryCounts = React.useCallback(async () => {
    try {
      const mod = await import('@/data/storage/library/adapter');
      const res = await (mod as any).getLibraryCounts();
      return res;
    } catch (e) {
      try {
        console.warn('[library] getLibraryCounts failed', e);
      } catch {
        /* ignored */
      }
      return { programs_count: 0, workouts_count: 0, workouts_created_this_week: 0 };
    }
  }, []);

  // ----- Programs (Supabase-backed) -----
  const listCoachPrograms = React.useCallback(
    async (search?: string) => {
      try {
        let ownerId: string | undefined;
        try {
          const sbMod = await import('@/lib/supabase');
          const sb = (sbMod as any).getSupabase?.();
          const user = await sb?.auth.getUser();
          ownerId = user?.data?.user?.id || ownerId;
        } catch {
          /* ignored */
        }
        ownerId = ownerId || String(currentCoach?.id || 'coach_demo');
        const mod = await import('@/data/storage/programs/adapter');
        return await (mod as any).listProgramsForCoach(ownerId, { search, includePublic: true, limit: 50 });
      } catch (e) {
        try {
          console.warn('[programs] listCoachPrograms failed', e);
        } catch {
          /* ignored */
        }
        return [];
      }
    },
    [currentCoach?.id],
  );

  const getProgram = React.useCallback(async (id: string) => {
    try {
      const mod = await import('@/data/storage/programs/adapter');
      return await (mod as any).getProgramById(String(id));
    } catch (e) {
      try {
        console.warn('[programs] getProgram failed', e);
      } catch {
        /* ignored */
      }
      return null;
    }
  }, []);

  const saveCoachProgram = React.useCallback(async (program: any, weeks?: any[], days?: any[]) => {
    try {
      const adapter = await import('@/data/storage/programs/adapter');
      // Normalize to snake_case arrays as needed is handled inside RPC wrapper; just pass through
      const id = await (adapter as any).saveProgramWithStructure(program || {}, weeks || [], days || []);
      const hydrated = await (adapter as any).getProgramById(String(id));
      try {
        window.dispatchEvent(new CustomEvent('programs:changed'));
      } catch {
        /* ignored */
      }
      return hydrated;
    } catch (e) {
      try {
        console.error('[programs] saveCoachProgram failed', e);
      } catch {
        /* ignored */
      }
      throw e as any;
    }
  }, []);

  const duplicateCoachProgram = React.useCallback(
    async (id: string) => {
      const {
        optimisticMutation,
        generateClientMutationId,
        createOptimisticItem,
        reconcileOptimisticItem,
        removeOptimisticItem,
      } = await import('../utils/optimisticMutation');

      // Find source program in state
      const srcInState = coachPrograms.find((p) => p.id === id);
      if (!srcInState) {
        throw new Error('Source program not found');
      }

      // Generate client mutation ID
      const clientMutationId = generateClientMutationId();

      // Find next available copy number
      const baseName = srcInState.name;
      const existingCopies = coachPrograms
        .map((p) => p.name)
        .filter(
          (name) =>
            name === baseName ||
            name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\(Copy \\d+\\)$`)),
        )
        .map((name) => {
          const match = name.match(/\(Copy (\d+)\)$/);
          return match ? parseInt(match[1], 10) : 0;
        });
      const nextNumber = existingCopies.length > 0 ? Math.max(...existingCopies) + 1 : 1;
      const uniqueName = `${baseName} (Copy ${nextNumber})`;

      // Create optimistic copy
      const optimisticCopy = createOptimisticItem(
        {
          ...srcInState,
          name: uniqueName,
          is_public: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        clientMutationId,
      );

      return await optimisticMutation({
        mutationFn: async () => {
          // Fetch full program with all nested data
          const src = await getProgram(String(id));
          if (!src) throw new Error('Source program not found');

          // Find next available copy number (server-side check)
          const baseName = src.name;
          const existingCopies = coachPrograms
            .map((p) => p.name)
            .filter(
              (name) =>
                name === baseName ||
                name.match(new RegExp(`^${baseName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\(Copy \\d+\\)$`)),
            )
            .map((name) => {
              const match = name.match(/\(Copy (\d+)\)$/);
              return match ? parseInt(match[1], 10) : 0;
            });
          const nextNumber = existingCopies.length > 0 ? Math.max(...existingCopies) + 1 : 1;
          const uniqueName = `${baseName} (Copy ${nextNumber})`;

          const clonedProgram: any = {
            name: uniqueName,
            slug: undefined,
            total_weeks: src.total_weeks,
            level: src.level,
            goal: src.goal ?? null,
            phase: src.phase ?? null,
            equipment_required: Array.isArray(src.equipment_required) ? src.equipment_required : [],
            tags: Array.isArray(src.tags) ? src.tags : [],
            notes: src.notes ?? null,
            pattern: src.pattern ?? null,
            is_public: false,
            client_mutation_id: clientMutationId, // For reconciliation
          };

          const clonedWeeks = (src.weeks || []).map((w: any) => ({
            week_number: Number(w.week_number),
            is_deload: !!w.is_deload,
            focus: w.focus ?? null,
            notes: w.notes ?? null,
          }));

          const clonedDays = (src.days || []).map((d: any) => ({
            week_number: Number(d.week_number),
            weekday: Number(d.weekday),
            day_type: d.day_type ?? null,
            title: d.title ?? null,
            duration_min: d.duration_min ?? null,
            meals: d.meals ?? null,
            workout_id: d.workout?.id || d.workout_id || null,
            exercise_snapshot: d.exercise_snapshot ?? null,
          }));

          const res = await saveCoachProgram(clonedProgram, clonedWeeks, clonedDays);
          return res;
        },
        onOptimistic: () => {
          // Add to pending
          setPendingProgramDuplicates((prev) => new Set([...prev, id]));
          // Add optimistic copy to top of list
          setCoachPrograms((prev) => [optimisticCopy, ...prev]);
        },
        onRollback: () => {
          // Remove from pending
          setPendingProgramDuplicates((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          // Remove optimistic copy
          setCoachPrograms((prev) => removeOptimisticItem(prev, clientMutationId));
        },
        onSuccess: (realProgram) => {
          // Remove from pending
          setPendingProgramDuplicates((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          // Replace optimistic copy with real data
          if (realProgram) {
            setCoachPrograms((prev) => reconcileOptimisticItem(prev, clientMutationId, realProgram));
          }
          // Trigger refresh event
          try {
            window.dispatchEvent(new CustomEvent('programs:changed'));
          } catch {
            /* ignored */
          }
        },
        successMessage: 'Program duplicated',
      });
    },
    [coachPrograms, getProgram, saveCoachProgram],
  );

  const deleteCoachProgram = React.useCallback(
    async (id: string, opts?: { hard?: boolean }) => {
      const {
        optimisticMutation,
        classifyError: _classifyError,
        mergeById: _mergeById,
      } = await import('../utils/optimisticMutation');

      // Store program for rollback
      const programToDelete = coachPrograms.find((p) => p.id === id);
      if (!programToDelete) {
        throw new Error('Program not found');
      }

      return await optimisticMutation({
        mutationFn: async () => {
          const sb = (await import('@/lib/supabase')).getSupabase?.();
          if (!sb) throw new Error('Supabase not configured');

          if (opts?.hard) {
            const { error } = await sb.from('programs').delete().eq('id', String(id));
            if (error) throw error;
          } else {
            const { error } = await sb
              .from('programs')
              .update({ deleted_at: new Date().toISOString() })
              .eq('id', String(id));
            if (error) throw error;
          }

          // Invalidate programs cache
          const { dataCache } = await import('@/utils/cache');
          dataCache.invalidate('programs:');

          return true;
        },
        onOptimistic: () => {
          // Add to pending
          setPendingProgramDeletes((prev) => new Set([...prev, id]));
          // Remove from list immediately
          setCoachPrograms((prev) => prev.filter((p) => p.id !== id));
        },
        onRollback: () => {
          // Remove from pending
          setPendingProgramDeletes((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          // Restore program to list
          setCoachPrograms((prev) => {
            // Find insertion point (maintain sort order by created_at desc)
            const insertIndex = prev.findIndex(
              (p) => new Date(p.created_at || 0) < new Date(programToDelete.created_at || 0),
            );
            if (insertIndex === -1) {
              return [...prev, programToDelete];
            }
            return [...prev.slice(0, insertIndex), programToDelete, ...prev.slice(insertIndex)];
          });
        },
        onSuccess: () => {
          // Remove from pending
          setPendingProgramDeletes((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          // Trigger refresh event
          try {
            window.dispatchEvent(new CustomEvent('programs:changed'));
          } catch {
            /* ignored */
          }
        },
        successMessage: 'Program deleted',
      });
    },
    [coachPrograms],
  );

  // ----- Client Programs (Supabase-backed) -----

  // Cache for client assignments to speed up profile loading (must be declared before functions that use it)
  // Freshness (incl. failure backoff) lives in assignmentsCachePolicy.
  const assignmentsCache = React.useRef<Map<string, AssignmentsCacheEntry<ClientProgramAssignment[]>>>(new Map());

  const clearAssignmentsCache = React.useCallback((clientId?: string) => {
    if (clientId) {
      assignmentsCache.current.delete(clientId);
    } else {
      assignmentsCache.current.clear();
    }
  }, []);

  const assignProgramToClient = React.useCallback(
    async (programId: string, clientId: string, startDate: string, timezone = 'UTC', replaceExisting = false) => {
      try {
        // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
        const res = await programAssignmentsApi.assignProgram({
          programId,
          clientId,
          startDate,
          timezone,
          replaceExisting,
        });

        if (!res.ok) {
          console.error('Failed to assign program from Worker:', res.status, res.body ?? res.error);
          throw new Error(bodyError(res) || 'Failed to assign program');
        }

        const { assignmentId } = res;
        // Clear caches so client sees fresh data
        clearProgramCache();
        clearAssignmentsCache(clientId);

        // Dispatch assignments:changed event for metric refreshes
        try {
          window.dispatchEvent(new CustomEvent('assignments:changed'));
        } catch {
          /* ignored */
        }

        return assignmentId as string;
      } catch (e) {
        console.error('[client-programs] assignProgramToClient failed', e);
        throw e as any;
      }
    },
    [clearAssignmentsCache],
  );

  const listClientPrograms = React.useCallback(async () => {
    try {
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (!sb) throw new Error('Supabase not configured');
      const {
        data: { user },
      } = await sb.auth.getUser();
      if (!user?.id) throw new Error('Not authenticated');
      const { data, error } = await sb
        .from('client_programs')
        .select('id, client_id, program_id, start_date, timezone, status, created_at, updated_at')
        .eq('owner_coach_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (e) {
      try {
        console.error('[client-programs] listClientPrograms failed', e);
      } catch {
        /* ignored */
      }
      return [];
    }
  }, []);

  const getClientAssignments = React.useCallback(async (clientId: string): Promise<ClientProgramAssignment[]> => {
    try {
      // Check cache first for instant loading
      const cached = assignmentsCache.current.get(clientId);
      const now = Date.now();
      if (cached && isAssignmentsCacheFresh(cached, now)) {
        // Fresh success, or a recent failure still backing off (see assignmentsCachePolicy)
        return cached.data;
      }

      // TODO(perf): caller-side N+1 — when invoked once per client in a list
      // it fans out N requests. Sentry COACHED-REACT-UI-13 tracks this; right
      // fix is a server-side bulk endpoint
      //   GET /api/clients/program-assignments?clientIds=a,b,c
      // plus a batched caller. Single-call cache below (assignmentsCache)
      // helps repeat lookups but doesn't address the first-load fanout.
      const assignmentsRes = await fetch(`/api/clients/program-assignments?clientId=${clientId}`, {
        credentials: 'include',
      });

      if (!assignmentsRes.ok) {
        console.error('[client-programs] Worker endpoint failed:', assignmentsRes.status);
        const entry = failedAssignmentsEntry(cached, [], now);
        assignmentsCache.current.set(clientId, entry);
        return entry.data;
      }

      const { items: data } = await assignmentsRes.json();
      if (!data) return cached?.data || [];

      const mapped = (data || []).map((r: any) => {
        // Compute isCustomized based on whether any fields differ from template OR has day-level snapshots
        const hasDisplayNameOverride = r.display_name && r.display_name !== r.programs?.name;
        const hasDescriptionOverride = r.description && r.description !== r.programs?.notes;
        const hasDifficultyOverride = r.difficulty && r.difficulty !== r.programs?.level;
        const hasTagsOverride = r.tags && r.tags.length > 0;
        const hasWeeksOverride = r.total_weeks_override != null;
        const hasSnapshotCustomizations = r.has_snapshot_customizations === true;

        const isCustomized =
          hasDisplayNameOverride ||
          hasDescriptionOverride ||
          hasDifficultyOverride ||
          hasTagsOverride ||
          hasWeeksOverride ||
          hasSnapshotCustomizations;

        return {
          id: r.id,
          programId: r.program_id,
          startDate: r.start_date,
          timezone: r.timezone,
          status: r.status ?? null,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
          // Client-specific overrides
          displayName: r.display_name,
          description: r.description,
          difficulty: r.difficulty,
          tags: r.tags || [],
          totalWeeksOverride: r.total_weeks_override,
          // Effective total weeks (override or template)
          effectiveTotalWeeks: r.total_weeks_override ?? r.programs?.total_weeks ?? 1,
          isCustomized,
          // Workout structure with actual days
          weeks: r.weeks || [],
          program: r.programs
            ? {
                id: r.programs.id,
                name: r.programs.name,
                level: r.programs.level ?? null,
                totalWeeks: r.programs.total_weeks ?? null,
                goal: r.programs.goal ?? null,
                notes: r.programs.notes ?? null,
                tags: r.programs.tags ?? [],
              }
            : null,
        };
      });

      // Update cache
      assignmentsCache.current.set(clientId, { data: mapped, timestamp: now });

      return mapped;
    } catch (e) {
      console.error('[client-programs] getClientAssignments failed', e);
      const entry = failedAssignmentsEntry(assignmentsCache.current.get(clientId), [], Date.now());
      assignmentsCache.current.set(clientId, entry);
      return entry.data;
    }
  }, []);

  // Warm the assignments cache for all clients (pre-load for faster navigation)
  const warmClientAssignmentsCache = React.useCallback(async () => {
    try {
      const clientList = clientsRef.current || [];

      if (clientList.length === 0) {
        return;
      }

      // Load assignments for all clients in parallel (with individual error handling)
      const _results = await Promise.allSettled(
        clientList.map(async (client) => {
          try {
            const assignments = await getClientAssignments(client.id);
            return { clientId: client.id, success: true, count: assignments.length };
          } catch (error) {
            return { clientId: client.id, success: false, error };
          }
        }),
      );
    } catch (error) {
      console.error('[cache-warming] Cache warming failed:', error);
      // Don't throw - this is an optimization, not critical
    }
  }, [getClientAssignments]);

  // Load last-seen data for all clients
  const loadLastSeenData = React.useCallback(async () => {
    try {
      const clientList = clientsRef.current || [];
      const clientIds = clientList.filter((c: any) => !c.isPendingInvite).map((c: any) => c.id);

      if (clientIds.length === 0) {
        return;
      }

      const response = await fetch(`/api/clients/last-seen?clientIds=${clientIds.join(',')}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const json = await response.json();
        setLastSeenData(json.data?.lastSeen || json.lastSeen || {});
      } else {
        console.warn('[last-seen] Failed to load last-seen data:', response.status);
      }
    } catch (error) {
      console.error('[last-seen] Failed to load last-seen data:', error);
      // Don't throw - this is an optimization, not critical
    }
  }, []);

  // Load clients function (used by multiple effects)
  const loadClients = React.useCallback(async () => {
    if (isMockMode()) {
      try {
        const mocks = await listClientsData();
        clientsRef.current = mocks as any;
        setClients(mocks as any);

        // Seed dashboard mocks after clients are loaded (only if not already seeded)
        const savedPrograms = localStorage.getItem('coachd:programs');
        const savedClientProgramsV2 = localStorage.getItem('coachd:clientProgramsV2');
        const savedClientEvents = localStorage.getItem('coachd:clientEvents');

        if (!savedPrograms && !savedClientProgramsV2 && !savedClientEvents) {
          const firstClientId = mocks && mocks.length > 0 ? mocks[0].id : 'demo-client';
          const dashboardMocks = buildClientDashboardMocks(firstClientId);
          setPrograms(dashboardMocks.programs);
          setClientProgramsV2(dashboardMocks.clientPrograms);
          setClientEvents(dashboardMocks.clientEvents);
        }
      } catch (e) {
        console.error('[clients] mock load failed', e);
        clientsRef.current = [];
        setClients([]);
      }
    } else {
      // Load real clients from Supabase
      try {
        const realClients = await listClients();
        clientsRef.current = realClients;
        setClients(realClients);
      } catch (e) {
        console.error('[clients] real load failed', e);
        clientsRef.current = [];
        setClients([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const archiveClientProgram = React.useCallback(
    async (assignmentId: string) => {
      try {
        // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
        const res = await programAssignmentsApi.pauseAssignment(assignmentId);

        if (!res.ok) {
          console.error('Failed to archive program assignment from Worker:', res.status, res.body ?? res.error);
          throw new Error(bodyError(res) || 'Failed to archive assignment');
        }

        // Clear assignments cache since status changed
        clearAssignmentsCache();
        return true;
      } catch (e) {
        console.error('[client-programs] archive failed', e);
        return false;
      }
    },
    [clearAssignmentsCache],
  );

  const getClientProgramDays = React.useCallback(async (clientProgramId: string) => {
    try {
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (!sb) throw new Error('Supabase not configured');
      const { data, error } = await sb
        .from('client_program_days')
        .select(
          `
          id, date, week_number, weekday, title, workout_id, exercise_snapshot, snapshot_meta,
          workouts:workout_id (
              id, name, modality, estimated_duration_min,
            workout_exercises (
              id, order_index, track_by, sets, reps, 
              duration_sec, distance, distance_unit, target_pace_sec,
              rest_sec, rounds, work_sec, interval_type,
              tempo, rest_between_sets_sec, effort, intensity,
              load_kg, load_pct_1rm,
              notes, execution_notes,
              group_id, group_type, within_group_order,
              custom_name,
              alternatives, target_muscles, set_patterns, block_metadata,
              is_optional, is_advanced, show_in_analytics,
              exercise:exercise_id ( id, name )
            )
          )
        `,
        )
        .eq('client_program_id', clientProgramId)
        .order('week_number', { ascending: true })
        .order('weekday', { ascending: true });
      if (error) throw error;
      return data || [];
    } catch (e) {
      try {
        console.error('[client-programs] getClientProgramDays failed', e);
      } catch {
        /* ignored */
      }
      return [];
    }
  }, []);

  const saveClientDaySnapshot = React.useCallback(
    async (
      dayId: string,
      items: any[],
      meta?: { name?: string | null; durationMin?: number | null; modality?: string | null; notes?: string | null },
    ) => {
      try {
        const { isUuid } = await import('@/lib/validation');
        const { sanitizeRawItems, sanitizeSnapshotMeta, denormalizeNames, buildDayPatch } =
          await import('@/lib/clientPrograms/snapshotSanitizer');
        const sb = (await import('@/lib/supabase')).getSupabase();
        if (!sb) throw new Error('Supabase not configured');

        if (!isUuid(dayId)) throw new Error('Invalid day ID');
        await ensureAssignmentActive(dayId);

        // A workout day can legitimately have zero exercises (name/metadata only — e.g. a day the
        // coach named but hasn't filled in, or a linked-workout day still loading its exercises).
        // The normal path runs sanitizeRawItems([]), which THROWS ("No exercises to save") and fails
        // the ENTIRE save (title/meta included), so the optimistic edit reverts and autosave loops.
        // For an empty list, persist ONLY the metadata: deliberately OMIT exercise_snapshot (never
        // clobber a not-yet-loaded day's real exercises with []) AND workout_id (never sever a linked
        // workout). buildDayPatch can't do meta-only (it hardcodes both), so build the patch inline.
        // The two apply-endpoint callers still rely on sanitizeRawItems throwing on empty, so we
        // branch BEFORE it rather than changing the shared helper.
        const hasItems = Array.isArray(items) && items.length > 0;
        const snapshotMeta = sanitizeSnapshotMeta(meta);
        let patch: Record<string, unknown>;
        if (!hasItems) {
          patch = { updated_at: new Date().toISOString() };
          if (snapshotMeta.name !== undefined) patch.title = snapshotMeta.name;
          if (Object.keys(snapshotMeta).length > 0) patch.snapshot_meta = snapshotMeta;
        } else {
          const cleaned = sanitizeRawItems(items);
          // Fallback names for items that arrive without one. (The old version asked the storage
          // adapter for functions it never exported, so this always resolved nothing.)
          const resolver = async (_ids: string[]) => {
            await useExerciseLibraryStore.getState().load();
            return new Map<string, string>(
              selectExercises(useExerciseLibraryStore.getState()).map((e) => [e.id, e.name]),
            );
          };
          const snapshotWithNames = await denormalizeNames(cleaned, items, resolver);
          patch = buildDayPatch(snapshotWithNames, snapshotMeta);
        }

        await commitOrThrow(sb.from('client_program_days').update(patch).eq('id', dayId));

        // Invalidate cache so other views see fresh data
        clearAssignmentsCache();

        try {
          const { track } = await import('@/lib/analytics');
          track('day_snapshot_saved', { dayId });
        } catch {
          /* ignored */
        }

        return true;
      } catch (e) {
        console.error('[client-programs] saveClientDaySnapshot failed', e);
        throw e;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Helper: ensure write operations succeed or throw with clear errors
  // Normalize known blocked-write messages to friendly errors
  const normalizeBlockedWriteError = (err: any): Error => {
    const msg = String(err?.message ?? err ?? '').toLowerCase();
    if (msg.includes('archived')) return new Error('This assignment is archived — edits are disabled.');
    if (msg.includes('paused')) return new Error('This assignment is paused — edits are disabled.');
    if (msg.includes('denied') || msg.includes('blocked'))
      return new Error('Save blocked (access denied or assignment inactive).');
    return new Error('Save blocked (access denied or assignment inactive).');
  };

  const commitOrThrow = async (q: any) => {
    const { data, error } = await q.select('id'); // forces return representation
    if (error) throw normalizeBlockedWriteError(error);
    if (!data?.length) throw normalizeBlockedWriteError('blocked');
    return data;
  };

  // Preflight: ensure assignment is active (throws friendly error if paused/archived)
  const ensureAssignmentActive = async (dayId: string) => {
    const status = await getAssignmentStatusByDay(dayId);
    if (status !== 'active') {
      throw new Error(
        status === 'paused'
          ? 'This assignment is paused — edits are disabled.'
          : 'This assignment is archived — edits are disabled.',
      );
    }
  };

  // Preflight for create scenarios where we only have client_program_id
  const ensureClientProgramActive = async (clientProgramId: string) => {
    const sb = (await import('@/lib/supabase')).getSupabase();
    if (!sb) throw new Error('Supabase not configured');
    const cp = await sb.from('client_programs').select('status').eq('id', clientProgramId).single();
    const status = cp.data?.status as string | undefined;
    if (status !== 'active') {
      throw new Error(
        status === 'paused'
          ? 'This assignment is paused — edits are disabled.'
          : 'This assignment is archived — edits are disabled.',
      );
    }
  };

  // Helper: get assignment status by day ID
  const getAssignmentStatusByDay = React.useCallback(async (dayId: string) => {
    try {
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (!sb) throw new Error('Supabase not configured');

      const day = await sb.from('client_program_days').select('client_program_id').eq('id', dayId).single();
      if (day.error || !day.data?.client_program_id) throw day.error || new Error('Day not found');

      const cp = await sb.from('client_programs').select('status').eq('id', day.data.client_program_id).single();
      if (cp.error) throw cp.error;

      return cp.data?.status as string | null;
    } catch (e) {
      console.error('[client-programs] getAssignmentStatusByDay failed', e);
      return null;
    }
  }, []);

  const setClientDayWorkout = React.useCallback(async (dayId: string, workoutId: string | null) => {
    try {
      const { isUuid } = await import('@/lib/validation');
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (!sb) throw new Error('Supabase not configured');

      // Input validation
      if (!isUuid(dayId)) throw new Error('Invalid day ID');
      if (workoutId !== null && !isUuid(workoutId)) throw new Error('Invalid workout ID');

      // Preflight
      await ensureAssignmentActive(dayId);

      // If linking a workout, fetch its name to update the title
      let workoutTitle = 'Rest';
      if (workoutId) {
        try {
          const { data: workout } = await sb.from('workouts').select('name').eq('id', workoutId).single();
          if (workout?.name) {
            workoutTitle = workout.name;
          }
        } catch (e) {
          console.warn('[client-programs] Failed to fetch workout name:', e);
        }
      }

      const patch: any = {
        workout_id: workoutId ?? null, // link or unlink
        exercise_snapshot: null, // Clear snapshot when linking workout (mutually exclusive)
        snapshot_meta: null, // Clear snapshot metadata too
        title: workoutTitle, // Update title to workout name or 'Rest'
        updated_at: new Date().toISOString(),
      };

      await commitOrThrow(sb.from('client_program_days').update(patch).eq('id', dayId));

      return true;
    } catch (e) {
      console.error('[client-programs] setClientDayWorkout failed', e);
      throw e; // Re-throw for UI error handling
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearClientDaySnapshot = React.useCallback(async (dayId: string) => {
    try {
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (!sb) throw new Error('Supabase not configured');
      await ensureAssignmentActive(dayId);
      await commitOrThrow(
        sb
          .from('client_program_days')
          .update({
            exercise_snapshot: null,
            workout_id: null,
            snapshot_meta: null,
            title: 'Rest',
            updated_at: new Date().toISOString(),
          })
          .eq('id', dayId),
      );
      return true;
    } catch (e) {
      try {
        console.error('[client-programs] clearClientDaySnapshot failed', e);
      } catch {
        /* ignored */
      }
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateClientProgramMeta = React.useCallback(
    async (
      assignmentId: string,
      patch: {
        displayName?: string | null;
        description?: string | null;
        difficulty?: string | null;
        tags?: string[] | null;
        totalWeeksOverride?: number | null;
      },
    ) => {
      try {
        const sb = (await import('@/lib/supabase')).getSupabase();
        if (!sb) throw new Error('Supabase not configured');
        const {
          data: { user },
        } = await sb.auth.getUser();
        if (!user?.id) throw new Error('Not authenticated');

        // Build update object with only provided fields
        const updateData: any = { updated_at: new Date().toISOString() };
        if ('displayName' in patch) updateData.display_name = patch.displayName;
        if ('description' in patch) updateData.description = patch.description;
        if ('difficulty' in patch) updateData.difficulty = patch.difficulty;
        if ('tags' in patch) updateData.tags = patch.tags;
        if ('totalWeeksOverride' in patch) updateData.total_weeks_override = patch.totalWeeksOverride;

        const { data, error } = await sb
          .from('client_programs')
          .update(updateData)
          .eq('id', assignmentId)
          .eq('owner_coach_id', user.id)
          .select('id, display_name, difficulty, tags, description, total_weeks_override')
          .single();

        if (error) throw error;
        clearAssignmentsCache();
        return data;
      } catch (e) {
        console.error('[client-programs] updateClientProgramMeta failed', e);
        return false;
      }
    },
    [clearAssignmentsCache],
  );

  const createClientProgramDay = React.useCallback(
    async (
      clientProgramId: string,
      { weekNumber, weekday, title }: { weekNumber: number; weekday: number; title?: string },
    ) => {
      try {
        const { isUuid, clampInt, sanitizeText } = await import('@/lib/validation');
        const sb = (await import('@/lib/supabase')).getSupabase();
        if (!sb) throw new Error('Supabase not configured');

        // Input validation
        if (!isUuid(clientProgramId)) throw new Error('Invalid assignment ID');
        const wn = clampInt(weekNumber, 1, 104); // generous upper bound
        const wd = clampInt(weekday, 0, 6); // 0=Sunday, 6=Saturday
        if (!wn || wd === null || wd === undefined) throw new Error('Invalid week/weekday');
        const safeTitle = sanitizeText(title, 120);

        await ensureClientProgramActive(clientProgramId);

        // 1) If it already exists, return it
        const existing = await sb
          .from('client_program_days')
          .select('id')
          .eq('client_program_id', clientProgramId)
          .eq('week_number', wn)
          .eq('weekday', wd)
          .maybeSingle();

        if (existing.data?.id) return { id: existing.data.id };

        // 2) Load assignment to compute date
        const cpData = await sb
          .from('client_programs')
          .select('start_date, timezone')
          .eq('id', clientProgramId)
          .single();

        if (cpData.error) throw cpData.error;
        const startDate = cpData.data?.start_date; // YYYY-MM-DD

        // Anchor to the Sunday of the start-date week (matches SQL RPC assign_program_to_client)
        const base = new Date(`${startDate}T00:00:00Z`);
        const startDow = base.getUTCDay(); // 0=Sun..6=Sat
        const sundayAnchor = new Date(base.getTime() - startDow * 86400000);
        const daysOffset = (wn - 1) * 7 + wd;
        const date = new Date(sundayAnchor.getTime() + daysOffset * 86400000).toISOString().slice(0, 10);

        // 3) Insert
        const ins = await sb
          .from('client_program_days')
          .insert({
            client_program_id: clientProgramId,
            week_number: wn,
            weekday: wd,
            date,
            title: safeTitle,
            workout_id: null,
            exercise_snapshot: null,
          })
          .select('id')
          .single();

        if (ins.error) {
          console.error('[createClientProgramDay] supabase error', ins.error);
          throw new Error(ins.error.message || 'Failed to create day');
        }
        clearAssignmentsCache();
        return { id: ins.data.id };
      } catch (e) {
        console.error('[client-programs] createClientProgramDay failed', e);
        throw e;
      }
    },
    [clearAssignmentsCache],
  );

  // Feature 1 (coach calendar drag-drop): swap the WORKOUT CONTENT between two days, leaving each
  // row's date/week/weekday fixed — same semantics as dragging a day in the program builder
  // (useProgramBuilder.swapDays). Two content-only updates on rows in the same assignment.
  const swapClientDayWorkouts = React.useCallback(
    async (dayIdA: string, dayIdB: string) => {
      try {
        const { isUuid } = await import('@/lib/validation');
        const sb = (await import('@/lib/supabase')).getSupabase();
        if (!sb) throw new Error('Supabase not configured');
        if (!isUuid(dayIdA) || !isUuid(dayIdB)) throw new Error('Invalid day ID');
        if (dayIdA === dayIdB) return true; // dropping onto itself is a no-op

        // Both rows belong to the same assignment; a single active-check suffices.
        await ensureAssignmentActive(dayIdA);

        const { data: rows, error } = await sb
          .from('client_program_days')
          .select('id, exercise_snapshot, snapshot_meta, title, workout_id')
          .in('id', [dayIdA, dayIdB]);
        if (error) throw error;
        const a = rows?.find((r: any) => r.id === dayIdA);
        const b = rows?.find((r: any) => r.id === dayIdB);
        if (!a || !b) throw new Error('Day not found');

        // Only the workout content moves — NOT date/week_number/weekday (those stay with the slot).
        const content = (r: any) => ({
          exercise_snapshot: r.exercise_snapshot ?? null,
          snapshot_meta: r.snapshot_meta ?? null,
          title: r.title ?? null,
          workout_id: r.workout_id ?? null,
          updated_at: new Date().toISOString(),
        });
        const aOriginal = content(a); // A's content, to write into B (and to roll A back to on failure)
        const bOriginal = content(b); // B's content, to write into A

        // Two writes, no DB transaction. If the SECOND fails after the first landed, A would hold B's
        // content while B still holds its own → the dragged workout is duplicated and A's is lost.
        // Best-effort rollback: restore A to its original content so a partial failure is a no-op.
        await commitOrThrow(sb.from('client_program_days').update(bOriginal).eq('id', dayIdA));
        try {
          await commitOrThrow(sb.from('client_program_days').update(aOriginal).eq('id', dayIdB));
        } catch (e) {
          try {
            await sb.from('client_program_days').update(aOriginal).eq('id', dayIdA);
          } catch {
            /* rollback is best-effort; original error is surfaced below */
          }
          throw e;
        }

        clearAssignmentsCache();
        return true;
      } catch (e) {
        console.error('[client-programs] swapClientDayWorkouts failed', e);
        throw e; // surface paused/archived + other errors to the UI
      }
    },
    // ensureAssignmentActive/commitOrThrow are stable component consts (as in the sibling helpers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearAssignmentsCache],
  );

  const clearClientDayWorkout = React.useCallback(async (dayId: string) => {
    try {
      const { isUuid } = await import('@/lib/validation');
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (!sb) throw new Error('Supabase not configured');

      // Input validation
      if (!isUuid(dayId)) throw new Error('Invalid day ID');

      await ensureAssignmentActive(dayId);

      await commitOrThrow(
        sb
          .from('client_program_days')
          .update({
            workout_id: null,
            exercise_snapshot: null,
            snapshot_meta: null,
            title: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', dayId),
      );

      return true;
    } catch (e) {
      console.error('[client-programs] clearClientDayWorkout failed', e);
      throw e; // Re-throw for UI error handling
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listClients = React.useCallback(async () => {
    try {
      setClientsLoadError(null);
      let clientsRes = await csrfFetch('/api/coach/clients', { credentials: 'include' });

      // Retry once on 401/403 — session cookies may not be ready yet (same race as billing)
      if ((clientsRes.status === 401 || clientsRes.status === 403) && !clientsRes.ok) {
        await new Promise((r) => setTimeout(r, 1000));
        clientsRes = await csrfFetch('/api/coach/clients', { credentials: 'include' });
      }

      if (!clientsRes.ok) {
        const msg = `Failed to load clients (${clientsRes.status})`;
        console.error('[clients] Worker endpoint failed:', clientsRes.status);
        setClientsLoadError(msg);
        return [];
      }

      const json = await clientsRes.json();
      const items = json.data?.items || json.items || [];
      const clients = (items || []).map((r: any) => ({
        id: r.id,
        name: r.name || r.email,
        email: r.email,
        status: r.status || 'active',
        assignedPrograms: [],
        assignedNutritionPlans: [],
        coachNotes: [],
        joinDate: r.linkedAt || r.created_at,
        primaryCoachId: r.primaryCoachId,
      }));

      setClientsLoadError(null);
      return clients;
    } catch (e) {
      console.error('[clients] listClients failed', e);
      setClientsLoadError('Failed to load clients. Please check your connection and try again.');
      return [];
    }
  }, []);

  const createClientProfile = React.useCallback(
    async (name: string, email: string, userId?: string, coachId?: string) => {
      try {
        const sb = (await import('@/lib/supabase')).getSupabase();
        if (!sb) throw new Error('Supabase not configured');

        const {
          data: { user },
        } = await sb.auth.getUser();
        if (!user?.id) throw new Error('Not authenticated');

        // Use provided userId or current user's ID
        const targetUserId = userId || user.id;
        const targetCoachId = coachId || user.id;

        // Check if profile already exists
        const { data: existing } = await sb.from('profiles').select('id, role').eq('id', targetUserId).single();

        let profileId = targetUserId;

        if (!existing) {
          // Create profile with explicit ID to match Supabase auth user
          const { data: profile, error: profileError } = await sb
            .from('profiles')
            .insert({
              id: targetUserId, // Explicitly set ID to match Supabase auth user
              email,
              full_name: name,
              role: 'client',
              timezone: 'UTC',
            })
            .select('id')
            .single();

          if (profileError) {
            console.error('Profile creation error:', profileError);
            throw profileError;
          }
          profileId = profile.id;
        } else if (existing.role !== 'client') {
          throw new Error('User exists but is not a client');
        }

        // Check if coach-client relationship already exists
        const { data: existingLink } = await sb
          .from('coach_clients')
          .select('coach_id, client_id')
          .eq('coach_id', targetCoachId)
          .eq('client_id', profileId)
          .single();

        if (!existingLink) {
          // Link to coach (use provided coachId or current user)
          const { error: linkError } = await sb.from('coach_clients').insert({
            coach_id: targetCoachId,
            client_id: profileId,
          });

          if (linkError && linkError.code !== '23505') {
            // Ignore duplicate key errors
            console.error('Coach-client link error:', linkError);
            throw linkError;
          }
        }

        // Reload clients list
        const updatedClients = await listClients();
        setClients(updatedClients);

        return profileId;
      } catch (e) {
        console.error('[clients] createClientProfile failed', e);
        throw e;
      }
    },
    [listClients],
  );

  // Load clients when authentication state changes
  useEffect(() => {
    if (authReady && authSession && userType === 'coach' && !useMocks) {
      listClients()
        .then((freshClients) => {
          setClients((prev) => {
            const notesByClientId = new Map<string, any[]>();
            for (const c of prev) {
              if (c.coachNotes && c.coachNotes.length > 0) {
                notesByClientId.set(c.id, c.coachNotes);
              }
            }
            if (notesByClientId.size === 0) return freshClients;
            return freshClients.map((fc: any) => {
              const notes = notesByClientId.get(fc.id);
              return notes ? { ...fc, coachNotes: notes } : fc;
            });
          });
          setClientsLoaded(true);
        })
        .catch((err) => {
          console.warn('[clients] Failed to load, likely auth not ready:', err);
          setClients([]);
          setClientsLoaded(true);
        });
    } else if (!authSession && !useMocks) {
      // Non-mock mode: clear clients when not authenticated
      setClients([]);
      setClientsLoaded(false);
    }
    // Mock mode clients are handled in the existing loadClients function
  }, [authReady, authSession, userType, useMocks, listClients]);

  const saveIntakeAnswer = React.useCallback(
    async (
      packetId: string,
      section: 'personal' | 'goals' | 'experience' | 'trackers' | 'form_flags',
      key: string,
      value: any,
    ): Promise<void> => {
      await intakeStore.upsertAnswer(String(packetId), section as any, String(key), value);
    },
    [],
  );

  const addIntakeGoal = React.useCallback(
    async (
      packetId: string,
      goal: {
        title: string;
        unit?: string | null;
        targetValue?: number | null;
        priority?: 'low' | 'medium' | 'high';
        deadline?: string | null;
      },
    ): Promise<IntakeGoal> => {
      const mapped: any = {
        title: goal.title,
        unit: goal.unit ?? null,
        targetValue: goal.targetValue ?? null,
        priority: goal.priority ?? 'medium',
        dueOn: goal.deadline ?? null,
      };
      return intakeStore.addGoalDraft(String(packetId), mapped);
    },
    [],
  );

  const removeIntakeGoal = React.useCallback(async (id: string): Promise<void> => {
    await intakeStore.removeGoalDraft(String(id));
  }, []);

  const addIntakeTracker = React.useCallback(
    async (
      packetId: string,
      tracker: {
        title: string;
        unit?: string | null;
        cadence?: string | null;
        reminders?: boolean;
        initialValue?: number | null;
        showInAnalytics?: boolean;
      },
    ): Promise<any> => {
      const mapped: any = {
        title: tracker.title,
        unit: tracker.unit ?? '',
        cadence: tracker.cadence ?? 'weekly',
        days: (tracker as any)?.days || [],
        reminders: !!tracker.reminders,
        showInAnalytics: !!tracker.showInAnalytics,
      };
      const { upsertTrackerDraft } = await import('@/data/storage/intake/adapter');
      return upsertTrackerDraft(String(packetId), mapped);
    },
    [],
  );

  const removeIntakeTracker = React.useCallback(async (packetIdOrGuid: string, idOrTitle?: string): Promise<void> => {
    const { deleteTrackerDraft } = await import('@/data/storage/intake/adapter');
    if (idOrTitle) await deleteTrackerDraft(String(packetIdOrGuid), String(idOrTitle));
    else await deleteTrackerDraft(String(packetIdOrGuid), String(packetIdOrGuid));
  }, []);

  const submitIntake = React.useCallback(async (packetId: string): Promise<void> => {
    await intakeStore.submitPacket(String(packetId));
  }, []);

  const listIntakeGoals = React.useCallback(async (packetId: string) => {
    const fn = (intakeStore as any).listGoalsForPacket as ((pid: string) => Promise<any[]>) | undefined;
    return fn ? fn(String(packetId)) : [];
  }, []);

  const listIntakeTrackers = React.useCallback(async (packetId: string) => {
    const { listTrackerDrafts } = await import('@/data/storage/intake/adapter');
    return (await listTrackerDrafts(String(packetId))) || [];
  }, []);

  const listIntakeAnswers = React.useCallback(async (packetId: string) => {
    const fn = (intakeStore as any).listAnswersForPacket as ((pid: string) => Promise<any[]>) | undefined;
    return fn ? fn(String(packetId)) : [];
  }, []);

  const deleteIntakeAnswer = React.useCallback(async (packetId: string, section: string, key: string) => {
    const fn = (intakeStore as any).deleteAnswer as
      | ((pid: string, s: string, k: string) => Promise<boolean>)
      | undefined;
    return fn ? fn(String(packetId), String(section), String(key)) : false;
  }, []);

  const getIntakeForClient = React.useCallback(async (clientId: string, clientEmail?: string) => {
    try {
      // Load via Worker (BFF pattern for httpOnly cookie auth)
      const intakeRes = await fetch(`/api/clients/intake?clientId=${clientId}`, { credentials: 'include' });
      if (!intakeRes.ok) {
        console.warn('Failed to get intake for client:', intakeRes.status);
        return null;
      }
      const { items } = await intakeRes.json();
      const _firstItem = Array.isArray(items) ? items[0] : null;
      if ((!items || items.length === 0) && clientEmail) {
        const allIntakeRes = await fetch('/api/clients/intake', { credentials: 'include' });
        if (allIntakeRes.ok) {
          const allPayload = await allIntakeRes.json();
          const allItems = Array.isArray(allPayload?.items) ? allPayload.items : [];
          const normalizedEmail = String(clientEmail).trim().toLowerCase();
          const emailMatches = allItems.filter((item: any) => {
            const packetEmail = String(item?.client_email || item?.clientEmail || '')
              .trim()
              .toLowerCase();
            return !!packetEmail && packetEmail === normalizedEmail;
          });
          const firstEmailMatch = emailMatches[0] || null;
          if (firstEmailMatch) {
            return firstEmailMatch;
          }
        }
      }
      return items?.[0] || null;
    } catch (err) {
      console.warn('Failed to get intake for client:', err);
      return null;
    }
  }, []);

  const getIntakePacketById = React.useCallback(async (packetId: string) => {
    try {
      // Load via Worker (BFF pattern for httpOnly cookie auth)
      const intakeRes = await fetch(`/api/clients/intake?packetId=${packetId}`, { credentials: 'include' });
      if (!intakeRes.ok) {
        console.warn('Failed to get intake packet:', intakeRes.status);
        return null;
      }
      const { packet } = await intakeRes.json();
      return packet || null;
    } catch (err) {
      console.warn('Failed to get intake packet:', err);
      return null;
    }
  }, []);

  const finalizeIntake = React.useCallback(
    async (
      packetOrInviteId: string,
      packetSeed?: {
        id: string;
        inviteId?: string;
        clientEmail?: string;
        status?: string;
        goals?: any[];
        trackers?: any[];
        answers?: any[];
      },
    ): Promise<{ clientId: string; migratedGoals: number; migratedTrackers: number; mergedAtISO: string }> => {
      const normKey = (s?: string) => (s || '').trim().toLowerCase();

      // 1) Resolve packet
      let pkt: any = packetSeed?.id ? packetSeed : null;
      if (!pkt) {
        try {
          if (typeof (intakeStore as any).getPacketById === 'function') {
            pkt = await (intakeStore as any).getPacketById(packetOrInviteId);
          }
        } catch {
          /* ignored */
        }
      }
      if (!pkt) {
        try {
          pkt = await (intakeStore as any).getPacketByInvite(packetOrInviteId);
        } catch {
          /* ignored */
        }
      }
      if (!pkt || !pkt.id) throw new Error('Packet not found');

      // Get coach ID from invite or coach_clients relationship (not from currentCoach which may be undefined for clients)
      let coachId: string;
      const sb = getSupabase();
      const email = String(pkt.clientEmail || '').toLowerCase() || undefined;

      if (sb && pkt.inviteId) {
        // Try to get coach from invite
        try {
          const { invitesStore } = await import('@/data/storage/app/invites.adapter');
          const invite = await invitesStore.get(String(pkt.inviteId));
          coachId = String(invite?.coachId || currentCoach?.id || 'coach_demo');
        } catch (err) {
          console.warn('Failed to load invite:', err);
          coachId = String(currentCoach?.id || 'coach_demo');
        }
      } else if (sb && authUser?.id) {
        // Try to get coach from coach_clients relationship for authenticated client
        try {
          const { data: link } = await sb
            .from('coach_clients')
            .select('coach_id')
            .eq('client_id', String(authUser.id))
            .maybeSingle();
          coachId = String(link?.coach_id || currentCoach?.id || 'coach_demo');
        } catch (err) {
          console.warn('Failed to query coach_clients:', err);
          coachId = String(currentCoach?.id || 'coach_demo');
        }
      } else {
        // Fallback to currentCoach (mock mode or coach viewing)
        coachId = String(currentCoach?.id || 'coach_demo');
      }

      const answers = Array.isArray(pkt.answers) ? pkt.answers : [];
      const fullName = answers.find((a: any) => a.section === 'personal' && (a.key === 'full_name' || a.key === 'name'))
        ?.value as string | undefined;

      // 1b) Stable mapping to reuse same client across reloads / re-runs
      const mapGet = (k: string) => {
        try {
          return localStorage.getItem(k) || undefined;
        } catch {
          return undefined;
        }
      };
      const mapSet = (k: string, v: string) => {
        try {
          localStorage.setItem(k, v);
        } catch {
          /* ignored */
        }
      };
      const byPktKey = `coachd:intake:packet:${String(pkt.id)}:clientId`;
      const byInvKey = pkt.inviteId ? `coachd:intake:invite:${String(pkt.inviteId)}:clientId` : undefined;
      const mappedId = mapGet(byPktKey) || (byInvKey ? mapGet(byInvKey) : undefined);

      // 2) Ensure/create local client - FIRST check database for existing profile
      let client =
        (clientsRef.current || []).find(
          (c) =>
            (mappedId && c.id === mappedId) ||
            (email && String(c.email || '').toLowerCase() === email) ||
            (fullName && String(c.name || '').trim() === String(fullName).trim()),
        ) || null;

      // If not found in memory, check database for existing profile by email
      if (!client && email) {
        try {
          const sb = getSupabase();
          if (sb) {
            const { data: existingProfile } = await sb
              .from('profiles')
              .select('id, email, full_name, role')
              .eq('email', email.toLowerCase())
              .eq('role', 'client')
              .maybeSingle();

            if (existingProfile) {
              client = {
                id: existingProfile.id,
                name: existingProfile.full_name || email.split('@')[0],
                email: existingProfile.email,
                assignedPrograms: [],
                assignedNutritionPlans: [],
                primaryCoachId: coachId,
                joinDate: new Date().toISOString(),
              } as any;

              // Add to clients list
              setClients((prev) => {
                const exists = prev.some((c) => c.id === client!.id);
                if (exists) return prev;
                const next = [...prev, client as any];
                if (isMockMode()) {
                  try {
                    localStorage.setItem('coach-clients', JSON.stringify(next));
                  } catch {
                    /* ignored */
                  }
                }
                return next;
              });
            }
          }
        } catch (err) {
          console.warn('Failed to query existing profile:', err);
        }
      }

      if (!client) {
        client = {
          id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          name: (fullName || email?.split('@')[0] || 'Client').trim(),
          email,
          assignedPrograms: [],
          assignedNutritionPlans: [],
          primaryCoachId: coachId,
          joinDate: new Date().toISOString(),
        } as any;
        setClients((prev) => {
          const exists = prev.some(
            (c) => c.id === client!.id || (!!email && String(c.email || '').toLowerCase() === email),
          );
          const next = exists
            ? prev.map((c) => (c.id === client!.id ? { ...c, email: c.email || email } : c))
            : [...prev, { ...client!, email: client!.email || email } as any];
          if (isMockMode()) {
            try {
              localStorage.setItem('coach-clients', JSON.stringify(next));
            } catch {
              /* ignored */
            }
          }
          return next;
        });
      }
      // Store mapping for future re-runs
      try {
        mapSet(byPktKey, String(client!.id));
        if (byInvKey) mapSet(byInvKey, String(client!.id));
      } catch {
        /* ignored */
      }

      // 3) Load drafts if not embedded
      let goalDrafts: any[] = Array.isArray(pkt.goals) ? pkt.goals : [];
      let trackerDrafts: any[] = Array.isArray(pkt.trackers) ? pkt.trackers : [];
      try {
        if (!goalDrafts.length && typeof (intakeStore as any).listGoalsForPacket === 'function')
          goalDrafts = await (intakeStore as any).listGoalsForPacket(pkt.id);
      } catch {
        /* ignored */
      }
      try {
        if (!trackerDrafts.length && typeof (intakeStore as any).listTrackersForPacket === 'function')
          trackerDrafts = await (intakeStore as any).listTrackersForPacket(pkt.id);
      } catch {
        /* ignored */
      }

      // 4) Dedupe from existing
      const existingGoals = await (async () => {
        try {
          return value.listGoals ? await value.listGoals(client!.id) : [];
        } catch {
          return [];
        }
      })();
      const existingTrackers = await (async () => {
        try {
          return await listTrackers(client!.id);
        } catch {
          return [];
        }
      })();
      const goalKeys = new Set((existingGoals || []).map((g: any) => normKey(String(g.title))));
      const trackerKeys = new Set((existingTrackers || []).map((t: any) => normKey(String(t.title))));

      // 5) Migrate goals
      let migratedGoals = 0;
      for (const g of goalDrafts) {
        const title = String(g?.title || g?.name || '').trim();
        if (!title) continue;
        const key = normKey(title);
        if (goalKeys.has(key)) continue;
        const newGoal: any = {
          clientId: client!.id,
          ownerCoachId: coachId,
          title,
          type: 'performance',
          unit: g.unit ?? '',
          targetValue: Number(g.targetValue ?? g.target ?? 0),
          currentValue: 0,
          priority: g.priority ?? 'medium',
          shareWithCoach: true,
          status: 'active',
          progress: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        try {
          await (value as any).createClientGoal(newGoal);
          goalKeys.add(key);
          migratedGoals++;
        } catch {
          /* ignored */
        }
      }

      // 6) Migrate trackers
      let migratedTrackers = 0;
      for (const t of trackerDrafts) {
        const title = String(t?.title || t?.name || '').trim();
        if (!title) {
          continue;
        }
        const key = normKey(title);
        if (trackerKeys.has(key)) {
          continue;
        }
        const schedule: TrackerSchedule = {
          cadence: t.cadence || 'weekly',
          daysOfWeek: [0],
          remindersEnabled: !!t.reminders,
        } as any;
        try {
          // Bypass userType guard by writing directly via store
          const now = new Date().toISOString();
          const row = normalizeTrackerBeforeSave({
            id: `trk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            clientId: String(client!.id),
            ownerCoachId: coachId, // Use coachId instead of currentCoach?.id
            title,
            unit: (t.unit ?? 'lbs') as any,
            targetDirection: 'stability',
            schedule,
            createdAt: now,
            updatedAt: now,
          } as any);
          await trackersStore.upsertTracker(row);
          trackerKeys.add(key);
          migratedTrackers++;
        } catch (err) {
          console.error('Failed to migrate tracker:', title, err);
        }
      }

      // 7) Mark merged (safe in supabase adapter)
      await intakeMarkMerged(String(pkt.id), String(client!.id));

      // 8) Force refresh of goals/trackers state to immediately show migrated data in UI
      try {
        const refreshedGoals = await goalsStore.getClientGoals(String(client!.id));
        setClientGoalsMap((prev) => ({ ...prev, [String(client!.id)]: refreshedGoals as ClientGoal[] }));

        const refreshedTrackers = await trackersStore.listTrackers(String(client!.id));
        setClientTrackersMap((prev) => ({ ...prev, [String(client!.id)]: refreshedTrackers as ClientTracker[] }));
      } catch (err) {
        console.warn('Failed to refresh goals/trackers state after migration:', err);
      }

      // 9) Emit event (best effort) and mirror to local state so selectors pick it up immediately
      try {
        const evtBase: any = {
          clientId: client!.id,
          type: 'client_joined',
          summary: 'Client joined',
          detail: email,
          priority: 'low',
        };
        await addClientEvent(evtBase);
        const mirrored = {
          id: `evt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          createdAt: new Date().toISOString(),
          ...evtBase,
        } as any;
        setClientEvents((prev) => {
          const out = [mirrored, ...prev];
          try {
            localStorage.setItem('coachd-client-events', JSON.stringify(out));
          } catch {
            /* ignored */
          }
          return out;
        });
      } catch {
        /* ignored */
      }

      const mergedAtISO = new Date().toISOString();
      return { clientId: client!.id, migratedGoals, migratedTrackers, mergedAtISO };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCoach?.id, clients, createTracker, addClientEvent],
  );

  const deleteTracker = React.useCallback(
    async (clientId: string, trackerId: string) => {
      if (userType !== 'coach') return;
      await trackersStore.deleteTracker(String(trackerId));
      // Refresh trackers list for this client
      const refreshed = await trackersStore.listTrackers(String(clientId));
      setClientTrackersMap((prev) => ({ ...prev, [String(clientId)]: refreshed as ClientTracker[] }));
    },
    [userType],
  );

  const listTrackerEntries = React.useCallback(async (trackerId: string) => {
    return trackersStore.listEntries(String(trackerId));
  }, []);

  const addTrackerEntry = React.useCallback(
    async (args: {
      trackerId: string;
      clientId: string;
      value: number;
      note?: string;
      createdBy?: 'client' | 'coach';
    }) => {
      try {
        // Use Worker endpoint instead of direct Supabase. The coach endpoint only accepts the
        // tracker's coach, so a client logging their own entry needs the client endpoint.
        const response =
          userType === 'coach'
            ? await trackersApi.addTrackerEntry({
                trackerId: args.trackerId,
                clientId: args.clientId,
                value: args.value,
                note: args.note,
                createdBy: args.createdBy ?? 'coach',
              })
            : await trackersApi.logClientTrackerEntry({
                trackerId: args.trackerId,
                value: args.value,
                note: args.note,
              });

        if (!response.ok) {
          throw new Error(bodyError(response) || 'Failed to add tracker entry');
        }

        const result = response as { entry: Record<string, any> };
        // Trigger event so linked goals can refresh
        try {
          window.dispatchEvent(new CustomEvent('goals:updated', { detail: { clientId: args.clientId } }));
        } catch {
          /* ignored */
        }

        // Update local cache immediately
        const newEntry = result.entry;

        // Update the tracker's last logged time in cache
        setClientTrackersMap((prev) => {
          const clientTrackers = prev[String(args.clientId)] || [];
          const updatedTrackers = clientTrackers.map((tracker) => {
            if (tracker.id === args.trackerId) {
              return {
                ...tracker,
                lastLoggedAtISO: newEntry.at,
                updatedAt: new Date().toISOString(),
              };
            }
            return tracker;
          });

          return {
            ...prev,
            [String(args.clientId)]: updatedTrackers,
          };
        });
      } catch (error) {
        console.error('❌ Failed to add tracker entry:', error);
        throw error;
      }
    },
    [userType],
  );

  const signOut = React.useCallback(async () => {
    let signedInAsClient = userType === 'client';
    try {
      if (!signedInAsClient && localStorage.getItem('coachd:userType') === 'client') {
        signedInAsClient = true;
      }
    } catch {
      /* ignore */
    }

    // 1. Clear httpOnly cookies via Worker (best effort; never throws)
    await signOutOnServer();

    // 2. Clear Supabase client-side session (localStorage / in-memory)
    try {
      const sb = getSupabase();
      if (sb) await sb.auth.signOut();
    } catch {
      /* best effort */
    }

    // 3. Clear local app state
    try {
      clearSentryUser();
      resetPostHog();
      setAuthSession(null);
      setAuthUser(null);
      localStorage.removeItem('coachd:userType');
    } catch {
      /* ignored */
    }
    clearPendingInvite();

    // 4. Redirect to sign-in (must not rely on GET /api/auth/signout — Vite-only dev serves SPA HTML there)
    window.location.replace(signedInAsClient ? '/client/auth' : '/coach/sign-in');
  }, [userType]);

  // --- Lightweight messaging actions ---
  const listThreadsForCoach = React.useCallback(async (id: string): Promise<MessageThread[]> => {
    return messagesStore.getThreadsByCoach(String(id));
  }, []);

  // Evaluate metric alerts for active client on focus
  useEffect(() => {
    function onFocus() {
      try {
        const client = (clients || [])[0];
        if (!client) return;
        import('@/data/storage/metrics/adapter').then(async (m) => {
          const rules = await (m.metricsStore as any).getRules?.(String(client.id));
          if (!Array.isArray(rules) || rules.length === 0) return;
          const { evaluateRules } = await import('@/utils/metricAlerts');
          const items = await evaluateRules(String(client.id), rules as any);
          const today = new Date().toISOString().slice(0, 10);
          for (const it of items) {
            const dedupeId = `metric_alert:${it.ruleId}:${today}`;
            await addClientEvent({
              clientId: String(client.id),
              type: 'metric_alert' as any,
              summary: it.summary,
              detail: it.detail,
              meta: { ruleId: it.ruleId, metricId: it.metricId, day: today, dedupeId } as any,
            });
          }
        });
      } catch {
        /* ignored */
      }
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  // Synthesize tracker due reminders on focus
  useEffect(() => {
    const onFocus = () => {
      try {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const dedupeKey = `coachd:tracker_due_dedupe:${today}`;
        let seen: Record<string, true> = {} as any;
        try {
          seen = JSON.parse(localStorage.getItem(dedupeKey) || '{}') || {};
        } catch {
          /* ignored */
        }
        const coachScopedClients = Array.isArray(clients)
          ? clients.filter(
              (c) => !currentCoach?.id || !c.primaryCoachId || String(c.primaryCoachId) === String(currentCoach.id),
            )
          : [];
        (async () => {
          for (const c of coachScopedClients) {
            try {
              const rows = await trackersStore.listTrackers(String(c.id));
              const due = rows.filter((t: any) => !!t.schedule?.remindersEnabled && String(t.nextDueOn) === today);
              for (const t of due) {
                const key = `${c.id}:${t.id}`;
                if (seen[key]) continue;
                seen[key] = true as any;
                await addClientEvent({
                  clientId: String(c.id),
                  type: 'metric_alert' as any,
                  summary: `Tracker due today: ${t.title}`,
                  detail: '',
                  meta: { trackerId: t.id },
                });
              }
            } catch {
              /* ignored */
            }
          }
          try {
            localStorage.setItem(dedupeKey, JSON.stringify(seen));
          } catch {
            /* ignored */
          }
        })();
      } catch {
        /* ignored */
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, currentCoach?.id]);

  const listThreadsForClient = React.useCallback(async (id: string): Promise<MessageThread[]> => {
    return messagesStore.getThreadsByClient(String(id));
  }, []);
  const markThreadRead = React.useCallback(
    async (args: { threadId: string; userType: 'coach' | 'client'; userId: string }) => {
      await messagesStore.markRead(args.threadId, args.userType, args.userId);
    },
    [],
  );
  const sendMessage = React.useCallback(
    async (args: {
      threadId?: string;
      clientId?: string;
      coachId?: string;
      body: string;
    }): Promise<{ id: string; threadId: string } | void> => {
      const now = new Date().toISOString();
      // 1) Resolve thread first (by id or pair)
      let thread = args.threadId ? await (messagesStore as any).getThread?.(String(args.threadId)) : null;
      const resolvedCoachId = String(args.coachId ?? currentCoach?.id ?? 'coach_demo');
      const resolvedClientId = String(args.clientId ?? ((Array.isArray(clients) && clients[0]?.id) || 'c1'));
      if (!thread) {
        const maybe = await (messagesStore as any).getThreadByPair?.(resolvedClientId, resolvedCoachId);
        thread = maybe ? maybe : await messagesStore.createThread(resolvedClientId, resolvedCoachId);
      }

      // 2) Derive author from thread participants / current role
      const coachOnThread = String(thread.coachId);
      const clientOnThread = String(thread.clientId);
      const authorType = (userType === 'coach' ? 'coach' : 'client') as 'coach' | 'client';
      const authorId = authorType === 'coach' ? coachOnThread : clientOnThread;

      // 3) Append
      const msg: ChatMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        threadId: String(thread.id),
        authorType,
        authorId,
        body: String(args.body || ''),
        createdAt: now,
      };
      await messagesStore.appendMessage(msg);
      // Optional dual-write to local during supabase cutover
      try {
        const dual = (import.meta as any).env?.VITE_MESSAGES_DUAL_WRITE === '1';
        const usingSupabase = (import.meta as any).env?.VITE_MESSAGES_BACKEND === 'supabase';
        if (dual && usingSupabase) {
          const { localMessagesStore } = await import('@/data/storage/messages/local');
          await localMessagesStore.appendMessage(msg);
        }
      } catch {
        /* ignored */
      }

      // 4) Emit unread event only when the other side is recipient
      try {
        const emitForClientRecipient =
          (import.meta as any).env?.VITE_UNREAD_EVENT_FOR_CLIENT === '1' && authorType === 'coach';
        const emitForCoachRecipient = authorType === 'client';
        if (emitForCoachRecipient || emitForClientRecipient) {
          await addClientEvent({
            clientId: String(clientOnThread),
            type: 'unread_message' as any,
            summary: 'New message',
            detail: String(args.body || '').slice(0, 120),
            meta: { threadId: String(thread.id) } as any,
          });
        }
      } catch {
        /* ignored */
      }

      // 5) Create activity event for client messages (when client sends to coach)
      try {
        if (authorType === 'client') {
          await createMessageActivity(String(clientOnThread), String(coachOnThread), String(args.body || ''), msg.id);
        }
      } catch (error) {
        console.error('Failed to create message activity event:', error);
      }
      return { id: msg.id, threadId: String(thread.id) };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userType, clients, coachId, currentCoach?.id],
  );

  // Optional dev seed
  useEffect(() => {
    const seed = async () => {
      try {
        if ((import.meta as any).env?.VITE_MESSAGES_DEV_SEED !== '1') return;
        // Only seed in mock mode
        if (import.meta.env?.VITE_USE_MOCKS !== 'true') return;
        const cid = String((Array.isArray(clients) && clients[0]?.id) || 'c1');
        const existing = (messagesStore as any).getThreadByPair?.(String(cid), String(coachId));
        if (existing) return;
        const t = await messagesStore.createThread(cid, String(coachId));
        const samples = [
          { authorType: 'client', authorId: cid, body: 'Hi coach! Quick question about my workout.' },
          { authorType: 'coach', authorId: String(coachId), body: 'Sure! What do you need help with?' },
          { authorType: 'client', authorId: cid, body: 'Are lunges okay with my knee today?' },
          {
            authorType: 'coach',
            authorId: String(coachId),
            body: 'Yes, do reverse lunges and reduce range of motion.',
          },
        ];
        for (const s of samples) {
          const msg: any = {
            id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            threadId: t.id,
            createdAt: new Date().toISOString(),
            readBy: [],
            ...s,
          };
          await messagesStore.appendMessage(msg);
        }
      } catch {
        /* ignored */
      }
    };
    seed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId]);

  // Supabase health check in dev when supabase backend is selected
  useEffect(() => {
    try {
      const usingSupabase = (import.meta as any).env?.VITE_MESSAGES_BACKEND === 'supabase';
      if ((import.meta as any).env?.DEV && usingSupabase) {
        import('@/utils/supabaseHealth')
          .then(async (m) => {
            const res = await m.supabaseHealth();
            // eslint-disable-next-line no-console
            console.debug('[Supabase health]', res);
          })
          .catch(() => {});
      }
    } catch {
      /* ignored */
    }
  }, []);

  // Guard: block workout/program content edits in client mode (optional dev bypass)
  const allowClientEdits = (import.meta as any).env?.VITE_ALLOW_CLIENT_EDITS === '1';
  function requireCoach(action: string) {
    if (allowClientEdits) return true;
    if ((userType ?? 'client') !== 'coach') {
      try {
        if ((import.meta as any).env?.DEV) console.warn(`[guard] ${action} blocked for clients`);
      } catch {
        /* ignored */
      }
      return false;
    }
    return true;
  }

  // Goals: load for all clients and set up real-time subscriptions
  useEffect(() => {
    const sb = getSupabase();

    // Load goals and trackers for all clients
    const loadAllGoalsAndTrackers = async () => {
      let allClients: string[] = [];

      if (isMockMode()) {
        allClients = ['c1'];
      } else {
        // Include both: coach's clients AND the authenticated user (if client)
        const clientIds = clients.map((c) => c.id);
        if (authUser?.id && !clientIds.includes(authUser.id)) {
          allClients = [...clientIds, authUser.id];
        } else {
          allClients = clientIds;
        }
      }

      // Clear any bad cached data for all clients
      allClients.forEach((clientId) => {
        clearGoalsCache(clientId);
      });

      // TODO(perf): N+1 fetch pattern flagged by Sentry (COACHED-REACT-UI-4 /
      // COACHED-REACT-UI-13). For each client we do TWO sequential awaited
      // fetches — /api/coach/goals and /api/coach/trackers — so a coach with
      // 20 clients on dashboard load triggers 40 sequential round-trips.
      // Right fix is a server-side bulk endpoint:
      //   GET /api/coach/clients-overview → { [clientId]: { goals, trackers } }
      // and replace this loop with a single call. Tracked in Sentry; not done
      // here because it's a multi-file change (new worker endpoint + caller +
      // tests + deploy).
      for (const clientId of allClients) {
        try {
          // Load goals via Worker (BFF pattern for httpOnly cookie auth)
          const goalsRes = await fetch(`/api/coach/goals?clientId=${clientId}`, { credentials: 'include' });
          if (goalsRes.ok) {
            const result = await goalsRes.json();
            // Map database format to ClientGoal format
            const goals = (result.goals || []).map((row: any) => ({
              id: row.id,
              clientId: row.client_id,
              ownerCoachId: row.owner_coach_id,
              title: row.title,
              description: row.description,
              targetValue: row.target_value ? parseFloat(row.target_value) : 0,
              currentValue: row.current_value ? parseFloat(row.current_value) : 0,
              unit: row.unit,
              type: row.type,
              status: row.status,
              progress: row.progress,
              schedule: row.schedule,
              lastCheckinAtISO: row.last_checkin_at,
              priority: row.priority,
              deadline: row.deadline,
              history: [],
              createdAt: row.created_at,
              updatedAt: row.updated_at,
              nextDueOn: row.next_due_on,
            }));
            setClientGoalsMap((prev) => ({ ...prev, [String(clientId)]: goals }));
          } else {
            console.warn(`Failed to load goals for client ${clientId}:`, goalsRes.status);
            setClientGoalsMap((prev) => ({ ...prev, [String(clientId)]: [] }));
          }

          // Load trackers via Worker (BFF pattern for httpOnly cookie auth)
          const trackersRes = await fetch(`/api/coach/trackers?clientId=${clientId}`, { credentials: 'include' });
          if (trackersRes.ok) {
            const { trackers } = await trackersRes.json();
            setClientTrackersMap((prev) => ({ ...prev, [String(clientId)]: trackers || [] }));
          } else {
            console.warn(`Failed to load trackers for client ${clientId}:`, trackersRes.status);
            setClientTrackersMap((prev) => ({ ...prev, [String(clientId)]: [] }));
          }
        } catch (err) {
          console.warn(`Failed to load goals/trackers for client ${clientId}:`, err);
          setClientGoalsMap((prev) => ({ ...prev, [String(clientId)]: [] }));
          setClientTrackersMap((prev) => ({ ...prev, [String(clientId)]: [] }));
        }
      }
    };

    loadAllGoalsAndTrackers();

    // Set up real-time subscriptions for goal and tracker changes
    if (sb && !isMockMode()) {
      const goalsChannel = sb
        .channel('goals-realtime')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'client_goals',
          },
          async (payload) => {
            const affectedClientId = (payload.new as any)?.client_id || (payload.old as any)?.client_id;
            if (affectedClientId) {
              try {
                // Clear cache first to force fresh fetch
                clearGoalsCache(affectedClientId);

                // Fetch fresh data via Worker (which filters for active goals)
                const response = await fetch(`/api/coach/goals?clientId=${affectedClientId}`, {
                  credentials: 'include',
                });
                if (response.ok) {
                  const result = await response.json();
                  // Map database format to ClientGoal format
                  const goals = (result.goals || []).map((row: any) => ({
                    id: row.id,
                    clientId: row.client_id,
                    ownerCoachId: row.owner_coach_id,
                    title: row.title,
                    description: row.description,
                    targetValue: row.target_value ? parseFloat(row.target_value) : 0,
                    currentValue: row.current_value ? parseFloat(row.current_value) : 0,
                    unit: row.unit,
                    type: row.type,
                    status: row.status,
                    progress: row.progress,
                    schedule: row.schedule,
                    priority: row.priority,
                    deadline: row.deadline,
                    shareWithCoach: row.share_with_coach ?? true,
                    lastCheckinAtISO: row.last_checkin_at,
                    history: [],
                    createdAt: row.created_at,
                    updatedAt: row.updated_at,
                    nextDueOn: row.next_due_on,
                  }));
                  setClientGoalsMap((prev) => ({ ...prev, [affectedClientId]: goals }));
                }
              } catch (err) {
                console.warn('Failed to reload goals via Worker:', err);
              }
            }
          },
        )
        .subscribe();

      const trackersChannel = sb
        .channel('trackers-realtime')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'client_trackers',
          },
          (payload) => {
            const affectedClientId = (payload.new as any)?.client_id || (payload.old as any)?.client_id;
            if (affectedClientId) {
              trackersStore
                .listTrackers(affectedClientId)
                .then((ts) => {
                  setClientTrackersMap((prev) => ({ ...prev, [affectedClientId]: ts }));
                })
                .catch((err) => {
                  console.warn('Failed to reload trackers:', err);
                });
            }
          },
        )
        .subscribe();

      const trackerEntriesChannel = sb
        .channel('tracker-entries-realtime')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'client_tracker_entries',
          },
          (payload) => {
            const affectedClientId = (payload.new as any)?.client_id || (payload.old as any)?.client_id;
            if (affectedClientId) {
              trackersStore
                .listTrackers(affectedClientId)
                .then((ts) => {
                  setClientTrackersMap((prev) => ({ ...prev, [affectedClientId]: ts }));
                })
                .catch((err) => {
                  console.warn('Failed to reload trackers after entry change:', err);
                });
            }
          },
        )
        .subscribe();

      // Subscribe to coach_clients changes for real-time client sync
      const coachClientsChannel = sb
        .channel('coach-clients-realtime')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'coach_clients',
            filter: `coach_id=eq.${currentCoach?.id}`,
          },
          async (_payload) => {
            try {
              const updatedClients = await listClients();
              setClients(updatedClients);
            } catch (error) {
              console.error('Failed to reload clients after coach-client change:', error);
            }
          },
        )
        .subscribe();

      return () => {
        sb.removeChannel(goalsChannel);
        sb.removeChannel(trackersChannel);
        sb.removeChannel(trackerEntriesChannel);
        sb.removeChannel(coachClientsChannel);
      };
    } else {
      // Fallback to localStorage events for mock mode
      const onStorage = (e: StorageEvent) => {
        if (e.key === 'coachd-client-goals' || e.key === 'coachd-client-trackers') {
          loadAllGoalsAndTrackers();
        }
      };
      window.addEventListener('storage', onStorage);
      return () => window.removeEventListener('storage', onStorage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, authUser]);
  // Heuristic to infer modality
  const inferModality = (s?: string) => {
    const x = (s ?? '').toLowerCase();
    if (/\b(rest|off|recover|deload)\b/.test(x)) return 'recovery';
    if (/\b(hiit|interval)\b/.test(x)) return 'hiit';
    if (/\b(cardio|run|bike|row|erg|conditioning)\b/.test(x)) return 'cardio';
    if (/\b(mobility|stretch|yoga)\b/.test(x)) return 'mobility';
    return 'strength';
  };

  // Sample food items database
  const sampleFoodItems: FoodItem[] = [
    {
      id: 'f1',
      name: 'Chicken Breast',
      calories: 165,
      protein: 31,
      carbs: 0,
      fat: 3.6,
      serving_size: '100g',
      category: 'Protein',
    },
    {
      id: 'f2',
      name: 'Brown Rice',
      calories: 112,
      protein: 2.6,
      carbs: 23,
      fat: 0.9,
      serving_size: '100g',
      category: 'Carbs',
    },
    {
      id: 'f3',
      name: 'Broccoli',
      calories: 34,
      protein: 2.8,
      carbs: 7,
      fat: 0.4,
      serving_size: '100g',
      category: 'Vegetables',
    },
    {
      id: 'f4',
      name: 'Almonds',
      calories: 579,
      protein: 21,
      carbs: 22,
      fat: 50,
      serving_size: '100g',
      category: 'Fats',
    },
    {
      id: 'f5',
      name: 'Sweet Potato',
      calories: 86,
      protein: 1.6,
      carbs: 20,
      fat: 0.1,
      serving_size: '100g',
      category: 'Carbs',
    },
    {
      id: 'f6',
      name: 'Salmon',
      calories: 208,
      protein: 20,
      carbs: 0,
      fat: 13,
      serving_size: '100g',
      category: 'Protein',
    },
    { id: 'f7', name: 'Avocado', calories: 160, protein: 2, carbs: 9, fat: 15, serving_size: '100g', category: 'Fats' },
    {
      id: 'f8',
      name: 'Quinoa',
      calories: 120,
      protein: 4.4,
      carbs: 22,
      fat: 1.9,
      serving_size: '100g',
      category: 'Carbs',
    },
    {
      id: 'f9',
      name: 'Greek Yogurt',
      calories: 59,
      protein: 10,
      carbs: 3.6,
      fat: 0.4,
      serving_size: '100g',
      category: 'Protein',
    },
    {
      id: 'f10',
      name: 'Spinach',
      calories: 23,
      protein: 2.9,
      carbs: 3.6,
      fat: 0.4,
      serving_size: '100g',
      category: 'Vegetables',
    },
  ];

  // Sample nutrition templates with actual food data
  const sampleNutritionTemplates: NutritionTemplate[] = [
    {
      id: 'test-template',
      title: 'Test',
      description: 'Test meal plan with sample foods',
      tags: ['Test', 'Sample'],
      difficulty: 'Beginner',
      duration: '1 week',
      thumbnail: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400',
      createdDate: '2024-08-01',
      assignedClients: 2,
      isPublic: true,
      targetNutrition: {
        dailyCalories: 2000,
        proteinPercentage: 25,
        carbsPercentage: 45,
        fatPercentage: 30,
      },
      days: [
        {
          id: 'test-d1',
          dayNumber: 1,
          dayName: 'Monday',
          dayType: 'Test Day', // Use a different normalized name for demo
          meals: [
            {
              id: 'test-m1',
              name: 'Breakfast',
              type: 'breakfast',
              foods: [
                {
                  foodItem: sampleFoodItems[8], // Greek Yogurt
                  quantity: 150,
                  unit: 'g',
                  nutrition: { calories: 88.5, protein: 15, carbs: 5.4, fat: 0.6 },
                },
                {
                  foodItem: sampleFoodItems[3], // Almonds
                  quantity: 20,
                  unit: 'g',
                  nutrition: { calories: 115.8, protein: 4.2, carbs: 4.4, fat: 10 },
                },
              ],
              totalNutrition: { calories: 204.3, protein: 19.2, carbs: 9.8, fat: 10.6 },
            },
            {
              id: 'test-m2',
              name: 'Lunch',
              type: 'lunch',
              foods: [
                {
                  foodItem: sampleFoodItems[0], // Chicken Breast
                  quantity: 120,
                  unit: 'g',
                  nutrition: { calories: 198, protein: 37.2, carbs: 0, fat: 4.32 },
                },
                {
                  foodItem: sampleFoodItems[1], // Brown Rice
                  quantity: 80,
                  unit: 'g',
                  nutrition: { calories: 89.6, protein: 2.08, carbs: 18.4, fat: 0.72 },
                },
                {
                  foodItem: sampleFoodItems[2], // Broccoli
                  quantity: 80,
                  unit: 'g',
                  nutrition: { calories: 27.2, protein: 2.24, carbs: 5.6, fat: 0.32 },
                },
              ],
              totalNutrition: { calories: 314.8, protein: 41.52, carbs: 24, fat: 5.36 },
            },
            {
              id: 'test-m3',
              name: 'Dinner',
              type: 'dinner',
              foods: [
                {
                  foodItem: sampleFoodItems[5], // Salmon
                  quantity: 120,
                  unit: 'g',
                  nutrition: { calories: 249.6, protein: 24, carbs: 0, fat: 15.6 },
                },
                {
                  foodItem: sampleFoodItems[4], // Sweet Potato
                  quantity: 150,
                  unit: 'g',
                  nutrition: { calories: 129, protein: 2.4, carbs: 30, fat: 0.15 },
                },
                {
                  foodItem: sampleFoodItems[6], // Avocado
                  quantity: 50,
                  unit: 'g',
                  nutrition: { calories: 80, protein: 1, carbs: 4.5, fat: 7.5 },
                },
              ],
              totalNutrition: { calories: 458.6, protein: 27.4, carbs: 34.5, fat: 23.25 },
            },
          ],
          dailyNutrition: { calories: 977.7, protein: 88.12, carbs: 68.3, fat: 39.21 },
        },
      ],
    },
    {
      id: 'nt1',
      title: 'High Protein Muscle Building',
      description: 'Optimized meal plan for muscle growth and recovery',
      tags: ['High Protein', 'Muscle Building', 'Performance'],
      difficulty: 'Intermediate',
      duration: '4 weeks',
      thumbnail: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400',
      createdDate: '2024-08-01',
      assignedClients: 5,
      isPublic: true,
      targetNutrition: {
        dailyCalories: 2500,
        proteinPercentage: 30,
        carbsPercentage: 40,
        fatPercentage: 30,
      },
      days: [
        {
          id: 'd1',
          dayNumber: 1,
          dayName: 'Monday',
          dayType: 'High Protein Day', // Use normalized name
          meals: [
            {
              id: 'm1',
              name: 'Breakfast',
              type: 'breakfast',
              foods: [
                {
                  foodItem: sampleFoodItems[8], // Greek Yogurt
                  quantity: 200,
                  unit: 'g',
                  nutrition: { calories: 118, protein: 20, carbs: 7.2, fat: 0.8 },
                },
                {
                  foodItem: sampleFoodItems[3], // Almonds
                  quantity: 30,
                  unit: 'g',
                  nutrition: { calories: 173.7, protein: 6.3, carbs: 6.6, fat: 15 },
                },
              ],
              totalNutrition: { calories: 291.7, protein: 26.3, carbs: 13.8, fat: 15.8 },
            },
            {
              id: 'm2',
              name: 'Lunch',
              type: 'lunch',
              foods: [
                {
                  foodItem: sampleFoodItems[0], // Chicken Breast
                  quantity: 150,
                  unit: 'g',
                  nutrition: { calories: 247.5, protein: 46.5, carbs: 0, fat: 5.4 },
                },
                {
                  foodItem: sampleFoodItems[1], // Brown Rice
                  quantity: 100,
                  unit: 'g',
                  nutrition: { calories: 112, protein: 2.6, carbs: 23, fat: 0.9 },
                },
                {
                  foodItem: sampleFoodItems[2], // Broccoli
                  quantity: 100,
                  unit: 'g',
                  nutrition: { calories: 34, protein: 2.8, carbs: 7, fat: 0.4 },
                },
              ],
              totalNutrition: { calories: 393.5, protein: 51.9, carbs: 30, fat: 6.7 },
            },
            {
              id: 'm3',
              name: 'Dinner',
              type: 'dinner',
              foods: [
                {
                  foodItem: sampleFoodItems[5], // Salmon
                  quantity: 150,
                  unit: 'g',
                  nutrition: { calories: 312, protein: 30, carbs: 0, fat: 19.5 },
                },
                {
                  foodItem: sampleFoodItems[4], // Sweet Potato
                  quantity: 200,
                  unit: 'g',
                  nutrition: { calories: 172, protein: 3.2, carbs: 40, fat: 0.2 },
                },
                {
                  foodItem: sampleFoodItems[9], // Spinach
                  quantity: 100,
                  unit: 'g',
                  nutrition: { calories: 23, protein: 2.9, carbs: 3.6, fat: 0.4 },
                },
              ],
              totalNutrition: { calories: 507, protein: 36.1, carbs: 43.6, fat: 20.1 },
            },
          ],
          dailyNutrition: { calories: 1192.2, protein: 114.3, carbs: 87.4, fat: 42.6 },
        },
      ],
    },
  ];

  // Sample workout templates
  const sampleWorkoutTemplates: WorkoutTemplate[] = [
    {
      id: 'wt1',
      title: 'Beginner Full Body',
      description: 'A comprehensive full body workout for beginners',
      tags: ['Beginner', 'Full Body', 'Strength'],
      difficulty: 'Beginner',
      duration: '4 weeks',
      createdDate: '2024-08-01',
      assignedClients: 3,
      isPublic: true,
      weeks: {
        week1: [
          {
            id: 'w1d1',
            name: 'Full Body A',
            exercises: [
              { id: 'e1', name: 'Bodyweight Squat', sets: 3, reps: '8-12', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e2', name: 'Push-ups', sets: 3, reps: '5-10', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e3', name: 'Bent-over Row', sets: 3, reps: '8-12', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e4', name: 'Plank', sets: 3, reps: '20-30s', effort: 'RPE 6-7', rest: '30s' },
            ],
            notes: 'Focus on form over speed',
            estimatedDuration: 45,
            dayOfWeek: 'Monday',
          },
          {
            id: 'w1d2',
            name: 'Full Body B',
            exercises: [
              { id: 'e5', name: 'Goblet Squat', sets: 3, reps: '8-12', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e6', name: 'Incline Push-ups', sets: 3, reps: '5-10', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e7', name: 'Lat Pulldown', sets: 3, reps: '8-12', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e8', name: 'Dead Bug', sets: 3, reps: '5-8 each side', effort: 'RPE 6-7', rest: '30s' },
            ],
            notes: 'Increase weight when you can complete all reps',
            estimatedDuration: 45,
            dayOfWeek: 'Wednesday',
          },
          {
            id: 'w1d3',
            name: 'Full Body C',
            exercises: [
              { id: 'e9', name: 'Lunges', sets: 3, reps: '6-10 each leg', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e10', name: 'Chest Press', sets: 3, reps: '8-12', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e11', name: 'Seated Row', sets: 3, reps: '8-12', effort: 'RPE 6-7', rest: '60s' },
              { id: 'e12', name: 'Side Plank', sets: 2, reps: '15-20s each side', effort: 'RPE 6-7', rest: '30s' },
            ],
            notes: 'Great job this week!',
            estimatedDuration: 45,
            dayOfWeek: 'Friday',
          },
        ],
      },
    },
    {
      id: 'wt2',
      title: 'Upper/Lower Split',
      description: 'Intermediate program focusing on upper/lower body split',
      tags: ['Intermediate', 'Upper/Lower', 'Strength'],
      difficulty: 'Intermediate',
      duration: '6 weeks',
      createdDate: '2024-08-01',
      assignedClients: 2,
      isPublic: true,
      weeks: {
        week1: [
          {
            id: 'w2d1',
            name: 'Upper Body A',
            exercises: [
              { id: 'e13', name: 'Bench Press', sets: 4, reps: '6-8', effort: 'RPE 7-8', rest: '2-3min' },
              { id: 'e14', name: 'Bent-over Row', sets: 4, reps: '6-8', effort: 'RPE 7-8', rest: '2-3min' },
              { id: 'e15', name: 'Overhead Press', sets: 3, reps: '8-10', effort: 'RPE 7-8', rest: '2min' },
              { id: 'e16', name: 'Pull-ups', sets: 3, reps: '5-10', effort: 'RPE 7-8', rest: '2min' },
              { id: 'e17', name: 'Dips', sets: 3, reps: '8-12', effort: 'RPE 7-8', rest: '90s' },
            ],
            notes: 'Focus on progressive overload',
            estimatedDuration: 60,
            dayOfWeek: 'Monday',
          },
          {
            id: 'w2d2',
            name: 'Lower Body A',
            exercises: [
              { id: 'e18', name: 'Barbell Squat', sets: 4, reps: '6-8', effort: 'RPE 7-8', rest: '2-3min' },
              { id: 'e19', name: 'Romanian Deadlift', sets: 4, reps: '8-10', effort: 'RPE 7-8', rest: '2-3min' },
              {
                id: 'e20',
                name: 'Bulgarian Split Squat',
                sets: 3,
                reps: '8-12 each leg',
                effort: 'RPE 7-8',
                rest: '90s',
              },
              { id: 'e21', name: 'Calf Raises', sets: 4, reps: '12-15', effort: 'RPE 7-8', rest: '60s' },
              { id: 'e22', name: 'Plank', sets: 3, reps: '30-60s', effort: 'RPE 7-8', rest: '60s' },
            ],
            notes: 'Maintain proper form throughout',
            estimatedDuration: 60,
            dayOfWeek: 'Tuesday',
          },
        ],
      },
    },
  ];

  const _sampleClients: Client[] = [
    {
      id: 'c1',
      name: 'Alex Johnson',
      email: 'alex@example.com',
      assignedPrograms: [],
      assignedNutritionPlans: [],
      joinDate: '2024-07-01',
      lastLoginDate: '2024-08-10T14:30:00.000Z',
      goals: ['Build Strength', 'Improve Form'],
      notes: 'Experienced lifter, prefers compound movements',
      coachNotes: [],
      nutritionPreferences: {
        allergies: ['Nuts'],
        dietaryRestrictions: [],
        preferences: ['High Protein', 'Whole Foods'],
      },
    },
    {
      id: 'c2',
      name: 'Maria Lopez',
      email: 'maria@example.com',
      assignedPrograms: [],
      assignedNutritionPlans: [],
      joinDate: '2024-07-15',
      lastLoginDate: '2024-08-12T09:10:00.000Z',
      goals: ['General Fitness', 'Consistency'],
      notes: 'New to resistance training; prefers guided workouts',
      coachNotes: [],
    },
    {
      id: 'c3',
      name: 'Jordan Smith',
      email: 'jordan@example.com',
      assignedPrograms: [],
      assignedNutritionPlans: [],
      joinDate: '2024-07-20',
      lastLoginDate: '2024-08-05T18:45:00.000Z',
      goals: ['Lose Weight', 'Improve Conditioning'],
      notes: 'Busy schedule—prefers short HIIT sessions',
      coachNotes: [],
    },
  ];

  // Helper function to create deep copy of nutrition template with new IDs
  const _createClientNutritionPlanData = (template: NutritionTemplate): NutritionTemplate => {
    // Legacy path retained for callers that still use it; prefer expander path in assignment
    return {
      ...template,
      id: `nt_client_${Date.now()}_${Math.random()}`,
      days: Array.isArray(template.days)
        ? template.days.map((day, di) => ({
            ...day,
            id: String(day?.id ?? `d_client_${di}_${Math.random()}`),
            meals: Array.isArray(day.meals)
              ? day.meals.map((meal, mi) => ({
                  ...meal,
                  id: String(meal?.id ?? `m_client_${mi}_${Math.random()}`),
                  name: meal?.name || (meal?.type ? meal.type.charAt(0).toUpperCase() + meal.type.slice(1) : 'Meal'),
                  foods: Array.isArray(meal.foods)
                    ? meal.foods.map((food) => ({
                        ...food,
                        foodItem: { ...(food as any).foodItem },
                        nutrition: { ...(food as any).nutrition },
                      }))
                    : [],
                  totalNutrition: { ...(meal as any).totalNutrition },
                }))
              : [],
            dailyNutrition: { ...(day as any).dailyNutrition },
          }))
        : [],
      targetNutrition: { ...template.targetNutrition },
    };
  };

  // Nutrition Management Functions
  const assignNutritionPlanToClients = (
    templateId: string,
    clientIds: string[],
    options: {
      startDate?: string;
      notes?: string;
      allowCustomizations: boolean;
    },
  ) => {
    const template = nutritionTemplates.find((t: any) => String(t.id) === String(templateId));
    if (!template) {
      throw new Error('Template not found');
    }
    // In mock mode, hardwire assignment to Alex Johnson (id 'c1') for reliable demos
    const targetIds = isMockMode() ? ['c1'] : clientIds || [];
    const clientIdsNorm = targetIds.map((id) => String(id).toLowerCase());
    // Helper: derive dayTypes from legacy days when needed
    const deriveDayTypesFromLegacyDays = (days?: any[]) => {
      if (!Array.isArray(days) || !days.length) return [] as any[];
      return days.map((d: any, i: number) => ({
        id: `dt_${i}_${Date.now()}`,
        name: String(d?.dayType || d?.dayName || `Day ${d?.dayNumber || i + 1}`),
        meals: (Array.isArray(d?.meals) ? d.meals : []).map((m: any) => ({
          ...m,
          id: String(m?.id ?? `m_${Math.random()}`),
          name: m?.name || (m?.type ? m.type.charAt(0).toUpperCase() + m.type.slice(1) : 'Meal'),
          foods: (m?.foods || []).map((f: any) => ({
            ...f,
            foodItem: { ...(f?.foodItem || {}), id: String(f?.foodItem?.id ?? `food_${Math.random()}`) },
          })),
        })),
      }));
    };

    // Nutrition is day-type based only: deep-clone dayTypes, no dates/expansion
    const deepCloneDayTypes = (dts?: DayType[]): DayType[] => {
      if (!Array.isArray(dts)) return [];
      return dts.map((dt, i) => ({
        id: String(dt?.id ?? `dt_${i}_${Date.now()}`),
        name: String(dt?.name ?? `Day ${i + 1}`),
        meals: (dt?.meals || []).map((m, j) => ({
          ...m,
          id: String((m as any)?.id ?? `m_${i}_${j}_${Date.now()}`),
          foods: ((m as any)?.foods || []).map((f: any) => ({
            ...f,
            foodItem: { ...(f?.foodItem || {}), id: String(f?.foodItem?.id ?? `food_${Math.random()}`) },
            quantity: Number(f?.quantity ?? 0),
            unit: String(f?.unit ?? 'serving'),
            nutrition: { ...(f?.nutrition || { calories: 0, protein: 0, carbs: 0, fat: 0 }) },
          })),
          totalNutrition: { ...((m as any)?.totalNutrition || { calories: 0, protein: 0, carbs: 0, fat: 0 }) },
        })),
      }));
    };
    const daysSrc = Array.isArray((template as any).days) ? (template as any).days : [];
    const typesLen = Array.isArray((template as any).dayTypes) ? (template as any).dayTypes.length : 0;
    const daysLen = daysSrc.length;
    const rawTypes =
      daysLen && daysLen >= typesLen ? deriveDayTypesFromLegacyDays(daysSrc) : (template as any).dayTypes || [];
    const dayTypes = deepCloneDayTypes(rawTypes as any);

    // Remove existing nutrition plans for these clients first (strict string compare)
    setClientNutritionPlans((prev) =>
      prev.filter((plan) => !clientIdsNorm.includes(String(plan.clientId).toLowerCase())),
    );

    // Create new client nutrition plans using expanded days (authoritative)
    const snapshotAt = new Date().toISOString();
    const planVersion = Date.now();
    const newClientPlans: ClientNutritionPlan[] = clientIdsNorm.map((clientId) => ({
      id: `cnp_${Date.now()}_${clientId}`,
      clientId: String(clientId),
      templateId: String(templateId),
      planData: {
        ...(template as any),
        id: `nt_client_${Date.now()}_${Math.random()}`,
        dayTypes,
        days: undefined,
        // metadata for client view surfacing
        planVersion: planVersion as any,
        ownerCoachId: String(coachId),
      } as any,
      assignedDate: new Date().toISOString(),
      startDate: undefined,
      notes: options.notes,
      allowCustomizations: options.allowCustomizations,
      isCustomized: false,
      compliance: undefined,
      // stamp snapshot time
      planSnapshotAt: snapshotAt as any,
      assignedByCoachId: String(coachId),
    }));

    // Add new plans (ensure replacement)
    setClientNutritionPlans((prev) => {
      const filtered = prev.filter((plan) => !clientIdsNorm.includes(String(plan.clientId).toLowerCase()));
      const next = [...filtered, ...newClientPlans];
      return next;
    });

    // Update client references
    setClients((prev) =>
      prev.map((client) => {
        if (clientIdsNorm.includes(String(client.id).toLowerCase())) {
          const newPlanId = newClientPlans.find((plan) => plan.clientId === client.id)?.id;
          return {
            ...client,
            assignedNutritionPlans: newPlanId ? [newPlanId] : client.assignedNutritionPlans,
          };
        }
        return client;
      }),
    );

    // Coach Updates: push nutrition event per client via addClientEvent (assignment)
    newClientPlans.forEach(async (np) => {
      const dts = np.planData?.dayTypes?.length ?? 0;
      const evt = {
        clientId: String(np.clientId),
        type: 'nutrition_updated',
        summary: `Coach assigned nutrition plan: ${np.planData?.title || 'Meal plan'}`,
        detail: `${dts} day type${dts === 1 ? '' : 's'}`,
        priority: 'low',
      } as any;
      try {
        await addClientEvent(evt);
      } finally {
        setClientEvents((prev) => [
          { id: `evt_${Date.now()}_${Math.random()}`, createdAt: new Date().toISOString(), ...evt },
          ...prev,
        ]);
        try {
          localStorage.setItem(
            'coachd-client-events',
            JSON.stringify([
              { id: `evt_${Date.now()}_${Math.random()}`, createdAt: new Date().toISOString(), ...evt },
              ...clientEvents,
            ]),
          );
        } catch {
          /* ignored */
        }
      }
    });
  };

  // Load data from localStorage on mount (clients optionally from centralized mocks)
  useEffect(() => {
    // Clear client/nutrition copies that earlier builds wrote in production (never read back here).
    if (!isMockMode()) purgeServerDataMirror();

    const savedWorkoutTemplates = localStorage.getItem('coach-workout-templates');
    const savedClientPrograms = localStorage.getItem('coach-client-programs');
    const savedWorkouts = localStorage.getItem('coach-saved-workouts');
    const savedNutritionTemplates = localStorage.getItem('coach-nutrition-templates');
    const savedClientNutritionPlans = localStorage.getItem('coach-client-nutrition-plans');
    const savedFoodItems = localStorage.getItem('coach-food-items');
    const _savedClients = localStorage.getItem('coach-clients');
    const savedPrograms = localStorage.getItem('coachd-programs');
    const savedClientProgramsV2 = localStorage.getItem('coachd-client-programs');
    const savedClientEvents = localStorage.getItem('coachd-client-events');
    const savedProgramTemplates = localStorage.getItem('coachd-program-templates');

    // Load workout templates (mock only)
    if (isMockMode()) {
      if (savedWorkoutTemplates) {
        setTemplates(JSON.parse(savedWorkoutTemplates));
      } else {
        setTemplates(sampleWorkoutTemplates);
      }
    } else {
      setTemplates([]);
    }

    // Load client programs
    if (savedClientPrograms) {
      setClientPrograms(JSON.parse(savedClientPrograms));
    }

    // Load saved workouts (always; migrate older shapes safely)
    if (savedWorkouts) {
      try {
        const parsed = JSON.parse(savedWorkouts);
        const normalized: SavedWorkout[] = Array.isArray(parsed)
          ? parsed.map(migrateSavedWorkout).map((w) => ({ ...w, modality: w.modality ?? inferModality(w.name) }))
          : [];
        setSavedWorkouts(normalized);
      } catch {
        setSavedWorkouts([]);
      }
    } else {
      setSavedWorkouts([]);
    }

    // Load nutrition templates (mock only)
    if (isMockMode()) {
      if (savedNutritionTemplates) {
        const parsedTemplates = JSON.parse(savedNutritionTemplates);
        // Step 1: migrate days to ensure dayType normalization
        const migrated = parsedTemplates.map(migrateNutritionTemplate);
        // Step 2: derive dayTypes/cycleLength and coerce IDs to strings
        const normalized = migrated.map((t: any) => {
          const tmpl: NutritionTemplate = {
            ...t,
            id: String(t?.id ?? `nt_${Date.now()}_${Math.random()}`),
            days: Array.isArray(t?.days)
              ? t.days.map((d: any, idx: number) => ({
                  id: String(d?.id ?? `d_${idx}_${Math.random()}`),
                  dayNumber: Number(d?.dayNumber ?? idx + 1),
                  dayName: String(d?.dayName ?? `Day ${idx + 1}`),
                  dayType: d?.dayType ? String(d.dayType) : undefined,
                  meals: Array.isArray(d?.meals)
                    ? d.meals.map((m: any, mi: number) => ({
                        id: String(m?.id ?? `m_${mi}_${Math.random()}`),
                        name: String(m?.name ?? 'Meal'),
                        type: m?.type ?? 'custom',
                        notes: m?.notes ? String(m.notes) : undefined,
                        foods: Array.isArray(m?.foods)
                          ? m.foods.map((f: any) => ({
                              foodItem: {
                                id: String(f?.foodItem?.id ?? `food_${Math.random()}`),
                                name: String(f?.foodItem?.name ?? 'Unknown Food'),
                                calories: Number(f?.foodItem?.calories ?? f?.nutrition?.calories ?? 0),
                                protein: Number(f?.foodItem?.protein ?? f?.nutrition?.protein ?? 0),
                                carbs: Number(f?.foodItem?.carbs ?? f?.nutrition?.carbs ?? 0),
                                fat: Number(f?.foodItem?.fat ?? f?.nutrition?.fat ?? 0),
                                serving_size: String(f?.foodItem?.serving_size ?? '1 serving'),
                                category: String(f?.foodItem?.category ?? 'Other'),
                              },
                              quantity: Number(f?.quantity ?? 1),
                              unit: String(f?.unit ?? 'serving'),
                              nutrition: {
                                calories: Number(f?.nutrition?.calories ?? 0),
                                protein: Number(f?.nutrition?.protein ?? 0),
                                carbs: Number(f?.nutrition?.carbs ?? 0),
                                fat: Number(f?.nutrition?.fat ?? 0),
                              },
                            }))
                          : [],
                        totalNutrition: m?.totalNutrition ?? { calories: 0, protein: 0, carbs: 0, fat: 0 },
                      }))
                    : [],
                  dailyNutrition: d?.dailyNutrition ?? { calories: 0, protein: 0, carbs: 0, fat: 0 },
                }))
              : [],
          } as NutritionTemplate;

          // derive dayTypes from legacy days if absent/empty
          if (!Array.isArray(tmpl.dayTypes) || tmpl.dayTypes.length === 0) {
            const groups = new Map<string, any[]>();
            (tmpl.days || []).forEach((day) => {
              const key = String(day?.dayType || day?.dayName || `Day ${day?.dayNumber}`);
              if (!groups.has(key)) groups.set(key, []);
              groups.get(key)!.push(day);
            });
            const dayTypes = Array.from(groups.entries()).map(([name, days], idx) => ({
              id: `dt_${idx}_${Math.random()}`,
              name,
              meals: (days[0]?.meals || []).map((m: any) => ({
                ...m,
                id: String(m?.id ?? `m_${Math.random()}`),
                name: m?.name || (m?.type ? m.type.charAt(0).toUpperCase() + m.type.slice(1) : 'Meal'),
                foods: (m?.foods || []).map((f: any) => ({
                  ...f,
                  foodItem: { ...f.foodItem, id: String(f?.foodItem?.id ?? `food_${Math.random()}`) },
                })),
              })),
            }));
            return { ...tmpl, dayTypes, cycleLength: dayTypes.length } as NutritionTemplate;
          }

          // ensure ids are strings in provided dayTypes
          const coercedTypes = (tmpl.dayTypes || []).map((dt: any, idx: number) => ({
            id: String(dt?.id ?? `dt_${idx}_${Math.random()}`),
            name: String(dt?.name ?? `Type ${idx + 1}`),
            meals: Array.isArray(dt?.meals)
              ? dt.meals.map((m: any) => ({
                  id: String(m?.id ?? `m_${Math.random()}`),
                  name: String(m?.name ?? 'Meal'),
                  type: m?.type ?? 'custom',
                  notes: m?.notes ? String(m.notes) : undefined,
                  foods: Array.isArray(m?.foods)
                    ? m.foods.map((f: any) => ({
                        foodItem: {
                          id: String(f?.foodItem?.id ?? `food_${Math.random()}`),
                          name: String(f?.foodItem?.name ?? 'Unknown Food'),
                          calories: Number(f?.foodItem?.calories ?? f?.nutrition?.calories ?? 0),
                          protein: Number(f?.foodItem?.protein ?? f?.nutrition?.protein ?? 0),
                          carbs: Number(f?.foodItem?.carbs ?? f?.nutrition?.carbs ?? 0),
                          fat: Number(f?.foodItem?.fat ?? f?.nutrition?.fat ?? 0),
                          serving_size: String(f?.foodItem?.serving_size ?? '1 serving'),
                          category: String(f?.foodItem?.category ?? 'Other'),
                        },
                        quantity: Number(f?.quantity ?? 1),
                        unit: String(f?.unit ?? 'serving'),
                        nutrition: {
                          calories: Number(f?.nutrition?.calories ?? 0),
                          protein: Number(f?.nutrition?.protein ?? 0),
                          carbs: Number(f?.nutrition?.carbs ?? 0),
                          fat: Number(f?.nutrition?.fat ?? 0),
                        },
                      }))
                    : [],
                  totalNutrition: m?.totalNutrition ?? { calories: 0, protein: 0, carbs: 0, fat: 0 },
                }))
              : [],
          }));
          const cycleLength = Number(tmpl.cycleLength || coercedTypes.length) || coercedTypes.length;
          return { ...tmpl, dayTypes: coercedTypes, cycleLength } as NutritionTemplate;
        });

        // Merge with sample if needed
        const hasTestTemplate = normalized.some((t: NutritionTemplate) => t.id === 'test-template');
        if (!hasTestTemplate) {
          const testTemplate = sampleNutritionTemplates.find((t) => t.id === 'test-template');
          const withDerived = (testTemplate ? [...normalized, testTemplate] : normalized).map((t: any) => {
            if (!Array.isArray(t?.dayTypes) || t.dayTypes.length === 0) {
              const groups = new Map<string, any[]>();
              (t.days || []).forEach((day: any) => {
                const key = String(day?.dayType || day?.dayName || `Day ${day?.dayNumber}`);
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(day);
              });
              const dayTypes = Array.from(groups.entries()).map(([name, days], idx) => ({
                id: `dt_${idx}_${Math.random()}`,
                name,
                meals: (days[0]?.meals || []).map((m: any) => ({
                  ...m,
                  id: String(m?.id ?? `m_${Math.random()}`),
                  foods: (m?.foods || []).map((f: any) => ({
                    ...f,
                    foodItem: { ...f.foodItem, id: String(f?.foodItem?.id ?? `food_${Math.random()}`) },
                  })),
                })),
              }));
              return { ...t, dayTypes, cycleLength: dayTypes.length };
            }
            return t;
          });
          setNutritionTemplates(withDerived);
          try {
            const isDev = (import.meta as any).env?.DEV === true;
            if (isDev && withDerived[0]) {
              // eslint-disable-next-line no-console
              console.debug('[nutrition] first template normalized', {
                id: withDerived[0].id,
                dayTypes: Array.isArray(withDerived[0].dayTypes) ? withDerived[0].dayTypes.length : 0,
                cycleLength: withDerived[0].cycleLength,
              });
            }
          } catch {
            /* ignored */
          }
        } else {
          const mapped = normalized.map((t: any) => {
            if (!Array.isArray(t?.dayTypes) || t.dayTypes.length === 0) {
              const groups = new Map<string, any[]>();
              (t.days || []).forEach((day: any) => {
                const key = String(day?.dayType || day?.dayName || `Day ${day?.dayNumber}`);
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push(day);
              });
              const dayTypes = Array.from(groups.entries()).map(([name, days], idx) => ({
                id: `dt_${idx}_${Math.random()}`,
                name,
                meals: (days[0]?.meals || []).map((m: any) => ({
                  ...m,
                  id: String(m?.id ?? `m_${Math.random()}`),
                  foods: (m?.foods || []).map((f: any) => ({
                    ...f,
                    foodItem: { ...f.foodItem, id: String(f?.foodItem?.id ?? `food_${Math.random()}`) },
                  })),
                })),
              }));
              return { ...t, dayTypes, cycleLength: dayTypes.length };
            }
            return t;
          });
          setNutritionTemplates(mapped);
          try {
            const isDev = (import.meta as any).env?.DEV === true;
            if (isDev && mapped[0]) {
              // eslint-disable-next-line no-console
              console.debug('[nutrition] first template normalized', {
                id: mapped[0].id,
                dayTypes: Array.isArray(mapped[0].dayTypes) ? mapped[0].dayTypes.length : 0,
                cycleLength: mapped[0].cycleLength,
              });
            }
          } catch {
            /* ignored */
          }
        }
      } else {
        setNutritionTemplates(sampleNutritionTemplates);
      }
    } else {
      // Load from Worker in non-mock mode (BFF pattern for httpOnly cookie auth)
      const loadDbTemplates = async () => {
        try {
          const templatesRes = await csrfFetch('/api/coach/nutrition-templates', { credentials: 'include' });
          if (templatesRes.ok) {
            const { items } = await templatesRes.json();
            setNutritionTemplates(items || []);
          } else {
            console.error('[nutrition-templates] Worker endpoint failed:', templatesRes.status);
            setNutritionTemplates([]);
          }
        } catch (error) {
          console.warn('Failed to load nutrition templates from Worker:', error);
          setNutritionTemplates([]);
        }
      };
      loadDbTemplates();
    }

    if (isMockMode()) {
      if (savedClientNutritionPlans) {
        const parsedPlans = JSON.parse(savedClientNutritionPlans);
        const migratedPlans = parsedPlans.map((plan: any) => {
          const p = { ...plan };
          p.id = String(p.id ?? `cnp_${Math.random()}`);
          p.clientId = String(p.clientId ?? '');
          p.templateId = String(p.templateId ?? '');
          const pd = migrateNutritionTemplate(p.planData);
          // Derive dayTypes and drop days
          const deriveDayTypesFromLegacyDays = (days?: any[]) => {
            if (!Array.isArray(days) || !days.length) return [] as any[];
            const map = new Map<string, any[]>();
            for (const d of days) {
              const key = String(d?.dayType || d?.dayName || `Day ${d?.dayNumber || '?'}`);
              if (!map.has(key)) map.set(key, []);
              const have = map.get(key)!;
              if (have.length === 0 && Array.isArray(d?.meals) && d.meals.length) {
                map.set(
                  key,
                  d.meals.map((m: any) => ({
                    ...m,
                    id: String(m?.id ?? `m_${Math.random()}`),
                    foods: (m?.foods || []).map((f: any) => ({
                      ...f,
                      foodItem: { ...(f?.foodItem || {}), id: String(f?.foodItem?.id ?? `food_${Math.random()}`) },
                    })),
                  })),
                );
              }
            }
            return Array.from(map.entries()).map(([name, meals], i) => ({ id: `dt_${i}_${Date.now()}`, name, meals }));
          };
          const dayTypes =
            Array.isArray(pd?.dayTypes) && pd.dayTypes.length ? pd.dayTypes : deriveDayTypesFromLegacyDays(pd?.days);
          // Preserve existing days if present so nutrition "View details" has data after navigation
          p.planData = {
            ...(pd as any),
            dayTypes,
            days: Array.isArray((pd as any)?.days) ? (pd as any).days : undefined,
          };
          return p;
        });
        setClientNutritionPlans(migratedPlans);
      }
    } else {
      setClientNutritionPlans([]);
    }

    if (savedFoodItems) {
      try {
        const parsed = JSON.parse(savedFoodItems);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setFoodItems(parsed);
        } else {
          // Seed sensible defaults when the saved list is empty
          setFoodItems(sampleFoodItems);
        }
      } catch {
        // Corrupt JSON -> reset to defaults
        setFoodItems(sampleFoodItems);
      }
    } else {
      setFoodItems(sampleFoodItems);
    }

    // Seed program templates for mock mode
    if (savedProgramTemplates) {
      setProgramTemplates(JSON.parse(savedProgramTemplates));
    } else {
      setProgramTemplates(mockProgramTemplates);
    }

    // Dashboard domain restore (only if not already seeded in loadClients)
    if (savedPrograms) {
      try {
        const parsed = JSON.parse(savedPrograms);
        const normalized = Array.isArray(parsed)
          ? parsed.map((p: any) => ({
              ...p,
              days: Array.isArray(p.days)
                ? p.days.map((d: any) => ({
                    ...d,
                    workoutId: d?.workoutId != null ? String(d.workoutId) : undefined,
                    exerciseSnapshot: Array.isArray(d?.exerciseSnapshot)
                      ? d.exerciseSnapshot
                      : Array.isArray(d?.exercises)
                        ? d.exercises
                        : undefined,
                  }))
                : [],
            }))
          : parsed;
        setPrograms(normalized);
      } catch {
        setPrograms(JSON.parse(savedPrograms));
      }
    }
    if (savedClientProgramsV2) setClientProgramsV2(JSON.parse(savedClientProgramsV2));
    if (savedClientEvents) setClientEvents(JSON.parse(savedClientEvents));
    if (savedProgramTemplates) setProgramTemplates(JSON.parse(savedProgramTemplates));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount for localStorage restoration

  // Separate useEffect for data loading that depends on auth state
  useEffect(() => {
    // Load clients when auth is ready - with parallel loading for coach data
    if (!authReady) {
      return;
    }

    if (userType === 'coach') {
      // Optimized parallel loading with error isolation for coach
      (async () => {
        setCoachDataLoading(true);
        setCoachDataReady(false);

        // Phase 1: Load clients FIRST (cache warming/last-seen need clients)
        try {
          await loadClients();
        } catch (error) {
          console.error('[parallel-load] Failed to load clients:', error);
        }

        // Phase 2: Load everything else in parallel (now that clients are loaded)
        const _results = await Promise.allSettled([
          loadNutritionAssignments(),
          warmClientAssignmentsCache(),
          loadLastSeenData(),
        ]);

        // Give React time to batch state updates and complete initial render cycle
        // State updates from async functions are batched and may not be synchronous
        // A longer delay ensures components render with data, not empty defaults
        await new Promise((resolve) => setTimeout(resolve, 150));

        setCoachDataLoading(false);
        setCoachDataReady(true);
      })();
    } else {
      // Client mode - just load clients
      loadClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, userType]); // loadClients and other functions are stable callbacks defined below

  // Load nutrition templates from database
  useEffect(() => {
    let cancelled = false;
    async function loadTemplates() {
      try {
        // Load via Worker (BFF pattern for httpOnly cookie auth)
        const templatesRes = await csrfFetch('/api/coach/nutrition-templates', { credentials: 'include' });
        if (!templatesRes.ok) {
          throw new Error(`Worker endpoint failed: ${templatesRes.status}`);
        }
        const { items: rows } = await templatesRes.json();
        // Convert database format to expected format
        const converted = rows.map((row: any) => {
          return {
            id: row.id,
            title: row.title,
            ownerCoachId: row.coach_id,
            description: row.notes || '', // Map 'notes' to 'description' for UI
            tags: row.tags || [],
            difficulty: (row.difficulty || 'Intermediate') as 'Beginner' | 'Intermediate' | 'Advanced',
            duration: row.duration || 'Ongoing',
            thumbnail: row.thumbnail || undefined,
            createdDate: row.created_at.split('T')[0],
            updatedAt: row.updated_at, // Add updated_at for list view
            assignedClients: 0,
            isPublic: row.is_public || false,
            targetNutrition: {
              dailyCalories: row.target_calories || 2000,
              proteinPercentage: row.target_protein_pct || 25,
              carbsPercentage: row.target_carbs_pct || 45,
              fatPercentage: row.target_fat_pct || 30,
            },
            dayTypes:
              row.template_snapshot?.days?.map((day: any, idx: number) => {
                return {
                  id: `dt_${idx}`,
                  name: day.label || `Day ${idx + 1}`,
                  meals:
                    day.meals?.map((meal: any, mi: number) => ({
                      id: `m_${mi}`,
                      name: meal.label || (meal.type ? meal.type.charAt(0).toUpperCase() + meal.type.slice(1) : 'Meal'),
                      type: meal.type || 'custom', // Use saved type from snapshot instead of hardcoding 'custom'
                      foods:
                        meal.entries?.map((entry: any, _ei: number) => ({
                          foodItem: {
                            id: entry.food_id,
                            name: entry.name,
                            brand: entry.brand || undefined,
                            calories: entry.kcal,
                            protein: entry.protein_g,
                            carbs: entry.carbs_g,
                            fat: entry.fat_g,
                            serving_size: entry.portion_label,
                            category: 'Other',
                          },
                          quantity: entry.quantity,
                          unit: entry.portion_label,
                          portion_id: entry.portion_id || undefined,
                          portion_label: entry.portion_label,
                          portion_grams: entry.portion_grams,
                          total_kcal: entry.kcal,
                          total_protein_g: entry.protein_g,
                          total_carbs_g: entry.carbs_g,
                          total_fat_g: entry.fat_g,
                          nutrition: {
                            calories: entry.kcal,
                            protein: entry.protein_g,
                            carbs: entry.carbs_g,
                            fat: entry.fat_g,
                          },
                        })) || [],
                      totalNutrition: { calories: 0, protein: 0, carbs: 0, fat: 0 },
                    })) || [],
                };
              }) || [],
            cycleLength: row.template_snapshot?.days?.length || 1, // Use template_snapshot
          };
        });

        if (!cancelled) setNutritionTemplates(converted as any);
      } catch (e) {
        console.error('[nutrition] failed to load templates', e);
        if (!cancelled) setNutritionTemplates([]); // safe fallback
      }
    }
    if (!isMockMode()) {
      loadTemplates();
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // Load coach programs, exercises, and workouts from database (pre-loaded like nutrition)
  useEffect(() => {
    let cancelled = false;
    async function loadCoachData() {
      if (isMockMode()) {
        if (!cancelled) setCoachWorkoutsLoaded(true);
        return;
      }

      try {
        if (!cancelled) {
          setCoachWorkoutsLoaded(false);
        }
        const ownerId = currentCoach?.id;
        if (!ownerId) {
          console.warn('[coach-data] No currentCoach.id available, skipping data load');
          return;
        }

        // Load programs via Worker (BFF pattern for httpOnly cookie auth)
        try {
          const programsRes = await csrfFetch('/api/coach/programs', { credentials: 'include' });
          if (programsRes.ok) {
            const json = await programsRes.json();
            // Handle new standardized envelope: { ok, data: { items }, requestId }
            const programs = json.data?.items ?? json.items ?? [];
            if (!cancelled) {
              setCoachPrograms(programs || []);

              // Map programs to template format expected by ProgramBuilder
              const templatesFormatted = (programs || []).map((p: any) => ({
                id: p.id,
                title: p.name, // Map name -> title
                name: p.name,
                description: p.notes || '',
                notes: p.notes,
                tags: Array.isArray(p.tags) ? p.tags : [],
                difficulty: p.level ? p.level.charAt(0).toUpperCase() + p.level.slice(1) : 'Beginner',
                level: p.level,
                duration: `${p.total_weeks || 1} week${p.total_weeks !== 1 ? 's' : ''}`,
                total_weeks: p.total_weeks,
                createdDate: p.created_at || new Date().toISOString(),
                assignedClients: 0,
                isPublic: p.is_public || false,
                days: p.days || [], // Keep days array from database
                weeks: p.weeks || {}, // Legacy format if exists
              }));

              setTemplates(templatesFormatted);
            }
          } else {
            console.error('[coach-data] Failed to load programs from Worker:', programsRes.status);
            if (!cancelled) {
              setCoachPrograms([]);
              setTemplates([]);
            }
          }
        } catch (err) {
          console.error('[coach-data] Error loading programs:', err);
          if (!cancelled) setCoachPrograms([]);
        }

        // Load workouts via Worker (BFF pattern for httpOnly cookie auth)
        try {
          const workoutsRes = await csrfFetch('/api/coach/workouts', { credentials: 'include' });
          if (workoutsRes.ok) {
            const { items: rawWorkouts } = await workoutsRes.json();

            // Map the raw Worker response to extract itemsCount from workout_exercises
            const workouts = (rawWorkouts || []).map((w: any) => ({
              ...w,
              itemsCount: Array.isArray(w.workout_exercises) ? (w.workout_exercises[0]?.count ?? 0) : 0,
            }));

            if (!cancelled) {
              setCoachWorkouts(workouts);
              setCoachWorkoutsLoaded(true);
            }
          } else {
            console.error('[coach-data] Failed to load workouts from Worker:', workoutsRes.status);
            if (!cancelled) {
              setCoachWorkouts([]);
              setCoachWorkoutsLoaded(true);
            }
          }
        } catch (err) {
          console.error('[coach-data] Error loading workouts:', err);
          if (!cancelled) {
            setCoachWorkouts([]);
            setCoachWorkoutsLoaded(true);
          }
        }

        // Load client program assignments
        try {
          const sb = (await import('@/lib/supabase')).getSupabase();

          if (sb) {
            const { data: assignments, error } = await sb
              .from('client_programs')
              .select('id, client_id, program_id, status, created_at')
              .eq('owner_coach_id', ownerId)
              .order('created_at', { ascending: false });

            if (!error && assignments) {
              const assignmentsV2 = assignments.map((a: any) => ({
                clientId: String(a.client_id),
                programId: String(a.program_id),
                status: a.status || 'active',
              }));

              if (!cancelled) {
                setClientProgramsV2(assignmentsV2);
              }
            } else {
              console.error('[coach-data] Failed to load client programs:', error);
              if (!cancelled) setClientProgramsV2([]);
            }
          }
        } catch (err) {
          console.error('[coach-data] Error loading client programs:', err);
          if (!cancelled) setClientProgramsV2([]);
        }

        // Load meals via Worker (BFF pattern for httpOnly cookie auth)
        try {
          const mealsRes = await csrfFetch('/api/coach/meals', { credentials: 'include' });
          if (mealsRes.ok) {
            const { items: meals } = await mealsRes.json();
            if (!cancelled) {
              setCoachMeals(meals || []);
            }
          } else {
            console.error('[coach-data] Failed to load meals from Worker:', mealsRes.status);
            if (!cancelled) setCoachMeals([]);
          }
        } catch (err) {
          console.error('[coach-data] Error loading meals:', err);
          if (!cancelled) setCoachMeals([]);
        }
      } catch (e) {
        console.error('[coach-data] failed to load programs/exercises/workouts/meals', e);
        if (!cancelled) {
          setCoachPrograms([]);
          setCoachWorkouts([]);
          setCoachWorkoutsLoaded(true);
          setCoachMeals([]);
        }
      }
    }

    loadCoachData();

    return () => {
      cancelled = true;
    };
  }, [currentCoach?.id]);

  // Auto-migrate localStorage meals to Supabase on first load
  React.useEffect(() => {
    if (isMockMode()) return;
    if (!currentCoach?.id) return;

    const runAutoMigration = async () => {
      try {
        const { autoMigrateMeals } = await import('@/data/storage/meals/autoMigrate');
        await autoMigrateMeals();
      } catch (error) {
        console.error('Failed to auto-migrate meals:', error);
      }
    };

    runAutoMigration();
  }, [currentCoach?.id]);

  // Refresh pre-loaded data when programs/workouts change (exercises: see exerciseLibraryStore)
  // Stable listeners with useCallback to avoid duplicates
  const handleProgramsChanged = React.useCallback(async () => {
    if (isMockMode()) return;
    const ownerId = currentCoach?.id;
    if (!ownerId) return;

    try {
      const ProgramsMod = await import('@/data/storage/programs/adapter');
      const programs = await ProgramsMod.listProgramsForCoach(ownerId, { includePublic: true, limit: 50 });
      setCoachPrograms(programs);
    } catch (e) {
      console.error('[coach-data] failed to refresh programs', e);
    }
  }, [currentCoach?.id]);

  const handleWorkoutsChanged = React.useCallback(async () => {
    if (isMockMode()) return;
    const ownerId = currentCoach?.id;
    if (!ownerId) {
      return;
    }

    try {
      // Invalidate workouts cache to ensure fresh data
      const { dataCache } = await import('@/utils/cache');
      dataCache.invalidate('workouts:');
      const WorkoutsMod = await import('@/data/storage/workouts/adapter');
      const workouts = await WorkoutsMod.listWorkoutsForCoach(ownerId, { includePublic: true, limit: 50 });
      setCoachWorkouts(workouts);
    } catch (e) {
      console.error('[coach-data] failed to refresh workouts', e);
    }
  }, [currentCoach?.id]);

  const handleAssignmentsChanged = React.useCallback(async () => {
    if (isMockMode()) return;
    const ownerId = currentCoach?.id;
    if (!ownerId) return;

    try {
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (!sb) return;

      const { data: assignments } = await sb
        .from('client_programs')
        .select('id, client_id, program_id, status, created_at')
        .eq('owner_coach_id', ownerId)
        .order('created_at', { ascending: false });

      if (assignments) {
        const assignmentsV2 = assignments.map((a: any) => ({
          clientId: String(a.client_id),
          programId: String(a.program_id),
          status: a.status || 'active',
        }));

        setClientProgramsV2(assignmentsV2);
      }
    } catch (e) {
      console.error('[coach-data] failed to refresh assignments', e);
    }
  }, [currentCoach?.id]);

  useEffect(() => {
    window.addEventListener('programs:changed', handleProgramsChanged);
    window.addEventListener('workouts:changed', handleWorkoutsChanged);
    window.addEventListener('assignments:changed', handleAssignmentsChanged);

    return () => {
      window.removeEventListener('programs:changed', handleProgramsChanged);
      window.removeEventListener('workouts:changed', handleWorkoutsChanged);
      window.removeEventListener('assignments:changed', handleAssignmentsChanged);
    };
  }, [handleProgramsChanged, handleWorkoutsChanged, handleAssignmentsChanged]);

  // Migration/backfill for coach IDs and adapter persistence - with stability
  useEffect(() => {
    let cancelled = false;
    const cid = String(coachId);

    const migrate = async () => {
      if (cancelled) return;

      try {
        const cps = await appStore.getClientPrograms();
        if (cancelled) return;
        let changed = false;
        const patched = (Array.isArray(cps) ? cps : []).map((r: any) =>
          r?.assignedByCoachId ? r : ((changed = true), { ...r, assignedByCoachId: cid }),
        );
        if (changed && !cancelled) await appStore.setClientPrograms(patched);
      } catch (e) {
        if (!cancelled) console.warn('[coachId migration] client programs failed:', e);
      }

      if (cancelled) return;

      try {
        const tpls = await appStore.getProgramTemplates();
        if (cancelled) return;
        let changed = false;
        const patched = (Array.isArray(tpls) ? tpls : []).map((r: any) =>
          r?.ownerCoachId ? r : ((changed = true), { ...r, ownerCoachId: cid }),
        );
        if (changed && !cancelled) await appStore.setProgramTemplates(patched);
      } catch (e) {
        if (!cancelled) console.warn('[coachId migration] program templates failed:', e);
      }

      if (cancelled) return;

      try {
        const plans = await appStore.getClientNutritionPlans();
        if (cancelled) return;
        let changed = false;
        const patched = (Array.isArray(plans) ? plans : []).map((r: any) =>
          r?.assignedByCoachId ? r : ((changed = true), { ...r, assignedByCoachId: cid }),
        );
        if (changed && !cancelled) await appStore.setClientNutritionPlans(patched);
      } catch (e) {
        if (!cancelled) console.warn('[coachId migration] nutrition plans failed:', e);
      }

      if (cancelled) return;

      try {
        const tpls = await appStore.getNutritionTemplates();
        if (cancelled) return;
        let changed = false;
        const patched = (Array.isArray(tpls) ? tpls : []).map((r: any) =>
          r?.ownerCoachId ? r : ((changed = true), { ...r, ownerCoachId: cid }),
        );
        if (changed && !cancelled) await appStore.setNutritionTemplates(patched);
      } catch (e) {
        if (!cancelled) console.warn('[coachId migration] nutrition templates failed:', e);
      }

      if (cancelled) return;

      try {
        const evts = await appStore.getClientEvents();
        if (cancelled) return;
        let changed = false;
        const patched = (Array.isArray(evts) ? evts : []).map((e: any) =>
          e?.coachId ? e : ((changed = true), { ...e, coachId: cid }),
        );
        if (changed && !cancelled) await appStore.setClientEvents(patched);
      } catch (e) {
        if (!cancelled) console.warn('[coachId migration] client events failed:', e);
      }

      try {
        if ((import.meta as any).env?.DEV && !cancelled) {
          console.debug('[coachId migration] complete', cid);
        }
      } catch {
        /* ignored */
      }
    };

    // Debounce the migration to prevent rapid calls during HMR
    const timeoutId = setTimeout(migrate, 200);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [coachId]);

  // Save to localStorage whenever state changes - with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coach-workout-templates', JSON.stringify(templates));
      } catch (e) {
        console.warn('[localStorage] Failed to save templates:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [templates]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coach-client-programs', JSON.stringify(clientPrograms));
      } catch (e) {
        console.warn('[localStorage] Failed to save client programs:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [clientPrograms]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coach-saved-workouts', JSON.stringify(savedWorkouts));
      } catch (e) {
        console.warn('[localStorage] Failed to save workouts:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [savedWorkouts]);

  useEffect(() => {
    if (!isMockMode()) return;
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coach-nutrition-templates', JSON.stringify(nutritionTemplates));
      } catch (e) {
        console.warn('[localStorage] Failed to save nutrition templates:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [nutritionTemplates]);

  useEffect(() => {
    if (!isMockMode()) return;
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coach-client-nutrition-plans', JSON.stringify(clientNutritionPlans));
      } catch (e) {
        console.warn('[localStorage] Failed to save nutrition plans:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [clientNutritionPlans]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coach-food-items', JSON.stringify(foodItems));
      } catch (e) {
        console.warn('[localStorage] Failed to save food items:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [foodItems]);

  // Mock mode only: the client list embeds coachNotes and is never read back from storage.
  // In production this copy outlived sign-out on shared devices (see src/lib/localDataMirror.ts).
  useEffect(() => {
    if (!isMockMode()) return;
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coach-clients', JSON.stringify(clients));
      } catch (e) {
        console.warn('[localStorage] Failed to save clients:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [clients]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coachd-programs', JSON.stringify(programs));
      } catch (e) {
        console.warn('[localStorage] Failed to save programs:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [programs]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coachd-client-programs', JSON.stringify(clientProgramsV2));
      } catch (e) {
        console.warn('[localStorage] Failed to save client programs v2:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [clientProgramsV2]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coachd-client-events', JSON.stringify(clientEvents));
      } catch (e) {
        console.warn('[localStorage] Failed to save client events:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [clientEvents]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem('coachd-program-templates', JSON.stringify(programTemplates));
      } catch (e) {
        console.warn('[localStorage] Failed to save program templates:', e);
      }
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [programTemplates]);

  // Live-sync nutrition-related state across tabs/windows (mock/local only)
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      try {
        if (e.key === 'coach-client-nutrition-plans') {
          setClientNutritionPlans(JSON.parse(e.newValue || '[]') || []);
        }
        if (e.key === 'coachd-programs') {
          try {
            setPrograms(JSON.parse(e.newValue || '{}') || ({} as any));
          } catch {
            /* ignored */
          }
        }
        if (e.key === 'coachd-client-programs') {
          try {
            setClientProgramsV2(JSON.parse(e.newValue || '[]') || ([] as any));
          } catch {
            /* ignored */
          }
        }
        if (e.key === 'coach-nutrition-templates') {
          setNutritionTemplates(JSON.parse(e.newValue || '[]') || []);
        }
        if (e.key === 'coach-food-items') {
          setFoodItems(JSON.parse(e.newValue || '[]') || []);
        }
        if (e.key === 'coachd-client-events') {
          try {
            setClientEvents(JSON.parse(e.newValue || '[]') || []);
          } catch {
            /* ignored */
          }
        }
        if (e.key === 'coachd-client-programs') {
          // keep programs-v2 wired to selector consumers
          try {
            setClientProgramsV2(JSON.parse(e.newValue || '[]') || ([] as any));
          } catch {
            /* ignored */
          }
        }
      } catch {
        /* ignored */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Program Management Functions
  const saveTemplate = (template: WorkoutTemplate) => {
    if (!requireCoach('saveTemplate')) return;
    setTemplates((prev) => {
      // Check if template already exists, if so update it, otherwise add it
      const existingIndex = prev.findIndex((t) => t.id === template.id);
      if (existingIndex >= 0) {
        // Update existing template
        const updated = [...prev];
        updated[existingIndex] = { ...template, ownerCoachId: str(coachId) } as any;
        try {
          localStorage.setItem('coach-workout-templates', JSON.stringify(updated));
        } catch {
          /* ignored */
        }
        return updated;
      } else {
        // Add new template
        const out = [...prev, { ...template, ownerCoachId: str(coachId) } as any];
        try {
          localStorage.setItem('coach-workout-templates', JSON.stringify(out));
        } catch {
          /* ignored */
        }
        return out;
      }
    });
    // Also seed v2 programTemplates domain for future assignments
    try {
      const base = Array.isArray(programTemplates) ? programTemplates : [];
      const exists = base.some((t: any) => t?.id === template.id);
      const next = exists
        ? base.map((t: any) =>
            t.id === template.id
              ? {
                  ...t,
                  name: template.title,
                  totalWeeks: parseInt(String(template.duration).match(/(\d+)/)?.[1] || '1', 10) || 1,
                }
              : t,
          )
        : [
            ...base,
            {
              id: template.id,
              name: template.title,
              totalWeeks: parseInt(String(template.duration).match(/(\d+)/)?.[1] || '1', 10) || 1,
            },
          ];
      setProgramTemplates(next);
      try {
        localStorage.setItem('coachd-program-templates', JSON.stringify(next));
      } catch {
        /* ignored */
      }
    } catch {
      /* ignored */
    }
  };

  const deleteTemplate = (templateId: string) => {
    if (!requireCoach('deleteTemplate')) return;
    setTemplates((prev) => prev.filter((t) => t.id !== templateId));
    // Also remove any client programs using this template
    setClientPrograms((prev) => prev.filter((program) => program.templateId !== templateId));
  };

  // Helper function to create deep copy of workout template with new IDs
  const createClientProgramData = (template: WorkoutTemplate): WorkoutTemplate => {
    return {
      ...template,
      id: `wt_client_${Date.now()}_${Math.random()}`, // New ID for client's copy
      weeks: Object.fromEntries(
        Object.entries(template.weeks).map(([weekKey, days]) => [
          weekKey,
          days.map((day) => ({
            ...day,
            id: `d_client_${Date.now()}_${Math.random()}`, // New day ID
            exercises: day.exercises.map((exercise) => ({
              ...exercise,
              id: `e_client_${Date.now()}_${Math.random()}`, // New exercise ID
            })),
          })),
        ]),
      ),
    };
  };

  const assignProgramToClients = (
    templateId: string,
    clientIds: string[],
    options: {
      startDate?: string;
      notes?: string;
      allowCustomizations: boolean;
    },
  ) => {
    if (!requireCoach('assignProgramToClients')) return;
    const template = templates.find((t) => t.id === templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    const normIds = clientIds.map((id) => String(id));
    const startDate = options.startDate || new Date().toISOString().split('T')[0];

    // Persist each assignment to the server, then update local state
    const serverAssignments = normIds.map(async (clientId) => {
      try {
        await assignProgramToClient(templateId, clientId, startDate, 'UTC', true);
      } catch (err) {
        console.error(`[assignProgramToClients] server assign failed for client ${clientId}:`, err);
        throw err;
      }
    });

    Promise.all(serverAssignments)
      .then(() => {
        toast.success('Program assigned successfully');
        capturePostHog('program_assigned', { clientCount: normIds.length, templateId });
      })
      .catch(() => {
        toast.error('Some assignments failed to save. Please check and retry.');
      });

    // Remove existing programs for these clients first
    setClientPrograms((prev) => prev.filter((program) => !normIds.includes(String(program.clientId))));

    // Create new client programs with deep copies of template data
    const newClientPrograms: ClientProgram[] = clientIds.map((clientId) => ({
      id: `cp_${Date.now()}_${clientId}`,
      clientId: str(clientId),
      templateId: str(templateId),
      programData: createClientProgramData(template),
      assignedDate: new Date().toISOString(),
      startDate: options.startDate,
      notes: options.notes,
      allowCustomizations: options.allowCustomizations,
      isCustomized: false,
      assignedByCoachId: str(coachId),
    }));

    setClientPrograms((prev) => [...prev, ...newClientPrograms]);

    // Update client references
    setClients((prev) =>
      prev.map((client) => {
        if (normIds.includes(String(client.id))) {
          const newProgramId = newClientPrograms.find((program) => program.clientId === client.id)?.id;
          return {
            ...client,
            assignedPrograms: newProgramId ? [newProgramId] : client.assignedPrograms,
          };
        }
        return client;
      }),
    );

    // Keep the newer dashboard domain in sync
    try {
      const start = options.startDate ? new Date(options.startDate) : new Date();
      const current = {
        programs,
        clientPrograms: clientProgramsV2,
        clientEvents,
        programTemplates,
      } as any;
      let nextState = current;
      normIds.forEach((cid) => {
        try {
          nextState = assignProgramTemplateToClient(nextState, templateId, String(cid), start, undefined);
        } catch {
          /* ignored */
        }
      });
      setPrograms(nextState.programs);
      setClientProgramsV2(nextState.clientPrograms);
      setClientEvents(nextState.clientEvents);
    } catch {
      /* ignored */
    }

    try {
      window.dispatchEvent(new CustomEvent('assignments:changed'));
    } catch {
      /* ignored */
    }

    newClientPrograms.forEach(async (cp) => {
      const evt = {
        clientId: str(cp.clientId),
        type: 'program_updated',
        summary: 'Coach assigned program',
        detail: `Template ${(template as any)?.title || (template as any)?.name || ''}`,
        priority: 'medium',
      } as any;
      try {
        await addClientEvent(evt);
      } catch {
        /* ignored */
      }
      const mirrored = { id: `evt_${Date.now()}_${Math.random()}`, createdAt: new Date().toISOString(), ...evt } as any;
      setClientEvents((prev) => {
        const out = [mirrored, ...prev];
        try {
          localStorage.setItem('coachd-client-events', JSON.stringify(out));
        } catch {
          /* ignored */
        }
        return out;
      });
    });
  };

  const updateClientProgram = (clientProgramId: string, updates: Partial<WorkoutTemplate>) => {
    if ((userType ?? 'client') !== 'coach') {
      const mutated = Object.keys(updates || {});
      const contentKeys = ['programData', 'days', 'title', 'durationMin', 'workoutId', 'exerciseSnapshot'];
      if (mutated.some((k) => contentKeys.includes(k))) {
        try {
          if ((import.meta as any).env?.DEV) console.warn('[guard] client attempted program content edit');
        } catch {
          /* ignored */
        }
        return;
      }
    }
    setClientPrograms((prev) => {
      const next = prev.map((program) => {
        if (program.id !== clientProgramId) return program;
        const mergedProgramData: any = { ...program.programData, ...updates };
        // Normalize duration into both string and numeric forms to avoid parsing regressions
        if (typeof (updates as any)?.duration === 'string') {
          const m = (updates as any).duration.match(/(\d+)/);
          const n = m ? parseInt(m[1], 10) : undefined;
          if (Number.isFinite(n as any)) mergedProgramData.durationWeeks = n;
        }
        return { ...program, programData: mergedProgramData, isCustomized: true } as any;
      });
      try {
        const cp = next.find((p) => p.id === clientProgramId);
        if (cp) {
          const evt = {
            id: `evt_${Date.now()}_${Math.random()}`,
            clientId: cp.clientId,
            type: 'program_updated',
            summary: 'Workout program customized',
            detail: 'Changes saved for this client',
            severity: 'low',
            createdAt: new Date().toISOString(),
            viewUrl: `clients/${cp.clientId}/overview?insightId=programs`,
            read: false,
          } as any;
          setClientEvents((prevEvts) => {
            const out = [evt, ...prevEvts];
            try {
              localStorage.setItem('coachd-client-events', JSON.stringify(out));
            } catch {
              /* ignored */
            }
            return out;
          });
          // Fire-and-forget; do not await inside setState updater
          try {
            addClientEvent({
              clientId: String(cp.clientId),
              type: 'program_updated',
              summary: 'Workout program customized',
              detail:
                typeof updates?.duration === 'string'
                  ? `Duration set to ${updates.duration}`
                  : 'Changes saved for this client',
              priority: 'low',
            } as any);
          } catch {
            /* ignored */
          }
        }
      } catch {
        /* ignored */
      }
      // Persist immediately to avoid races with later effects
      try {
        localStorage.setItem('coach-client-programs', JSON.stringify(next));
      } catch {
        /* ignored */
      }
      return next;
    });

    // If duration changed, also reflect into the active v2 program by recomputing totalWeeks
    try {
      const cp = clientPrograms.find((p) => p.id === clientProgramId);
      if (cp && typeof updates?.duration === 'string') {
        const rel = (clientProgramsV2 || []).find(
          (r) => String(r.clientId) === String(cp.clientId) && r.status === 'active',
        );
        if (rel && programs && programs[rel.programId]) {
          const weeksNum = (() => {
            const m = String(updates.duration).match(/(\d+)/);
            const n = m ? parseInt(m[1], 10) : NaN;
            return Number.isFinite(n) && n > 0 ? n : programs[rel.programId].totalWeeks;
          })();
          setPrograms((prev) => ({
            ...prev,
            [rel.programId]: {
              ...prev[rel.programId],
              totalWeeks: weeksNum,
            },
          }));
        }
      }
      // If title changed in customizer, mirror to v2 program name for consistent display
      if (cp && typeof updates?.title === 'string' && updates.title.trim().length) {
        const rel = (clientProgramsV2 || []).find(
          (r) => String(r.clientId) === String(cp.clientId) && r.status === 'active',
        );
        if (rel && programs && programs[rel.programId]) {
          setPrograms((prev) => ({
            ...prev,
            [rel.programId]: {
              ...prev[rel.programId],
              name: updates.title as string,
            },
          }));
        }
      }
    } catch {
      /* ignored */
    }
  };

  const removeClientProgram = async (clientProgramId: string) => {
    if (!requireCoach('removeClientProgram')) return;

    // Update local state immediately for UI responsiveness
    setClientPrograms((prev) => prev.filter((p) => p.id !== clientProgramId));

    // Delete from database
    try {
      const sb = (await import('@/lib/supabase')).getSupabase();
      if (sb) {
        const { error } = await sb.from('client_programs').delete().eq('id', clientProgramId);

        if (error) {
          console.error('[removeClientProgram] Database delete failed:', error);
        } else {
          /* no-op */
        }
      }
    } catch (e) {
      console.error('[removeClientProgram] Error:', e);
    }

    // Dispatch assignments:changed event for metric refreshes
    try {
      window.dispatchEvent(new CustomEvent('assignments:changed'));
    } catch {
      /* ignored */
    }
  };

  const getClientPrograms = (clientId: string) => {
    // Legacy accessor retained for compatibility; prefer v2 elsewhere
    return clientPrograms.filter((program) => program.clientId === clientId);
  };

  // NEW: v2 program updater for editor (edits days/title/duration directly on compiled program)
  const _updateV2Program = (clientId: string, patch: any) => {
    const rel = (clientProgramsV2 || []).find((r) => String(r.clientId) === String(clientId) && r.status === 'active');
    if (!rel) return;
    const pid = rel.programId;
    setPrograms((prev) => ({
      ...prev,
      [pid]: { ...prev[pid], ...patch },
    }));
  };

  const getClientProgram = (clientProgramId: string) => {
    return clientPrograms.find((program) => program.id === clientProgramId);
  };

  // Workout Library Functions
  const saveWorkout = async (payload: SavedWorkout) => {
    if (!requireCoach('saveWorkout')) return;

    // For non-mock mode, save to Supabase via Worker
    if (!useMocks) {
      try {
        const workoutId = payload.id || crypto.randomUUID();

        const itemsToSend = (payload.exercises || []).map((ex: any, idx: number) => ({
          exercise_id: ex.exerciseId || null,
          order_index: idx,
          group_id: ex.groupId || null,
          group_type: ex.groupType || null,
          within_group_order: ex.withinGroupOrder || null,
          notes: ex.notes || null,
          show_in_analytics: true,
          track_by: ex.trackBy || 'reps',
          sets: ex.sets || null,
          reps: ex.reps || null,
          duration_sec: ex.durationSec || null,
          distance: ex.distance || null,
          distance_unit: ex.distanceUnit || null,
          rounds: ex.rounds || null,
          work_sec: ex.workSec || null,
          rest_sec: ex.restSec || null,
          tempo: ex.tempo || null,
          rest_between_sets_sec: ex.restBetweenSetsSec || null,
          effort: ex.effort || null,
          intensity: ex.intensity || null,
          target_pace_sec: ex.targetPaceSec || null,
          load_kg: ex.loadKg || null,
          load_pct_1rm: ex.loadPct1rm || null,
          incline_pct: ex.inclinePct || null,
          speed_kph: ex.speedKph || null,
          cadence_rpm: ex.cadenceRpm || null,
          unilateral_side: ex.unilateralSide || null,
        }));

        const response = await workoutsApi.saveWorkout(
          {
            id: workoutId,
            name: payload.name,
            modality: payload.modality || 'strength',
            notes: payload.notes,
            difficulty: payload.difficulty ? String(payload.difficulty).toLowerCase() : null,
            estimated_duration_min: payload.estimatedDuration,
          },
          itemsToSend,
        );

        if (!response.ok) {
          // Legacy copy: the raw error body text.
          const error = response.body !== undefined ? JSON.stringify(response.body) : response.error;
          console.error('[GlobalState] Workout save failed:', error);
          throw new Error(`Failed to save workout: ${error}`);
        }

        // Trigger workouts refresh
        window.dispatchEvent(new CustomEvent('workouts:changed'));

        return;
      } catch (error) {
        console.error('[GlobalState] Error saving workout to Supabase:', error);
        throw error;
      }
    }

    // Mock mode: save to localStorage
    setSavedWorkouts((prev) => {
      const id = String(payload?.id ?? uid('w'));
      const next: SavedWorkout = {
        ...payload,
        id,
        exercises: Array.isArray(payload.exercises) ? payload.exercises : [],
        modality: payload?.modality ?? inferModality(payload?.name),
      } as SavedWorkout;
      const idx = prev.findIndex((w) => String(w.id) === id);
      const out =
        idx >= 0 ? [...prev.slice(0, idx), { ...prev[idx], ...next }, ...prev.slice(idx + 1)] : [...prev, next];
      try {
        localStorage.setItem('coach-saved-workouts', JSON.stringify(out));
      } catch {
        /* ignored */
      }
      return out;
    });
  };

  // ===== v2: Update compiled program for a client's active relation =====
  const updateCompiledProgram = (
    clientId: string,
    patch: { name?: string; totalWeeks?: number; weeks?: Record<string, WorkoutDay[]> },
  ) => {
    const rel = (clientProgramsV2 || []).find((r) => String(r.clientId) === String(clientId) && r.status === 'active');
    if (!rel) return;
    const pid = rel.programId;
    const existing = programs?.[pid];
    if (!existing) return;

    // Helper: day-of-week → Sunday=0 index
    const dowIndex = (s: string): number => {
      const k = (s || '').toLowerCase();
      if (k.startsWith('sun')) return 0;
      if (k.startsWith('mon')) return 1;
      if (k.startsWith('tue')) return 2;
      if (k.startsWith('wed')) return 3;
      if (k.startsWith('thu')) return 4;
      if (k.startsWith('fri')) return 5;
      if (k.startsWith('sat')) return 6;
      // default: Sunday
      return 0;
    };
    const inferType = (title?: string, modality?: any): any => {
      if (modality) return modality;
      const t = (title || '').toLowerCase();
      if (t.includes('rest') || t.includes('off')) return 'rest';
      if (t.includes('cardio') || t.includes('run') || t.includes('bike') || t.includes('row')) return 'cardio';
      if (t.includes('mobility') || t.includes('stretch') || t.includes('yoga')) return 'mobility';
      if (t.includes('hiit') || t.includes('interval')) return 'hiit';
      if (t.includes('recover')) return 'recovery';
      return 'strength';
    };

    let newDays = Array.isArray(existing.days) ? [...existing.days] : [];
    const byDate = new Map<string, any>(newDays.map((d: any) => [d.date, d]));
    const start = parseLocalISODate(existing.start);

    // Apply week/day overrides from the editor payload
    if (patch?.weeks && typeof patch.weeks === 'object') {
      Object.entries(patch.weeks).forEach(([weekKey, workouts]) => {
        const m = weekKey.match(/week(\d+)/i);
        const wNum = m ? parseInt(m[1], 10) : NaN;
        if (!Number.isFinite(wNum)) return;
        const base = new Date(start);
        base.setDate(start.getDate() + (wNum - 1) * 7);
        // Initialize all 7 days in this week to a cleared/rest state so deletions persist
        for (let i = 0; i < 7; i++) {
          const d = new Date(base);
          d.setDate(base.getDate() + i);
          const dateStr = toLocalISODate(d);
          byDate.set(dateStr, {
            date: dateStr,
            type: 'rest',
            title: 'Rest',
            durationMin: undefined,
            workoutId: undefined,
            exerciseSnapshot: undefined,
          } as any);
        }
        (workouts || []).forEach((w) => {
          const offset = dowIndex(String((w as any)?.dayOfWeek || 'Sunday'));
          const d = new Date(base);
          d.setDate(base.getDate() + offset);
          const dateStr = toLocalISODate(d);
          const title = (w as any)?.name ?? (w as any)?.title ?? 'Workout';
          const durationMin = (w as any)?.estimatedDuration ?? (w as any)?.durationMin;
          const modality = (w as any)?.modality;
          const exercises = Array.isArray((w as any)?.exercises) ? (w as any).exercises : undefined;
          const updated = {
            date: dateStr,
            type: inferType(title, modality),
            title,
            durationMin: typeof durationMin === 'number' ? durationMin : undefined,
            workoutId: undefined,
            exerciseSnapshot: exercises,
          } as any;
          byDate.set(dateStr, updated);
        });
      });
      newDays = Array.from(byDate.values()).sort((a, b) => (a.date < b.date ? -1 : 1));
    }

    const nextProgram = {
      ...existing,
      ...(patch?.name ? { name: patch.name } : {}),
      ...(Number.isFinite(patch?.totalWeeks as any) ? { totalWeeks: patch!.totalWeeks } : {}),
      ...(patch?.weeks ? { days: newDays } : {}),
    } as any;

    setPrograms((prev) => ({
      ...prev,
      [pid]: nextProgram,
    }));

    // Client-visible update event
    try {
      addClientEvent({
        clientId: String(clientId),
        type: 'program_updated',
        summary: 'Workout program customized',
        detail: patch?.totalWeeks ? `Duration set to ${patch.totalWeeks} weeks` : 'Changes saved for this client',
        priority: 'low',
      } as any);
    } catch {
      /* ignored */
    }
  };

  const deleteWorkout = (workoutId: string) => {
    if (!requireCoach('deleteWorkout')) return;
    setSavedWorkouts((prev) => prev.filter((w) => w.id !== workoutId));
  };

  const getSavedWorkouts = () => {
    return savedWorkouts;
  };

  const searchWorkouts = (query: string, tags?: string[]) => {
    const q = (query ?? '').toString().trim().toLowerCase();
    const selectedTags = Array.isArray(tags) ? tags.filter(Boolean).map((t) => String(t)) : [];

    return savedWorkouts.filter((workout) => {
      const name = (workout?.name ?? '').toString().toLowerCase();
      const wTagsLower = Array.isArray(workout?.tags) ? workout.tags.filter(Boolean).map((t) => t.toLowerCase()) : [];

      const matchesQuery = !q || name.includes(q) || wTagsLower.some((t) => t.includes(q));

      const matchesTags = selectedTags.length === 0 || selectedTags.some((t) => workout?.tags?.includes(t));

      return matchesQuery && matchesTags;
    });
  };

  // Expose workouts map for client selectors/UI
  const workouts = useMemo(() => {
    const entries = (savedWorkouts ?? []).map((w) => {
      const id = String(w.id);
      const exercises = Array.isArray(w.exercises) ? w.exercises : [];
      return [
        id,
        {
          id,
          name: w.name,
          durationMin: w.estimatedDuration,
          notes: w.notes,
          modality: w.modality ?? inferModality(w.name),
          exercises,
        },
      ] as const;
    });
    return Object.fromEntries(entries);
  }, [savedWorkouts]);

  // Helper to estimate a duration from exercises if none provided
  const calcEstimatedDuration = (exs: Exercise[]) => {
    // naive estimate: ~120s per set (work + rest)
    try {
      return exs.reduce((mins, ex) => {
        const sets = Number(ex.sets ?? 0);
        // 120 seconds per set -> convert to minutes
        return mins + Math.ceil((sets * 120) / 60);
      }, 0);
    } catch {
      return 30; // safe default
    }
  };

  // Convenience helper to build a valid SavedWorkout and persist it
  const createAndSaveWorkout = (
    partial: Partial<SavedWorkout> & Pick<SavedWorkout, 'name' | 'exercises'>,
  ): SavedWorkout => {
    if (!requireCoach('createAndSaveWorkout')) return { id: '0', name: 'Untitled', exercises: [] } as any;
    // Normalize exercises preserving tracking modes
    const normalizedExercises: Exercise[] = (partial.exercises ?? []).map((e: any) => {
      const id = e?.id ? String(e.id) : uid('e');
      const name = String(e?.name ?? '').trim();
      const hasTime = e?.durationSec != null || (typeof e?.duration === 'string' && e.duration.trim().length > 0);
      const hasDistance = e?.distance != null;
      const hasInterval = e?.rounds != null || e?.workSec != null || e?.restSec != null;
      const trackBy =
        (e?.trackBy as any) ?? (hasTime ? 'time' : hasDistance ? 'distance' : hasInterval ? 'interval' : 'reps');

      const out: Exercise = {
        id,
        name,
        trackBy,
        effort: e?.effort ? String(e.effort) : undefined,
        rest: e?.rest ? String(e.rest) : undefined,
        notes: e?.notes ? String(e.notes) : undefined,
        intensity: e?.intensity ? String(e.intensity) : undefined,
      } as Exercise;

      if (trackBy === 'time') {
        out.durationSec = Number(e?.durationSec ?? 0) || 0;
      } else if (trackBy === 'distance') {
        out.distance = Number(e?.distance ?? 0) || 0;
        out.distanceUnit = e?.distanceUnit === 'km' ? 'km' : 'mi';
        const pace = Number(e?.targetPaceSec ?? 0);
        if (pace) out.targetPaceSec = pace;
      } else if (trackBy === 'interval') {
        out.rounds = Number(e?.rounds ?? 0) || 0;
        out.workSec = Number(e?.workSec ?? 0) || 0;
        const restS = Number(e?.restSec ?? 0);
        if (restS) out.restSec = restS;
      } else {
        out.sets = Number(e?.sets ?? 0);
        out.reps = String(e?.reps ?? '').trim();
      }

      return out;
    });

    const workout: SavedWorkout = {
      id: partial.id ? String(partial.id) : uid('w'),
      name: String(partial.name).trim() || 'Untitled Workout',
      exercises: normalizedExercises,
      notes: partial.notes?.toString().trim() || undefined,
      estimatedDuration:
        typeof partial.estimatedDuration === 'number'
          ? partial.estimatedDuration
          : calcEstimatedDuration(normalizedExercises),
      tags: Array.isArray(partial.tags) ? partial.tags.filter(Boolean).map((t) => t.toString().trim()) : [],
      createdDate: partial.createdDate ?? new Date().toISOString(),
      difficulty: (partial.difficulty as SavedWorkout['difficulty']) ?? 'Beginner',
      category: partial.category || undefined,
      modality: partial.modality ?? inferModality(partial.name),
    };

    saveWorkout(workout);
    return workout;
  };

  // Other functions (simplified for this fix)
  const saveNutritionTemplate = async (template: NutritionTemplate) => {
    try {
      // Convert template to database format
      const NT = await import('@/data/nutritionTemplates');
      const { buildTemplateSnapshotFromEditor } = await import('@/utils/nutrition/snapshot');

      // Build snapshot from template data using the utility
      const snapshot = buildTemplateSnapshotFromEditor(template);

      // Use the new upsert function for cleaner logic
      const finalId =
        template.id && !template.id.startsWith('template_') && !template.id.startsWith('nt_') ? template.id : undefined;

      const dbTemplate = await NT.upsertNutritionTemplate({
        id: finalId,
        title: template.title,
        notes: template.description || null, // Map 'description' to 'notes' for DB
        tags: template.tags || [],
        template_snapshot: snapshot,
        targetCalories: template.targetNutrition?.dailyCalories,
        proteinPct: template.targetNutrition?.proteinPercentage,
        carbsPct: template.targetNutrition?.carbsPercentage,
        fatPct: template.targetNutrition?.fatPercentage,
        difficulty: template.difficulty,
        duration: template.duration,
      } as any);

      // Convert database result to expected format
      const convertedTemplate = {
        id: dbTemplate.id,
        title: dbTemplate.title,
        ownerCoachId: dbTemplate.coach_id,
        description: dbTemplate.notes || '', // Map 'notes' back to 'description' for UI
        tags: dbTemplate.tags || [],
        difficulty: template.difficulty || ('Intermediate' as const),
        duration: template.duration || 'Ongoing',
        thumbnail: template.thumbnail,
        createdDate: dbTemplate.created_at.split('T')[0],
        assignedClients: 0,
        isPublic: false,
        targetNutrition: template.targetNutrition || {
          dailyCalories: 2000,
          proteinPercentage: 25,
          carbsPercentage: 45,
          fatPercentage: 30,
        },
        // Convert the database snapshot back to dayTypes for the UI
        dayTypes:
          (dbTemplate as any).template_snapshot?.days?.map((day: any, idx: number) => ({
            id: `dt_${idx}`,
            name: day.label || `Day ${idx + 1}`,
            meals:
              day.meals?.map((meal: any, mi: number) => ({
                id: `m_${mi}`,
                name: meal.label || (meal.type ? meal.type.charAt(0).toUpperCase() + meal.type.slice(1) : 'Meal'),
                type: meal.type || 'custom', // Use saved type from snapshot instead of hardcoding 'custom'
                foods:
                  meal.entries?.map((entry: any, _ei: number) => ({
                    foodItem: {
                      id: entry.food_id,
                      name: entry.name,
                      brand: entry.brand || undefined,
                      calories: entry.kcal,
                      protein: entry.protein_g,
                      carbs: entry.carbs_g,
                      fat: entry.fat_g,
                      serving_size: entry.portion_label,
                      category: 'Other',
                    },
                    quantity: entry.quantity,
                    unit: entry.portion_label,
                    portion_id: entry.portion_id || undefined,
                    portion_label: entry.portion_label,
                    portion_grams: entry.portion_grams,
                    total_kcal: entry.kcal,
                    total_protein_g: entry.protein_g,
                    total_carbs_g: entry.carbs_g,
                    total_fat_g: entry.fat_g,
                    nutrition: {
                      calories: entry.kcal,
                      protein: entry.protein_g,
                      carbs: entry.carbs_g,
                      fat: entry.fat_g,
                    },
                  })) || [],
                totalNutrition: { calories: 0, protein: 0, carbs: 0, fat: 0 },
              })) || [],
          })) || [],
        cycleLength: template.cycleLength || (dbTemplate as any).template_snapshot?.days?.length || 1,
      };

      // Update local state with converted result
      setNutritionTemplates((prev) => {
        const existingIndex = prev.findIndex((t) => t.id === convertedTemplate.id);
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = convertedTemplate as any;
          return updated;
        } else {
          return [convertedTemplate as any, ...prev];
        }
      });
    } catch (error: any) {
      console.error('❌ Failed to save nutrition template to database:', error);
      console.error('❌ Error details:', {
        code: error?.code,
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
      });
      throw error; // Re-throw so UI can handle the error
    }
  };

  const deleteNutritionTemplate = async (templateId: string) => {
    try {
      // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
      const res = await deleteTemplateViaApi(templateId);

      if (!res.ok) {
        throw new Error(bodyError(res) || withStatus(res, (status) => `Failed to delete template (${status})`));
      }

      // Update local state
      setNutritionTemplates((prev) => prev.filter((t) => t.id !== templateId));
    } catch (error) {
      console.error('Failed to delete nutrition template:', error);
      // Fallback to localStorage only
      setNutritionTemplates((prev) => prev.filter((t) => t.id !== templateId));
      throw error;
    }

    // Remove any client nutrition plans based on this template, and clean up client references
    setClientNutritionPlans((prevPlans) => {
      const removedPlanIds = prevPlans.filter((p) => p.templateId === templateId).map((p) => p.id);

      if (removedPlanIds.length > 0) {
        setClients((prevClients) =>
          prevClients.map((c) => ({
            ...c,
            assignedNutritionPlans: c.assignedNutritionPlans.filter((id) => !removedPlanIds.includes(id)),
          })),
        );
      }

      return prevPlans.filter((p) => p.templateId !== templateId);
    });
  };

  const updateClientNutritionPlan = async (planId: string, updates: Partial<NutritionTemplate>) => {
    try {
      // Check if this is a database assignment (UUID format) or in-memory plan
      const isDbAssignment =
        planId && planId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);

      if (isDbAssignment) {
        // Handle database assignment customization
        const { saveClientPlanSnapshot } = await import('@/data/nutritionAssignments');

        // Convert DayPlan[] to DayType[] format for database
        const dayTypesData =
          updates.days?.map((day: any) => ({
            id: day.id,
            name: day.dayType || day.dayName,
            meals: day.meals,
          })) || [];

        // Save to database (using editorData, not snapshot)
        await saveClientPlanSnapshot(planId, dayTypesData);

        // Trigger a refresh of assignments in EnhancedClientProfile
        // We'll emit a custom event that the profile can listen to
        window.dispatchEvent(
          new CustomEvent('nutrition-customization-saved', {
            detail: { planId, clientId: updates.id },
          }),
        );

        return;
      }
    } catch (error) {
      console.error('Failed to save nutrition customization to database:', error);
      // Show user-friendly error message
      toast.error(
        `Failed to save nutrition customization: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
      // Fall back to in-memory update
    }

    // Original in-memory logic for backward compatibility
    setClientNutritionPlans((prev) => {
      const nextPlans = prev.map((plan) => {
        if (plan.id !== planId) return plan;
        const prevData = plan.planData || ({} as NutritionTemplate);
        // Derive next dayTypes when caller provided only days
        const deriveDayTypesFromLegacyDays = (days?: any[]) => {
          if (!Array.isArray(days) || !days.length) return [] as any[];
          const map = new Map<string, any[]>();
          for (const d of days) {
            const key = String(d?.dayType || d?.dayName || `Day ${d?.dayNumber || '?'}`);
            if (!map.has(key)) map.set(key, []);
            if (Array.isArray(d?.meals)) {
              const meals = map.get(key)!;
              if (meals.length === 0) {
                map.set(
                  key,
                  d.meals.map((m: any) => ({
                    ...m,
                    id: String(m?.id ?? `m_${Math.random()}`),
                    foods: (m?.foods || []).map((f: any) => ({
                      ...f,
                      foodItem: { ...(f?.foodItem || {}), id: String(f?.foodItem?.id ?? `food_${Math.random()}`) },
                    })),
                  })),
                );
              }
            }
          }
          return Array.from(map.entries()).map(([name, meals], i) => ({ id: `dt_${i}_${Date.now()}`, name, meals }));
        };
        const next: NutritionTemplate = {
          ...prevData,
          ...updates,
          days: Array.isArray((updates as any)?.days) ? ((updates as any).days as any) : (prevData as any).days,
          dayTypes:
            Array.isArray((updates as any)?.dayTypes) && (updates as any).dayTypes!.length
              ? ((updates as any).dayTypes as any)
              : Array.isArray((updates as any)?.days)
                ? deriveDayTypesFromLegacyDays((updates as any).days)
                : (prevData as any).dayTypes,
          targetNutrition: {
            dailyCalories: updates?.targetNutrition?.dailyCalories ?? prevData.targetNutrition.dailyCalories,
            proteinPercentage:
              updates?.targetNutrition?.proteinPercentage ?? prevData.targetNutrition.proteinPercentage,
            carbsPercentage: updates?.targetNutrition?.carbsPercentage ?? prevData.targetNutrition.carbsPercentage,
            fatPercentage: updates?.targetNutrition?.fatPercentage ?? prevData.targetNutrition.fatPercentage,
          },
        } as NutritionTemplate;

        const snapshotAt = new Date().toISOString();
        (next as any).planSnapshotAt = snapshotAt;
        const prevVer = Number((prevData as any).planVersion ?? 0) || 0;
        (next as any).planVersion = prevVer + 1;

        const updated = { ...plan, planData: next, isCustomized: true };
        // push coach update event for nutrition customization
        try {
          const evt = {
            id: `evt_${Date.now()}_${Math.random()}`,
            clientId: updated.clientId,
            type: 'nutrition_updated',
            summary: 'Nutrition plan customized',
            detail: 'Changes saved for this client',
            severity: 'low',
            createdAt: new Date().toISOString(),
            viewUrl: `clients/${updated.clientId}/overview?insightId=nutrition`,
            read: false,
          } as any;
          setClientEvents((prevEvts) => {
            const out = [evt, ...prevEvts];
            try {
              localStorage.setItem('coachd-client-events', JSON.stringify(out));
            } catch {
              /* ignored */
            }
            return out;
          });
        } catch {
          /* ignored */
        }
        return updated;
      });
      try {
        localStorage.setItem('coach-client-nutrition-plans', JSON.stringify(nextPlans));
      } catch {
        /* ignored */
      }
      return nextPlans;
    });
  };

  const removeClientNutritionPlan = async (planId: string) => {
    try {
      // Use Worker endpoint (BFF pattern for httpOnly cookie auth)
      const res = await deleteNutritionAssignment(planId);

      if (!res.ok) {
        console.error('Failed to delete nutrition assignment from Worker:', res.status, res.body ?? res.error);
        throw new Error(bodyError(res) || 'Failed to delete assignment');
      }

      // Remove from local state after successful deletion
      setClientNutritionPlans((prev) => prev.filter((p) => p.id !== planId));

      // Refresh the assignments list to update the Active Plans count
      await loadNutritionAssignments();
    } catch (error) {
      console.error('Failed to remove nutrition assignment:', error);
      throw error; // Re-throw so the UI can show the error
    }
  };

  const getClientNutritionPlans = (clientId: string) => {
    return clientNutritionPlans.filter((plan) => plan.clientId === clientId);
  };

  // Load nutrition assignments from Worker endpoint
  const loadNutritionAssignments = React.useCallback(async () => {
    try {
      const response = await csrfFetch('/api/coach/nutrition-assignments', {
        credentials: 'include',
      });

      if (!response.ok) {
        console.error('[nutrition-assignments] Failed to load assignments:', response.status);
        return;
      }

      const data = await response.json();

      // API returns { items: [...] } not { assignments: [...] }
      const assignments = data.items || data.assignments || [];

      if (assignments && Array.isArray(assignments)) {
        // Transform assignments to match ClientNutritionPlan format
        const transformedPlans: ClientNutritionPlan[] = assignments.map((assignment: any) => {
          // Extract title with fallback chain: assignment.title -> template_snapshot.title -> empty string
          const title = assignment.title || assignment.template_snapshot?.title || '';

          // Log warning if title is missing (helps catch future issues)
          if (!title) {
            console.warn('[nutrition-assignments] Assignment missing title:', {
              assignmentId: assignment.id,
              clientId: assignment.client_id,
              hasTemplateSnapshot: !!assignment.template_snapshot,
              templateSnapshotTitle: assignment.template_snapshot?.title,
            });
          }

          // Convert database snapshot format to editor format using shared utility
          const dayTypes = snapshotToEditorFormat(assignment.template_snapshot);

          return {
            id: assignment.id,
            clientId: assignment.client_id,
            templateId: assignment.template_id || assignment.nutrition_template_id || '',
            title: title,
            planData: {
              id: assignment.template_id || assignment.id,
              title: title,
              description: assignment.notes || '',
              tags: [],
              difficulty: 'Intermediate' as const,
              duration: '',
              isPublic: false,
              assignedClients: 0,
              createdDate: assignment.created_at,
              targetNutrition: {
                dailyCalories: 2000,
                proteinPercentage: 30,
                carbsPercentage: 40,
                fatPercentage: 30,
              },
              dayTypes, // ✅ Properly converted with foods[]
            },
            assignedDate: assignment.created_at || assignment.assigned_date || new Date().toISOString(),
            startDate: assignment.start_date,
            notes: assignment.notes || '',
            allowCustomizations: assignment.allow_customizations ?? true,
            isCustomized: assignment.is_customized ?? false,
          };
        });

        setClientNutritionPlans(transformedPlans);
      } else {
        setClientNutritionPlans([]);
      }
    } catch (error) {
      console.error('[nutrition-assignments] Failed to load assignments:', error);
    }
  }, []);

  const getClientNutritionPlan = (planId: string) => {
    return clientNutritionPlans.find((plan) => plan.id === planId);
  };

  const searchFoodItems = (query: string) => {
    return foodItems.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));
  };

  const addFoodItem = (foodItem: Omit<FoodItem, 'id'>) => {
    const id = `f_${Date.now()}_${Math.random()}`;
    const newItem = { ...foodItem, id };
    setFoodItems((prev) => [...prev, newItem]);
    return id;
  };

  // Meals Management Functions
  const listCoachMeals = async (opts?: { includePublic?: boolean; search?: string; type?: string }) => {
    if (!currentCoach?.id) return [];

    try {
      // Load meals via Worker (BFF pattern for httpOnly cookie auth)
      const searchParam = opts?.search ? `&search=${encodeURIComponent(opts.search)}` : '';
      const mealsRes = await fetch(`/api/coach/meals?limit=200${searchParam}`, { credentials: 'include' });
      if (!mealsRes.ok) {
        console.error('Failed to list coach meals from Worker:', mealsRes.status);
        return [];
      }
      const { items } = await mealsRes.json();
      return items || [];
    } catch (error) {
      console.error('Failed to list coach meals:', error);
      return [];
    }
  };

  const getMealWithItems = async (mealId: string) => {
    try {
      const { mealsStore } = await import('@/data/storage/meals/adapter');
      return await mealsStore.getMealWithItems(mealId);
    } catch (error) {
      console.error('Failed to get meal with items:', error);
      return null;
    }
  };

  const saveCoachMeal = async (payload: any) => {
    if (!currentCoach?.id) throw new Error('No coach ID available');

    try {
      const { mealsStore } = await import('@/data/storage/meals/adapter');
      const { emit } = await import('@/utils/eventBus');

      const result = await mealsStore.saveCoachMeal(payload);

      // Refresh coach meals from Worker to get updated data
      try {
        const mealsRes = await csrfFetch('/api/coach/meals', { credentials: 'include' });
        if (mealsRes.ok) {
          const { items: meals } = await mealsRes.json();
          setCoachMeals(meals || []);
        }
      } catch (err) {
        console.warn('Failed to refresh meals after save:', err);
      }

      // Emit event for UI updates
      emit('meals:changed', { op: payload.id ? 'update' : 'create', id: result.id });

      return result;
    } catch (error) {
      console.error('Failed to save coach meal:', error);
      throw error;
    }
  };

  const deleteCoachMeal = async (mealId: string) => {
    try {
      const { mealsStore } = await import('@/data/storage/meals/adapter');
      const { emit } = await import('@/utils/eventBus');

      await mealsStore.deleteCoachMeal(mealId);

      // Emit event for UI updates
      emit('meals:changed', { op: 'delete', id: mealId });
    } catch (error) {
      console.error('Failed to delete coach meal:', error);
      throw error;
    }
  };

  const duplicateCoachMeal = async (mealId: string) => {
    try {
      const { mealsStore } = await import('@/data/storage/meals/adapter');
      const { emit } = await import('@/utils/eventBus');

      const result = await mealsStore.duplicateCoachMeal(mealId);

      // Emit event for UI updates
      emit('meals:changed', { op: 'create', id: result.id });

      return result;
    } catch (error) {
      console.error('Failed to duplicate coach meal:', error);
      throw error;
    }
  };

  const getClient = (clientId: string) => {
    return clients.find((client) => client.id === clientId);
  };

  const addClient = async (
    client: Omit<Client, 'id' | 'assignedPrograms' | 'assignedNutritionPlans' | 'coachNotes'>,
  ) => {
    if (isMockMode()) {
      // Mock mode: use in-memory state
      const id = `c_${Date.now()}_${Math.random()}`;
      const newClient: Client = {
        ...client,
        id,
        assignedPrograms: [],
        assignedNutritionPlans: [],
        coachNotes: [],
      };
      setClients((prev) => [...prev, newClient]);
      return id;
    } else {
      // Database mode: use createClientProfile
      try {
        const id = await createClientProfile(client.name, client.email || '');
        return id;
      } catch (error) {
        console.error('Failed to create client in database mode:', error);
        throw error;
      }
    }
  };

  const updateClient = (clientId: string, updates: Partial<Client>) => {
    setClients((prev) => prev.map((client) => (client.id === clientId ? { ...client, ...updates } : client)));
  };

  const deleteClient = (clientId: string) => {
    setClients((prev) => prev.filter((c) => c.id !== clientId));
  };

  // In-flight guard for note operations
  const [notesInFlight, setNotesInFlight] = React.useState<Set<string>>(new Set());

  /**
   * Generate deterministic idempotency key for note
   */
  const generateIdempotencyKey = async (clientId: string, noteContent: string): Promise<string> => {
    const input = `${clientId}|${noteContent.slice(0, 200)}|${Date.now()}`;
    // Use SubtleCrypto for consistent hashing
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = new Uint8Array(hashBuffer);
    // Convert to base64url
    const base64 = btoa(String.fromCharCode(...hashArray));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '').slice(0, 32);
  };

  const addClientNote = async (clientId: string, note: Omit<ClientNote, 'id'>) => {
    // Validate clientId is UUID-like
    if (!clientId || typeof clientId !== 'string' || clientId.length < 10) {
      console.error('[addClientNote] Invalid clientId:', clientId);
      return;
    }

    // Validate note content
    if (!note.note || typeof note.note !== 'string' || note.note.trim().length === 0) {
      console.error('[addClientNote] Invalid note content');
      return;
    }

    // Check if already in flight for this client
    if (notesInFlight.has(clientId)) {
      console.warn('[addClientNote] Operation already in flight for client:', clientId);
      return;
    }

    // Generate idempotency key
    const idempotencyKey = await generateIdempotencyKey(clientId, note.note);

    // Temporary ID for optimistic update (will be replaced by server ID)
    const tempNoteId = `temp_${Date.now()}_${idempotencyKey.slice(0, 8)}`;
    const newNote = { ...note, id: tempNoteId };

    // Capture previous state for rollback
    const previousClients = clients;

    // Mark in-flight
    setNotesInFlight((prev) => new Set(prev).add(clientId));

    // Update local state immediately for optimistic UI
    setClients((prev) =>
      prev.map((client) =>
        client.id === clientId
          ? {
              ...client,
              coachNotes: [newNote, ...((Array.isArray(client.coachNotes) ? client.coachNotes : []) as ClientNote[])],
            }
          : client,
      ),
    );

    // Persist via API
    try {
      const result = await notesApi.createNote({
        clientId,
        note: note.note.slice(0, 5000),
        type: ['observation', 'reminder', 'goal', 'concern', 'general', 'workout', 'nutrition'].includes(
          note.type || '',
        )
          ? (note.type as string)
          : 'observation',
        tags: Array.isArray(note.tags) ? note.tags.slice(0, 10) : [],
        pinned: Boolean(note.pinned),
        idempotencyKey,
      });

      if (!result.ok) {
        if (result.status === undefined) {
          // Never reached the server — the old network-error (catch) copy.
          toast.error('Failed to save note. Please check your connection.');
        } else if (result.status === 429) {
          // Handle rate limiting
          const retryAfter = result.retryAfter || 60;
          toast.error(`Too many requests. Please wait ${retryAfter} seconds.`);
        } else if (result.status === 401) {
          toast.error('Session expired. Please refresh the page.');
        } else {
          toast.error('Failed to save note. Please try again.');
        }
        console.error('[addClientNote] API error:', result.body ?? result.error);
        setClients(previousClients);
        return;
      }

      if (result.ok) {
        // Update with real note ID from server
        const realNoteId = result.noteId as string;
        setClients((prev) =>
          prev.map((client) =>
            client.id === clientId
              ? {
                  ...client,
                  coachNotes: (client.coachNotes || []).map((n) =>
                    n.id === tempNoteId ? { ...n, id: realNoteId } : n,
                  ),
                }
              : client,
          ),
        );

        if (result.duplicate) {
          /* no-op */
        }
      }
    } catch (err) {
      console.error('[addClientNote] Exception, rolling back:', err);
      toast.error('Failed to save note. Please check your connection.');
      setClients(previousClients);
    } finally {
      // Clear in-flight
      setNotesInFlight((prev) => {
        const next = new Set(prev);
        next.delete(clientId);
        return next;
      });
    }
  };

  const updateClientNote = async (clientId: string, noteId: string, updates: Partial<ClientNote>) => {
    // Validate inputs
    if (!clientId || !noteId) {
      console.error('[updateClientNote] Missing clientId or noteId');
      return false;
    }

    // Capture previous state for rollback
    const previousClients = clients;

    // Update local state immediately
    setClients((prev) =>
      prev.map((client) =>
        client.id === clientId
          ? {
              ...client,
              coachNotes: (Array.isArray(client.coachNotes) ? client.coachNotes : []).map((note) =>
                note.id === noteId ? { ...note, ...updates } : note,
              ),
            }
          : client,
      ),
    );

    // Persist to database
    try {
      const sb = getSupabase();
      if (sb) {
        const dbUpdates: Record<string, any> = {};
        if (updates.note !== undefined) dbUpdates.note_text = String(updates.note).slice(0, 5000);
        if (updates.type !== undefined) {
          dbUpdates.note_type = [
            'observation',
            'reminder',
            'goal',
            'concern',
            'general',
            'workout',
            'nutrition',
          ].includes(updates.type)
            ? updates.type
            : 'general';
        }
        if (updates.tags !== undefined) dbUpdates.tags = Array.isArray(updates.tags) ? updates.tags.slice(0, 10) : [];
        if (updates.pinned !== undefined) dbUpdates.pinned = Boolean(updates.pinned);
        dbUpdates.updated_at = new Date().toISOString();

        const { error } = await sb.from('client_notes').update(dbUpdates).eq('id', noteId);

        if (error) {
          console.error('[updateClientNote] DB error, rolling back:', { code: error.code, message: error.message });
          setClients(previousClients);
          return false;
        }
        return true; // persisted
      }
      return true; // no Supabase (e.g. mock mode) — the optimistic local update stands
    } catch (err) {
      console.error('[updateClientNote] Exception, rolling back:', err);
      setClients(previousClients);
      return false;
    }
  };

  const deleteClientNote = async (clientId: string, noteId: string) => {
    // Validate inputs
    if (!clientId || !noteId) {
      console.error('[deleteClientNote] Missing clientId or noteId');
      return;
    }

    // Capture previous state for rollback
    const previousClients = clients;

    // Update local state immediately (optimistic)
    setClients((prev) =>
      prev.map((client) =>
        client.id === clientId
          ? {
              ...client,
              coachNotes: (Array.isArray(client.coachNotes) ? client.coachNotes : []).filter(
                (note) => note.id !== noteId,
              ),
            }
          : client,
      ),
    );

    // Soft delete via API
    try {
      const result = await notesApi.deleteNote(noteId);

      if (!result.ok) {
        if (result.status === undefined) {
          // Never reached the server — the old network-error (catch) copy.
          toast.error('Failed to delete note. Please check your connection.');
        } else if (result.status === 401) {
          toast.error('Session expired. Please refresh the page.');
        } else {
          toast.error('Failed to delete note. Please try again.');
        }
        console.error('[deleteClientNote] API error:', result.body ?? result.error);
        setClients(previousClients);
      }
    } catch (err) {
      console.error('[deleteClientNote] Exception, rolling back:', err);
      toast.error('Failed to delete note. Please check your connection.');
      setClients(previousClients);
    }
  };

  const getClientNotes = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    return client ? client.coachNotes || [] : [];
  };

  // Notes pagination state
  const [notesCursors, setNotesCursors] = React.useState<Record<string, string | null>>({});
  const [notesHasMore, setNotesHasMore] = React.useState<Record<string, boolean>>({});

  // Load notes from API for a client (with optional pagination cursor)
  const loadClientNotes = React.useCallback(async (clientId: string, cursor?: string) => {
    try {
      const params = new URLSearchParams({ clientId, limit: '50' });
      if (cursor) params.set('cursor', cursor);

      const response = await fetch(`/api/notes?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('[loadClientNotes] Unauthorized');
          return;
        }
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();

      if (result.ok && result.items) {
        const notes: ClientNote[] = result.items.map((row: any) => ({
          id: row.id,
          date: row.date,
          note: row.note,
          type: row.type,
          tags: row.tags || [],
          pinned: row.pinned || false,
          coachName: 'Coach',
        }));

        setClients((prev) =>
          prev.map((client) =>
            client.id === clientId
              ? {
                  ...client,
                  coachNotes: cursor ? [...(client.coachNotes || []), ...notes] : notes,
                }
              : client,
          ),
        );

        // Update pagination state
        setNotesCursors((prev) => ({ ...prev, [clientId]: result.nextCursor }));
        setNotesHasMore((prev) => ({ ...prev, [clientId]: !!result.nextCursor }));
      }
    } catch (err) {
      console.error('[loadClientNotes] Error:', err);
    }
  }, []);

  // Load more notes (pagination)
  const loadMoreClientNotes = React.useCallback(
    async (clientId: string) => {
      const cursor = notesCursors[clientId];
      if (!cursor) return;
      await loadClientNotes(clientId, cursor);
    },
    [notesCursors, loadClientNotes],
  );

  // Check if client has more notes to load
  const clientHasMoreNotes = React.useCallback(
    (clientId: string): boolean => {
      return notesHasMore[clientId] ?? false;
    },
    [notesHasMore],
  );

  // Program Template Assignment (dev-only helper)
  const assignProgramTemplate = (
    templateId: string,
    clientId: string,
    startDate: Date,
    programNameOverride?: string,
  ) => {
    // Ensure the template exists in programTemplates; if not, synthesize from a WorkoutTemplate
    let idToUse = templateId;
    let templatesToUse = programTemplates;
    const exists = Array.isArray(programTemplates) && programTemplates.some((t: any) => t.id === templateId);
    if (!exists) {
      const wt = templates.find((t) => t.id === templateId);
      if (wt) {
        // Build per-week patterns from the WorkoutTemplate. Do NOT set .pattern if weeks exist.
        const weeks: Array<{ week: number; pattern: any[] }> = [];
        const weekKeys = Object.keys(wt.weeks || {});
        weekKeys.forEach((wkKey) => {
          const match = wkKey.match(/week(\d+)/i);
          const wNum = match ? parseInt(match[1], 10) : undefined;
          const rows = (wt.weeks as any)[wkKey] as any[] | undefined;
          const days = Array.isArray(rows) ? rows : [];
          const mapped = days.map((d: any, idx: number) => {
            const title = String(d?.name ?? d?.title ?? 'Workout');
            const k = title.toLowerCase();
            const type =
              k.includes('rest') || k.includes('off')
                ? 'rest'
                : k.includes('cardio') || k.includes('run')
                  ? 'cardio'
                  : k.includes('mobility')
                    ? 'mobility'
                    : k.includes('hiit')
                      ? 'hiit'
                      : k.includes('recovery')
                        ? 'recovery'
                        : 'strength';
            // carry inline exercises from the WorkoutTemplate into the ProgramTemplate pattern
            const exercises = Array.isArray(d?.exercises)
              ? d.exercises.map((e: any) => ({
                  id: e?.id,
                  name: e?.name,
                  // reps mode
                  sets: e?.sets,
                  reps: e?.reps,
                  // tracking discriminator
                  trackBy: e?.trackBy,
                  // time mode
                  durationSec: e?.durationSec,
                  // distance mode
                  distance: e?.distance,
                  distanceUnit: e?.distanceUnit,
                  targetPaceSec: e?.targetPaceSec,
                  // interval mode
                  rounds: e?.rounds,
                  workSec: e?.workSec,
                  restSec: e?.restSec,
                  // common
                  effort: e?.effort,
                  rest: e?.rest,
                  notes: e?.notes,
                  intensity: e?.intensity,
                }))
              : undefined;
            return {
              // Prefer explicit day labels from editor
              dayOfWeek: d?.dayOfWeek ?? d?.dayName ?? undefined,
              // If label missing, treat array index as Monday-first
              weekdayMon0: typeof d?.weekdayMon0 === 'number' ? d.weekdayMon0 : idx,
              title,
              type,
              durationMin: typeof d?.estimatedDuration === 'number' ? d.estimatedDuration : undefined,
              meals: 4,
              // If present, include inlined exercises so assignment can carry to exerciseSnapshot
              exercises,
              // If the day was linked to a saved workout in some flows, preserve that too
              workoutId: d?.workoutId != null ? String(d.workoutId) : undefined,
            };
          });
          if (wNum && mapped.length > 0) {
            weeks.push({ week: wNum, pattern: mapped });
          }
        });
        const _inferType = (title: string): 'strength' | 'hiit' | 'recovery' | 'rest' | 'cardio' | 'mobility' => {
          const k = title.toLowerCase();
          if (k.includes('rest') || k.includes('off')) return 'rest';
          if (k.includes('cardio') || k.includes('run')) return 'cardio';
          if (k.includes('mobility')) return 'mobility';
          if (k.includes('hiit')) return 'hiit';
          if (k.includes('recovery')) return 'recovery';
          return 'strength';
        };
        const totalWeeksParsed = parseInt(String(wt.duration).replace(/[^0-9]/g, ''), 10);
        const totalWeeks = Number.isFinite(totalWeeksParsed) && totalWeeksParsed > 0 ? totalWeeksParsed : 8;
        const derivedId = `tmpl_from_${wt.id}`;
        const asProgramTemplate = {
          id: derivedId,
          name: programNameOverride ?? wt.title,
          totalWeeks,
          ...(weeks.length > 0 ? { weeks } : { pattern: [] as any[] }),
          source: 'mock' as const,
        };
        // Replace any prior compiled template with the same derived id to avoid stale cache
        const base = Array.isArray(programTemplates) ? programTemplates : [];
        templatesToUse = [...base.filter((t: any) => t?.id !== derivedId), asProgramTemplate];
        idToUse = derivedId;
        setProgramTemplates(templatesToUse);
      }
    }

    const current = {
      programs,
      clientPrograms: clientProgramsV2,
      clientEvents,
      programTemplates: templatesToUse,
    };
    const next = assignProgramTemplateToClient(current, idToUse, clientId, startDate, programNameOverride);
    setPrograms(next.programs);
    setClientProgramsV2(next.clientPrograms);
    setClientEvents(next.clientEvents);

    // Keep legacy clientPrograms domain in sync for the editor/customizer flows
    try {
      // Prefer the WorkoutTemplate (builder template) as the source for legacy programData
      const wt = templates.find((t) => t.id === templateId);
      if (wt) {
        // Remove any existing legacy program for this client and insert a fresh one from the selected template
        const cpId = `cp_${Date.now()}_${clientId}`;
        const legacyCopy = createClientProgramData(wt);
        setClientPrograms((prev) => [
          ...prev.filter((p) => String(p.clientId) !== String(clientId)),
          {
            id: cpId,
            clientId: String(clientId),
            templateId: String(templateId),
            programData: legacyCopy,
            assignedDate: new Date().toISOString(),
            startDate: startDate ? new Date(startDate).toISOString() : undefined,
            notes: undefined,
            allowCustomizations: true,
            isCustomized: false,
          } as ClientProgram,
        ]);
        // Update client's assignedPrograms pointer used by some views
        setClients((prev) =>
          prev.map((c) => (String(c.id) === String(clientId) ? { ...c, assignedPrograms: [cpId] } : c)),
        );
      }
    } catch {
      /* ignored */
    }
  };

  // ===== Goals helpers/actions =====
  // Relative progress from baseline start toward target. Can be negative when moving away.
  const _computeRelativeProgress = (currentValue: number, targetValue: number, startValue: number): number => {
    const c = Math.round(Number(currentValue) || 0);
    const t = Math.round(Number(targetValue) || 0);
    const s = Math.round(Number(startValue) || 0);
    const denom = t - s;
    if (denom === 0) return 0;
    const ratio = (c - s) / denom;
    const pct = Math.round(ratio * 100);
    return Math.max(-100, Math.min(100, pct));
  };

  const _meetsTarget = (currentValue: number, targetValue: number, startValue: number): boolean => {
    const c = Math.round(Number(currentValue) || 0);
    const t = Math.round(Number(targetValue) || 0);
    const s = Math.round(Number(startValue) || 0);
    return t >= s ? c >= t : c <= t;
  };

  const _saveGoals = async (clientId: string, goals: ClientGoal[]) => {
    await goalsStore.setClientGoals(clientId, goals);
    setClientGoalsMap((prev) => ({ ...prev, [clientId]: goals }));
  };

  const clearGoalsCache = (clientId: string) => {
    setClientGoalsMap((prev) => {
      const newMap = { ...prev };
      delete newMap[String(clientId)];
      return newMap;
    });
  };

  const listGoals = React.useCallback(async (clientId: string): Promise<ClientGoal[]> => {
    try {
      const response = await fetch(`/api/coach/goals?clientId=${encodeURIComponent(clientId)}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to list goals');
      }

      const result = await response.json();

      // Transform database format to ClientGoal format
      const goals = (result.goals || []).map((row: any) => {
        return {
          id: row.id,
          clientId: row.client_id,
          ownerCoachId: row.owner_coach_id,
          title: row.title,
          description: row.description,
          targetValue: row.target_value ? parseFloat(row.target_value) : 0,
          currentValue: row.current_value ? parseFloat(row.current_value) : 0,
          unit: row.unit,
          type: row.type,
          status: row.status,
          progress: row.progress,
          schedule: row.schedule,
          priority: row.priority,
          deadline: row.deadline,
          shareWithCoach: row.share_with_coach ?? true,
          history: [], // We'll need to load this separately from client_goal_progress table
          createdAt: row.created_at,
          updatedAt: row.updated_at,
          nextDueOn: row.next_due_on,
          lastCheckinAtISO: row.last_checkin_at, // Map last check-in date for schedule calculation
          linkedTrackerId: row.linked_tracker_id, // Include linked tracker ID
          linkedTracker: row.linked_tracker, // Include linked tracker details
        };
      });

      // Update cache with fresh data
      setClientGoalsMap((prev) => ({
        ...prev,
        [String(clientId)]: goals,
      }));

      return goals;
    } catch (error) {
      console.error('Failed to list goals via Worker, falling back to direct Supabase:', error);

      // Since Worker failed and Supabase client doesn't have session,
      // we need to use the Worker's Supabase client via a different endpoint
      try {
        // Try to get goals via a different Worker endpoint that might work
        const response = await fetch(`/api/clients/goals?clientId=${encodeURIComponent(clientId)}`, {
          credentials: 'include',
        });

        if (response.ok) {
          const result = await response.json();
          const goals = (result.items || []).map((row: any) => ({
            id: row.id,
            clientId: row.client_id,
            ownerCoachId: row.owner_coach_id,
            title: row.title,
            description: row.description,
            targetValue: row.target_value !== null && row.target_value !== undefined ? parseFloat(row.target_value) : 0,
            currentValue:
              row.current_value !== null && row.current_value !== undefined ? parseFloat(row.current_value) : 0,
            unit: row.unit,
            type: row.type,
            status: row.status,
            progress: row.progress,
            schedule: row.schedule,
            priority: row.priority,
            deadline: row.deadline,
            shareWithCoach: row.share_with_coach ?? true,
            lastCheckinAtISO: row.last_checkin_at,
            history: [],
            createdAt: row.created_at,
            updatedAt: row.updated_at,
            nextDueOn: row.next_due_on,
          }));

          // Update cache
          setClientGoalsMap((prev) => ({
            ...prev,
            [String(clientId)]: goals,
          }));

          return goals;
        }
      } catch (fallbackError) {
        console.error('Client endpoint fallback also failed:', fallbackError);
      }

      // Final fallback - return empty array
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createClientGoal = async (
    goal: Omit<ClientGoal, 'id' | 'createdAt' | 'updatedAt' | 'progress' | 'status' | 'history'>,
  ) => {
    const cid = String(goal.clientId);

    // Get real client ID
    let realClientId = cid;

    // If clientId is a mock ID like "c1", try to find the real client UUID
    if (cid.length < 10) {
      // Mock IDs are short
      const realClient = clients.find((c) => String(c.id).includes(cid) || c.name?.toLowerCase().includes('demo'));
      if (realClient) {
        realClientId = realClient.id;
      }
    }

    try {
      // Use Worker endpoint instead of direct Supabase
      const response = await goalsApi.createGoal({
        clientId: realClientId,
        title: goal.title,
        description: goal.description,
        targetValue: goal.targetValue,
        currentValue: goal.currentValue,
        unit: goal.unit,
        goalType: goal.type,
        schedule: goal.schedule,
        linkedTrackerId: (goal as any).linkedTrackerId || null, // Include tracker link
      });

      if (!response.ok) {
        throw new Error(bodyError(response) || 'Failed to create goal');
      }

      const result = response as { goal: Record<string, any> };

      // Transform database format to ClientGoal format
      const newGoal: ClientGoal = {
        id: result.goal.id,
        clientId: result.goal.client_id,
        ownerCoachId: result.goal.owner_coach_id,
        title: result.goal.title,
        description: result.goal.description,
        targetValue: result.goal.target_value ? parseFloat(result.goal.target_value) : 0,
        currentValue: result.goal.current_value ? parseFloat(result.goal.current_value) : 0,
        unit: result.goal.unit,
        type: result.goal.type,
        status: result.goal.status,
        progress: result.goal.progress,
        schedule: result.goal.schedule,
        priority: result.goal.priority,
        deadline: result.goal.deadline,
        history: [], // We'll need to load this separately from client_goal_progress table
        createdAt: result.goal.created_at,
        updatedAt: result.goal.updated_at,
        nextDueOn: result.goal.next_due_on,
        linkedTrackerId: result.goal.linked_tracker_id, // Preserve linked tracker ID
        linkedTracker: result.goal.linked_tracker, // Preserve linked tracker details
      } as any;

      // Update local cache immediately
      setClientGoalsMap((prev) => ({
        ...prev,
        [cid]: [...(prev[cid] || []), newGoal],
      }));

      return newGoal.id;
    } catch (error) {
      console.error('❌ Failed to create goal:', error);
      throw error;
    }
  };

  const updateGoal = async (clientId: string, goalId: string, patch: Partial<ClientGoal>) => {
    try {
      // Map frontend field names to database field names
      const dbUpdates: any = {};
      if (patch.currentValue !== undefined) dbUpdates.current_value = patch.currentValue;
      if (patch.targetValue !== undefined) dbUpdates.target_value = patch.targetValue;
      if (patch.title !== undefined) dbUpdates.title = patch.title;
      if (patch.description !== undefined) dbUpdates.description = patch.description;
      if (patch.unit !== undefined) dbUpdates.unit = patch.unit;
      if (patch.type !== undefined) dbUpdates.type = patch.type;
      if (patch.deadline !== undefined) dbUpdates.deadline = patch.deadline;
      if (patch.priority !== undefined) dbUpdates.priority = patch.priority;
      if (patch.status !== undefined) dbUpdates.status = patch.status;
      if (patch.progress !== undefined) dbUpdates.progress = patch.progress;
      if (patch.schedule !== undefined) dbUpdates.schedule = patch.schedule;
      if (patch.lastCheckinAtISO !== undefined) dbUpdates.last_checkin_at = patch.lastCheckinAtISO;
      if (patch.nextDueOn !== undefined) dbUpdates.next_due_on = patch.nextDueOn;

      // Update via Worker endpoint
      const response = await goalsApi.updateGoal(goalId, dbUpdates);

      if (!response.ok) {
        throw new Error(bodyError(response) || 'Failed to update goal');
      }

      const result = response as { goal: Record<string, any> };

      // Map the response back to frontend format
      const updatedGoal: ClientGoal = {
        id: result.goal.id,
        clientId: result.goal.client_id,
        ownerCoachId: result.goal.owner_coach_id,
        title: result.goal.title,
        description: result.goal.description,
        targetValue: result.goal.target_value ? parseFloat(result.goal.target_value) : 0,
        currentValue: result.goal.current_value ? parseFloat(result.goal.current_value) : 0,
        unit: result.goal.unit,
        type: result.goal.type,
        status: result.goal.status,
        progress: result.goal.progress,
        schedule: result.goal.schedule,
        priority: result.goal.priority,
        deadline: result.goal.deadline,
        shareWithCoach: result.goal.share_with_coach ?? true,
        history: [],
        createdAt: result.goal.created_at,
        updatedAt: result.goal.updated_at,
        nextDueOn: result.goal.next_due_on,
      };

      // Update local state
      setClientGoalsMap((prev) => ({
        ...prev,
        [clientId]: (prev[clientId] || []).map((g) => (g.id === goalId ? updatedGoal : g)),
      }));
    } catch (error) {
      console.error('❌ Failed to update goal:', error);
      throw error;
    }
  };

  const deleteGoal = async (clientId: string, goalId: string) => {
    try {
      // Delete from database using Worker endpoint
      const response = await goalsApi.deleteGoal(goalId);

      if (!response.ok) {
        // Legacy copy: the raw error body text.
        const errorText = response.body !== undefined ? JSON.stringify(response.body) : response.error;
        console.error('🗑️ Worker delete failed:', errorText);
        throw new Error(errorText || 'Failed to delete goal');
      }

      // Update local state immediately
      setClientGoalsMap((prev) => ({
        ...prev,
        [clientId]: (prev[clientId] || []).filter((g) => g.id !== goalId),
      }));
    } catch (error) {
      console.error('❌ Failed to delete goal:', error);
      throw error;
    }
  };

  const addGoalProgress = async (clientId: string, goalId: string, value: number, note?: string) => {
    try {
      // Allow negative deltas (for decreasing progress)
      const deltaValue = Math.round(Number(value) || 0);

      // Add progress via Worker endpoint
      const response = await goalsApi.addGoalProgress(goalId, deltaValue, note);

      if (!response.ok) {
        throw new Error(bodyError(response) || 'Failed to add progress');
      }

      const result = response as { goal: Record<string, any> };

      // Map the updated goal back to frontend format (preserve linked tracker)
      const updatedGoal: ClientGoal = {
        id: result.goal.id,
        clientId: result.goal.client_id,
        ownerCoachId: result.goal.owner_coach_id,
        title: result.goal.title,
        description: result.goal.description,
        targetValue: result.goal.target_value ? parseFloat(result.goal.target_value) : 0,
        currentValue: result.goal.current_value ? parseFloat(result.goal.current_value) : 0,
        unit: result.goal.unit,
        type: result.goal.type,
        status: result.goal.status,
        progress: result.goal.progress,
        schedule: result.goal.schedule,
        priority: result.goal.priority,
        deadline: result.goal.deadline,
        shareWithCoach: result.goal.share_with_coach ?? true,
        history: [],
        createdAt: result.goal.created_at,
        updatedAt: result.goal.updated_at,
        nextDueOn: result.goal.next_due_on,
        lastCheckinAtISO: result.goal.last_checkin_at,
      };

      // Update local state immediately
      setClientGoalsMap((prev) => ({
        ...prev,
        [clientId]: (prev[clientId] || []).map((g) => (g.id === goalId ? updatedGoal : g)),
      }));

      // Trigger a custom event so components can listen for goal updates
      try {
        window.dispatchEvent(new CustomEvent('goals:updated', { detail: { clientId, goalId } }));
      } catch {
        /* ignored */
      }
    } catch (error) {
      console.error('❌ Failed to add goal progress:', error);
      throw error;
    }
  };

  // Reminder synthesis: on load/focus, push low-priority client_checkin for goals due today
  useEffect(() => {
    const synthesize = async () => {
      try {
        const d = atLocalMidnight(new Date());
        const todayISO = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const map = clientGoalsMap || {};
        for (const [cid, arr] of Object.entries(map)) {
          const due = (arr || []).filter((g) => g.status === 'active' && g.nextDueOn === todayISO);
          if (due.length > 0) {
            await addClientEvent({
              clientId: String(cid),
              type: 'client_checkin',
              summary: 'Goals due today',
              detail: `${due.length} goal${due.length > 1 ? 's' : ''} ready for check-in`,
              priority: 'low',
              meta: { goalIds: due.map((g) => g.id) },
            } as any);
          }
        }
      } catch {
        /* ignored */
      }
    };
    synthesize();
    const onFocus = () => synthesize();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientGoalsMap]);

  const value: GlobalState = {
    // Invites API
    inviteClientByEmail: async (args: { email: string; clientName?: string }) => {
      const coachId = String(currentCoach?.id ?? 'coach_demo');
      const email = String(args.email || '')
        .trim()
        .toLowerCase();

      // Pre-check client limit before creating the invite
      try {
        const statusRes = await csrfFetch('/api/billing/status', { credentials: 'include' });
        if (statusRes.ok) {
          const billing = await statusRes.json();
          const limit = billing?.subscription?.client_limit ?? DEFAULT_FREE_CLIENT_LIMIT;
          const current = billing?.client_count ?? 0;
          if (current >= limit) {
            throw Object.assign(
              new Error(`You have reached your plan limit of ${limit} clients. Upgrade your plan to add more.`),
              { code: 'CLIENT_LIMIT_REACHED', upgrade_required: true, limit, current },
            );
          }
        }
      } catch (e: any) {
        if (e?.code === 'CLIENT_LIMIT_REACHED') throw e;
        // Non-blocking: if billing check fails, let server-side enforce
      }

      const inv = {
        id: `inv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        email,
        clientName: args.clientName?.trim(),
        coachId,
        status: 'pending',
        createdAt: new Date().toISOString(),
      } as any;
      const { invitesStore } = await import('@/data/storage/app/invites.adapter');
      await invitesStore.insert(inv);
      try {
        await addClientEvent({
          clientId: String(email),
          type: 'client_invited',
          summary: `Invited ${email}`,
          detail: args.clientName || '',
          severity: 'low',
        } as any);
      } catch {
        /* ignored */
      }
      return inv;
    },
    sendInviteMagicLink: async (inviteId: string) => {
      // Changed to password-based signup flow
      const { invitesStore } = await import('@/data/storage/app/invites.adapter');
      const inv = await invitesStore.get(inviteId);
      if (!inv) throw new Error('Invite not found');

      // Generate signup link instead of magic link
      const signupUrl = `${window.location.origin}/signup?invite=${encodeURIComponent(inviteId)}&email=${encodeURIComponent(inv.email)}`;

      console.info('[Invite] Signup link generated:', signupUrl);

      // Send email via Cloudflare Worker endpoint
      try {
        const response = await clientsApi.sendInviteEmail({
          email: inv.email,
          inviteId: inv.id,
          clientName: inv.clientName || 'Client',
          signupUrl,
          coachName: currentCoach?.name || 'Your Coach',
        });

        // Success envelopes are spread onto `response`; failure bodies arrive as `response.body`.
        const payload = ((response.ok ? response : response.body) ?? {}) as Record<string, any>;

        if (!response.ok) {
          if (payload?.code === 'CLIENT_LIMIT_REACHED') {
            throw Object.assign(new Error(payload.message || 'Client limit reached'), {
              code: 'CLIENT_LIMIT_REACHED',
              upgrade_required: true,
              limit: payload.limit,
              current: payload.current,
            });
          }
          throw new Error(withStatus(response, (status) => `Email send failed: ${status}`));
        }

        if (payload?.existingInvite) {
          console.warn(
            '[Invite] 24h dedupe: no new email sent (same coach + email). Server log: invite.send.dedupe_blocked',
            { inviteId: payload.inviteId },
          );
          return false;
        }

        console.info('[Invite] Email sent successfully to', inv.email);
        return true;
      } catch (emailError: any) {
        if (emailError?.code === 'CLIENT_LIMIT_REACHED') throw emailError;
        console.error('[Invite] Email send failed, copying to clipboard:', emailError);
        // Fallback: copy to clipboard
        try {
          await navigator.clipboard.writeText(signupUrl);
          console.info('[Invite] Signup link copied to clipboard');
          return true;
        } catch (e) {
          console.warn('[Invite] Could not copy to clipboard', e);
          return false;
        }
      }
    },
    // Data
    templates,
    clientPrograms,
    savedWorkouts,
    workouts,
    nutritionTemplates,
    clientNutritionPlans,
    foodItems,
    clients,
    clientsLoadError,
    clientsLoaded,
    // Coach data (pre-loaded like nutrition)
    coachPrograms,
    coachWorkouts,
    coachMeals,
    programs,
    clientProgramsV2,
    clientEvents,
    programTemplates,

    // Goals
    clientGoalsMap,

    // Program Management
    saveTemplate,
    deleteTemplate,
    assignProgramToClients,
    updateClientProgram,
    removeClientProgram,
    getClientPrograms,
    getClientProgram,
    // v2 updater
    updateCompiledProgram,

    // Nutrition Management
    saveNutritionTemplate,
    deleteNutritionTemplate,
    assignNutritionPlanToClients,
    updateClientNutritionPlan,
    removeClientNutritionPlan,
    getClientNutritionPlans,
    getClientNutritionPlan,
    loadNutritionAssignments,

    // Coach data loading states
    coachDataLoading,
    coachDataReady,
    coachWorkoutsLoaded,
    lastSeenData,

    // Optimistic mutation pending states
    pendingProgramDeletes,
    pendingProgramDuplicates,
    pendingWorkoutDeletes,
    pendingWorkoutDuplicates,

    // Meals Management
    listCoachMeals,
    getMealWithItems,
    saveCoachMeal,
    deleteCoachMeal,
    duplicateCoachMeal,

    // Food Management
    searchFoodItems,
    addFoodItem,

    // Client Management
    addClient,
    updateClient,
    deleteClient,
    getClient,
    addClientNote,
    updateClientNote,
    deleteClientNote,
    getClientNotes,
    loadClientNotes,
    loadMoreClientNotes,
    clientHasMoreNotes,
    notesInFlight,

    // Workout Library
    saveWorkout,
    deleteWorkout,
    getSavedWorkouts,
    searchWorkouts,
    createAndSaveWorkout,

    // Program Template Assignment
    assignProgramTemplate,

    // Goals actions
    listGoals,
    createClientGoal,
    updateGoal,
    deleteGoal,
    addGoalProgress,
    clearGoalsCache,

    // Trackers
    listTrackers,
    createTracker,
    updateTracker,
    deleteTracker,
    listTrackerEntries,
    addTrackerEntry,

    // Messaging (P1A)
    listThreadsForCoach,
    listThreadsForClient,
    sendMessage,
    markThreadRead,

    // Intake v2
    createIntakePacket,
    getIntakeByInvite,
    saveIntakeAnswer,
    addIntakeGoal,
    removeIntakeGoal,
    addIntakeTracker,
    removeIntakeTracker,
    submitIntake,
    listIntakeGoals,
    listIntakeTrackers,
    listIntakeAnswers,
    deleteIntakeAnswer,
    getIntakeForClient,
    getIntakePacketById,
    // template APIs
    loadDefaultFormFlags: async () => await loadDefaultFormFlags(),
    saveDefaultFormFlags: async (ff: Partial<FormFlags>) => {
      await saveDefaultFormFlags(ff);
    },
    loadDefaultTrackersTemplate: async () => await loadDefaultTrackersTemplate(),
    saveDefaultTrackersTemplate: async (rows: any[]) => {
      await saveDefaultTrackersTemplate(rows as any);
    },
    loadDefaultCustomQs: async () => await loadDefaultCustomQs(),
    saveDefaultCustomQs: async (rows: any[]) => {
      await saveDefaultCustomQs(rows as any);
    },
    seedPacketFromTemplate: async (pid: string) => {
      await seedPacketFromTemplate(String(pid));
    },
    finalizeIntake,

    // Auth (optional)
    userType,
    setUserType,
    currentCoach,
    authSession: authSession as any,
    authUser: authUser as any,
    signOut,
    authReady: authReady as any,

    // Exercises
    getExercisesByIds: async (ids: string[]) => Array.from(await fetchExercisesByIds(ids)),

    // Workouts
    listCoachWorkouts,
    searchCoachWorkouts,
    getWorkout,
    saveCoachWorkout,
    deleteCoachWorkout,
    duplicateCoachWorkout,
    countCoachWorkouts,
    countWorkoutsCreatedThisWeek,
    countCoachPrograms,
    getLibraryCounts,

    // Programs
    listCoachPrograms,
    getProgram,
    saveCoachProgram,
    duplicateCoachProgram,
    deleteCoachProgram,

    // Client Programs
    assignProgramToClient,
    listClientPrograms,
    getClientAssignments,
    clearAssignmentsCache,
    archiveClientProgram,
    getClientProgramDays,
    saveClientDaySnapshot,
    setClientDayWorkout,
    clearClientDaySnapshot,
    createClientProgramDay,
    swapClientDayWorkouts,
    clearClientDayWorkout,
    getAssignmentStatusByDay,
    updateClientProgramMeta,

    // Client Management
    listClients,
    createClientProfile,
    archiveClient: async (clientId: string, archive: boolean) => {
      try {
        const response = await clientsApi.setClientArchived(clientId, archive);
        if (!response.ok) throw new Error('Archive failed');
        // Refresh client list
        const updated = await listClients();
        setClients(updated);
        return true;
      } catch (e) {
        console.error('archiveClient failed:', e);
        return false;
      }
    },
  };

  // Dev helper: expose userType for quick inspection
  try {
    if ((import.meta as any).env?.DEV) {
      (window as any).gsRef = {
        ...(window as any).gsRef,
        ...value,
        finalizeIntake,
        // Debug utilities
        debugSupabase: () => {
          const sb = getSupabase();
          return {
            hasSupabaseClient: !!sb,
            isConfigured: isSupabaseConfigured(),
            envVars: {
              SUPABASE_URL: !!(import.meta as any).env?.VITE_SUPABASE_URL,
              SUPABASE_ANON_KEY: !!(import.meta as any).env?.VITE_SUPABASE_ANON_KEY,
            },
          };
        },
        debugAuth: async () => {
          const sb = getSupabase();
          if (!sb) return { error: 'No Supabase client' };
          try {
            const {
              data: { user },
              error,
            } = await sb.auth.getUser();
            return { user, error, hasUser: !!user };
          } catch (e: any) {
            return { error: e.message };
          }
        },
        forceCoachMode: () => {
          setUserType('coach');
          return { userType: userType, newUserType: 'coach' };
        },
        listCoachWorkouts,
        searchCoachWorkouts,
        getWorkout,
        saveCoachWorkout,
        deleteCoachWorkout,
        duplicateCoachWorkout,
        countCoachWorkouts,
        countWorkoutsCreatedThisWeek,
        countCoachPrograms,
        getLibraryCounts,
        listCoachPrograms,
        getProgram,
        saveCoachProgram,
        duplicateCoachProgram,
        deleteCoachProgram,
        getExercisesByIds: async (ids: string[]) => Array.from(await fetchExercisesByIds(ids)),

        // Client Programs
        assignProgramToClient,
        listClientPrograms,
        getClientAssignments,
        clearAssignmentsCache,
        archiveClientProgram,
        getClientProgramDays,
        saveClientDaySnapshot,
        setClientDayWorkout,
        clearClientDaySnapshot,
        createClientProgramDay,
        swapClientDayWorkouts,
        clearClientDayWorkout,
        getAssignmentStatusByDay,
        updateClientProgramMeta,

        // Client Management
        listClients,
        createClientProfile,
        saveDefaultFormFlags,
        loadDefaultFormFlags,
        loadDefaultTrackersTemplate,
        loadDefaultCustomQs,
        saveDefaultTrackersTemplate,
        saveDefaultCustomQs,
        seedPacketFromTemplate,
        addIntakeTracker,
        removeIntakeTracker,
        listIntakeAnswers,
        deleteIntakeAnswer,
        get state() {
          return { ...value };
        },
      };
      (window as any).__gs = (window as any).__gs || {};
      Object.defineProperty((window as any).__gs, 'userType', {
        get: () => userType,
        set: (t: any) => setUserType(t === 'coach' || t === 'client' ? t : 'client'),
        configurable: true,
      });
    }
  } catch {
    /* ignored */
  }

  // Wrap in error boundary and add stable key
  return (
    <div key={providerKey} style={{ display: 'contents' }}>
      <GlobalStateContext.Provider value={value}>{children}</GlobalStateContext.Provider>
    </div>
  );
}
